import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BookOpen,
  Check,
  Copy,
  Download,
  ExternalLink,
  FileCheck2,
  FileText,
  GraduationCap,
  Image as ImageIcon,
  Loader2,
  Mail,
  Paperclip,
  Pencil,
  Phone,
  Printer,
  Save,
  Send,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import markdownit from "markdown-it";
import {
  type GradeLevel,
  type Incident,
  type SafetyCheck,
  effectiveGrade,
  formatGradeForDoc,
  isGradeSet,
} from "./types";
import { ParentCommunication } from "./ParentCommunication";
import { ISSDocsStage } from "./ISSDocsStage";
import {
  StageTracker,
  StageProgressBar,
  STAGE_META,
  EXTENDED_STAGE,
  showsExtendedStage,
  getStageCompletion,
  isStageReachable,
} from "./StageTracker";
import {
  IncidentSummaryCard,
  buildSummaryDigest,
  buildStageChips,
} from "./IncidentSummaryCard";
import {
  storageService,
  type FileUpload,
  StorageServiceError,
} from "@/services/storageService";
import { supabase } from "@/lib/supabase";
import { downloadHtmlAsWord } from "@/lib/exportWordDoc";
import { GoogleDocExportButton } from "@/components/restoration/GoogleDocExportButton";
import { useAuth } from "@/hooks/useAuth";
import {
  generate,
  type DebriefResult,
  type MissingChecksResult,
  type MotivationResult,
  type ParentEmailResult,
  type ParentScriptResult,
  type ReentryPlanResult,
  type ReflectionPromptsResult,
  type RemovalDecisionResult,
} from "./restorationGenerate";

// Markdown renderer for mentor-chat (Claude) replies shown inline in the
// scripted restoration conversation.
const mentorMd = markdownit({ html: false, linkify: true, typographer: true });

// Rough detector: is the user asking for coaching help rather than supplying
// the scripted answer the flow expects? We want Claude to respond thoughtfully
// mid-flow (e.g. "She says no one cares for her, what do I say?") instead of
// blindly logging the text and advancing to the next canned prompt.
function isMentorHelpRequest(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  if (t.length < 4) return false;
  if (t.includes("?")) return true;
  // Direct asks for help / guidance
  if (
    /\b(help|don'?t know|no idea|not sure|stuck|trouble|give me|suggest|advice|tips?|coach me|walk me through|what (do|should|can) (i|we)|how (do|should|can) (i|we)|what (do|should) (i|we) say|should (i|we))\b/i
      .test(t)
  ) {
    return true;
  }
  // Admin sharing emotional state — they need support before / during the convo.
  if (
    /\b(nervous|scared|worried|anxious|afraid|overwhelm(ed|ing)?|frustrat(ed|ing)?|exhausted|drained|shaky|unsure|dreading|losing it|at a loss|lost|defeated|shook|rattled|shaken|hurt|hurting|heartbroken|furious|livid|fuming|sad|crying|in tears|upset)\b/i
      .test(t)
  ) {
    return true;
  }
  // Admin describing how the student treated THEM (relational harm, not just
  // a fact statement). These are bids for acknowledgment, not form fills.
  if (
    /\b(?:(?:so|really|super|very)\s+)?(mean|cruel|nasty|rude|disrespectful|hurtful|awful|terrible|horrible)\s+to\s+(me|us)\b/i
      .test(t)
  ) {
    return true;
  }
  if (
    /\b(made me feel|embarrassed me|humiliated me|disrespected me|talked back|cursed at me|swore at me|yelled at me)\b/i
      .test(t)
  ) {
    return true;
  }
  // "This is hard / tough / too much / going sideways"
  if (
    /\b(this is (hard|tough|a lot|too much|going (nowhere|sideways|off the rails)))|it'?s (hard|tough|not working)|going (nowhere|sideways|off the rails)|not working|shut(ting)? down\b/i
      .test(t)
  ) {
    return true;
  }
  // Pushback / signals that the workflow isn't hearing them ("you don't know",
  // "you have no idea", "that's not what I said"). These almost always mean the
  // admin needs a real response, not the next form question.
  if (
    /\b(you (don'?t|do not) know|you (have|got) no idea|that'?s not what|i (didn'?t|did not) say|you'?re not listening|listen to me)\b/i
      .test(t)
  ) {
    return true;
  }
  // Short emotional exclamations without facts ("can't believe it", "it was awful")
  if (
    /\b(can'?t believe (it|that|this)|i'?m (so )?(done|over it|tired of)|(it|today|this) was awful|(it|today|this) was terrible|worst (day|one|incident))\b/i
      .test(t)
  ) {
    return true;
  }
  // Narrating what the student is doing / saying — almost always a coaching moment.
  if (
    /\b(she|he|they|kid|student)(?:'s| is| are)? (saying|crying|shutting down|shut down|mad|angry|upset|quiet|silent|yelling|screaming|refusing|withdrawn|sad|spiraling|melting down|raging|walking out|won'?t talk|won'?t answer|won'?t look)\b/i
      .test(t)
  ) {
    return true;
  }
  if (
    /\b(she|he|they|kid|student) (says|said|keeps (saying|telling|repeating)|thinks|believes|told me|yelled|cried|screamed|refused|threatened|insists|claims)\b/i
      .test(t)
  ) {
    return true;
  }
  // Common student lines admins paste in verbatim
  if (
    /\b(no one cares|nobody cares|doesn'?t (care|matter)|why (do you|should i)|i hate|i'?m done|leave me alone|you can'?t make me)\b/i
      .test(t)
  ) {
    return true;
  }
  // "talking to X now", "about to go in", "before I talk", "in the middle of"
  if (
    /\b(talking to (her|him|them) now|about to (go in|talk|have)|before i (go|talk)|in the middle of|right now in|sitting with (her|him|them))\b/i
      .test(t)
  ) {
    return true;
  }
  return false;
}

type MentorDirective = {
  routeToMentor: boolean;
  evidenceOnly: boolean;
  userPrompt: string;
};

// Parse explicit mentor/evidence-check commands so they can preempt scripted
// step routing (including completion transitions).
function parseMentorDirective(text: string): MentorDirective {
  const raw = text.trim();
  if (!raw) return { routeToMentor: false, evidenceOnly: false, userPrompt: raw };

  const stripped = raw.replace(/^\s*ask\s+mentor\s*[-:]*\s*/i, "").trim();
  const hasAskMentorPrefix = stripped !== raw;
  const probe = stripped || raw;
  const evidenceOnly =
    /\b(evidence check|quote exact|exact quote|where does it say|show (?:me )?(?:the )?(?:exact )?(?:line|quote|text)|not documented in upload|from the uploaded plan|source from upload)\b/i
      .test(probe);

  return {
    routeToMentor: hasAskMentorPrefix || evidenceOnly,
    evidenceOnly,
    userPrompt: probe,
  };
}

function isPauseOrHoldIntent(text: string): boolean {
  return /^(wait+|hold on|stop|pause|one more thing|i (?:need|have) to tell you something|don'?t move on|not yet|hang on)[.! ]*$/i
    .test(text.trim());
}

function isContinueIntent(text: string): boolean {
  return /^(continue|keep going|move on|next|done|done - continue|done — continue|skip|okay continue|ok continue)[.! ]*$/i
    .test(text.trim());
}

function isWorkflowControlUtterance(text: string): boolean {
  const t = text.trim();
  if (!t) return true;
  return (
    isPauseOrHoldIntent(t) ||
    isContinueIntent(t) ||
    /^(ask mentor|show me|what prompt|where am i|what'?s next|help|not sure)$/i.test(t)
  );
}

function looksLikePlanEvidenceQuestion(text: string): boolean {
  const t = text.toLowerCase().trim();
  const asksEvidence =
    /\b(quote exact|exact quote|where does it say|show (?:me )?(?:the )?(?:exact )?(?:line|quote|text)|source(?:\s+line)?|evidence check|not documented in upload)\b/
      .test(t);
  const hasPlanRef = /\b(bip|behavior intervention plan|uploaded|upload|plan|document)\b/.test(t);
  const processQuestion =
    /\b(what about|should (?:he|she|they|i|we)|do (?:i|we) need|next step|what now|what next|reflection form|conversation guide)\b/
      .test(t);
  return asksEvidence && hasPlanRef && !processQuestion;
}

/** Confidence in `human_centered_pause` needed before we branch off the
 *  scripted intake (separate from top-level `intent` confidence). */
const HUMAN_CONTEXT_PAUSE_CONFIDENCE_FLOOR = 0.72;

/** If the Haiku classifier is unavailable, only catch imminent-danger phrasing —
 *  nuanced human context is handled by `human_centered_pause` from the API. */
function isHumanContextPauseFallback(text: string): boolean {
  return /\b(suicid|self[-\s]?harm|kill (?:myself|himself|herself|themselves)|active shooter|bomb threat)\b/i.test(
    text,
  );
}

function shouldPauseForHumanCenteredContext(
  text: string,
  verdict: ClassifyVerdict | null,
): { pause: boolean; source: "ai" | "fallback_safety" } {
  const hcPause = !!verdict?.extracted?.human_centered_pause;
  const hcConf =
    typeof verdict?.extracted?.human_centered_confidence === "number"
      ? verdict.extracted.human_centered_confidence
      : 0;
  if (verdict && hcPause && hcConf >= HUMAN_CONTEXT_PAUSE_CONFIDENCE_FLOOR) {
    return { pause: true, source: "ai" };
  }
  if (!verdict && isHumanContextPauseFallback(text)) {
    return { pause: true, source: "fallback_safety" };
  }
  return { pause: false, source: "ai" };
}

function isInMomentUrgencyIntent(text: string): boolean {
  return /\b(right now|with (?:him|her|them) now|in my office|in front of me|about to go in|won'?t talk|won'?t answer|shut(?:ting)? down|escalat(?:e|ing|ed)|cry(?:ing)?|refus(?:ing|ed)|meltdown|raging)\b/i
    .test(text);
}

function isRepeatPushbackIntent(text: string): boolean {
  return /\b(did you hear me|are you listening|you missed (?:it|that)|i just told you|what\??\s*are you kidding|that's not what i said|you ignored)\b/i
    .test(text);
}

function isReflectionFormQuestion(text: string): boolean {
  // A genuine yes/no/what-is-it question about the reflection form.
  // Complaints ("the reflection form is vague") and regenerate requests
  // ("can you fix the reflection form?") deliberately do NOT count — they
  // route to the regenerate path instead so the leader isn't stuck in a
  // loop of canned recommendation text.
  if (isReflectionRegenerateRequest(text)) return false;
  return /\b(reflection form|should (?:he|she|they) do (?:a )?reflection|do we need (?:a )?reflection|what about (?:the )?reflection)\b/i
    .test(text);
}

// Detect when the admin is giving feedback on or asking us to redo the
// reflection form (rather than asking whether one is needed). True for
// quality complaints ("vague / bad / generic / not great / off"), explicit
// action verbs ("fix / redo / regenerate / update / rewrite / change /
// new one"), or any combination — provided the message also references
// the reflection / form. We intentionally cast a wide net here because
// the failure mode of false-positive regenerate is mild (a fresh form),
// while false-negative leaves the leader stuck repeating themselves.
function isReflectionRegenerateRequest(text: string): boolean {
  const t = text.toLowerCase();
  if (!/(reflection|the form|this form|that form|a form)/.test(t)) return false;
  const complaintRe =
    /\b(vague|bad|wrong|generic|weak|shallow|off|misses|missing|not (?:great|good|specific|helpful|right)|isn'?t (?:right|good|great|specific|helpful)|doesn'?t (?:fit|work|capture|reflect)|needs (?:to be )?(?:updated|fixed|redone|rewritten|new|better|work|improved|changed)|terrible|awful|useless|junk|sucks|too (?:vague|generic|broad|short|long|simple))\b/;
  const actionRe =
    /\b(fix|redo|regenerate|regen|update|rewrite|rework|try again|make (?:a |me )?(?:new|another|different)|new (?:one|reflection|form|version)|replace|improve|change|edit|adjust|tweak|tailor|customize|personali[sz]e)\b/;
  return complaintRe.test(t) || actionRe.test(t);
}

function isCurrentPromptQuestion(text: string): boolean {
  return /\b(what prompt|what am i answering|what do you need|what are you asking|what question|what should i answer|remind me the prompt)\b/i
    .test(text);
}

function currentPromptForStep(step: StepId, incident: Incident): string {
  const name = incident.student_identifier?.trim() || "the student";
  switch (step) {
    case "askContext":
      return "What happened right before the behavior (trigger, buildup, or out of nowhere)?";
    case "askPrior":
      return `What has the teacher already tried with ${name}?`;
    case "askTeacherAsk":
      return "What is the teacher specifically asking for from this referral?";
    case "understand":
      return `What do you think was going on for ${name} under the behavior?`;
    case "genOutputs":
      return "Choose your next artifact: conversation guide, reflection decision, or gap check.";
    case "postConversation":
      return `What happened in your conversation with ${name}, and what follow-up is needed?`;
    default:
      return "Answer the most recent prompt in chat, and I will keep moving from there.";
  }
}

function logUxSignal(event: string, meta?: Record<string, string | number | boolean>) {
  try {
    console.info("[RestorationRoomChat][ux]", event, meta || {});
  } catch {
    // no-op
  }
}

function extractCriticalContextSnippet(incident: Incident): string | null {
  const notes = incident.conversation_notes ?? "";
  const fromTagged = notes.match(/Critical context update:\s*([^\n]+)/i);
  if (fromTagged?.[1]) return fromTagged[1].trim();
  const fromAi = notes.match(/Human context \(AI\):\s*([^\n]+)/i);
  if (fromAi?.[1]) return fromAi[1].trim();
  const fromDesc = (incident.incident_description ?? "").match(
    /(?:Critical context|Human context \(AI\)):\s*([^\n]+)/i,
  );
  if (fromDesc?.[1]) return fromDesc[1].trim();
  return null;
}

/** Record already contains a human-stakes disclosure from the pause path. */
function contextSuggestsDeferReflection(incident: Incident): boolean {
  const desc = incident.incident_description || "";
  const notes = incident.conversation_notes || "";
  const blob = `${desc}\n${notes}`;
  if (/Critical context update:/i.test(blob)) return true;
  if (/Human context \(AI\):/i.test(blob)) return true;
  if (/(?:^|\n)(?:Critical context|Human context \(AI\)):/im.test(desc)) return true;
  return isHumanContextPauseFallback(blob);
}

function assessReflectionNeed(incident: Incident): {
  recommend: boolean;
  reason: string;
  /** Soft, non-blocking note (e.g. acute life context detected). The
   *  recommendation still defaults to "do it"; this is just guidance the
   *  leader can act on however they choose. */
  note?: string;
} {
  // Two HARD defers, both grounded in explicit leader-set signals:
  //   1. Safety pause is still active — regulation precedes reflection.
  //   2. The leader chose the documentation-only path.
  // Anything softer (acute life context surfaced via the human-centered
  // pause path) used to auto-defer here; that produced false negatives —
  // many incidents with serious life context still warrant a written
  // reflection. Those are now surfaced as a `note` instead, and the
  // recommendation defaults to "do it."
  if (incident.safety_check === "pause_needed") {
    return {
      recommend: false,
      reason: "safety/regulation still needs attention before written reflection",
    };
  }
  if (incident.chosen_path === "documentation_only") {
    return {
      recommend: false,
      reason: "this path is documentation-first, so reflection can be deferred",
    };
  }
  const note = contextSuggestsDeferReflection(incident)
    ? "There's acute life context on this record — you may want to start with a brief verbal check-in, then offer the written form."
    : undefined;
  return { recommend: true, reason: "reflection is useful now", note };
}

function isFormsLocationQuestion(text: string): boolean {
  return /\b(where (?:are|is)\s+(?:the\s+)?(?:form|forms|documents|docs)|did you make (?:any )?(?:forms|docs|documents)|i (?:don'?t|do not) (?:see|have)\s+(?:the\s+)?(?:forms|docs|documents)|where can i see (?:the\s+)?(?:forms|docs|documents))\b/i
    .test(text);
}

function isGenerateFormsRequest(text: string): boolean {
  return /\b(generate|make|create|build|draft|pull together)\b.*\b(form|forms|documentation|docs|materials|things i need)\b|\bmake the things i need\b/i
    .test(text);
}

// Detect low-information replies in structured steps so we can offer
// mentor coaching without losing place in the workflow.
function isVagueStructuredReply(step: "askContext" | "askPrior" | "askTeacherAsk", text: string): boolean {
  const t = text.trim();
  if (!t) return true;
  const lo = t.toLowerCase();

  if (isMentorHelpRequest(t)) return true;
  if (t.length <= 3) return true;

  if (
    /^(idk|i dk|don'?t know|not sure|unsure|help|everything|nothing|same as usual|usual|not really|no idea|whatever)$/i
      .test(t)
  ) {
    return true;
  }

  if (
    /\b(came out of nowhere|you know|just behavior|same stuff|all of it|all the things)\b/i
      .test(lo)
  ) {
    return true;
  }

  if (step === "askTeacherAsk") {
    if (/^(help|support|not sure|i don't know)$/i.test(lo)) return true;
  }

  return false;
}

// Detect "where am I / what's next / what do we know" style questions
// from the leader. These are answered from the captured incident state
// without advancing the form. Returns the orientation kind or null.
type OrientationKind = "where" | "next" | "what";
function classifyOrientationQuestion(text: string): OrientationKind | null {
  const t = text.trim().toLowerCase();
  if (!t) return null;
  if (t.length > 240) return null; // too long — probably substantive content
  // "what's next", "what do I do next", "what now", "what's the next step"
  if (
    /\b(what(?:'s| is)?\s+(?:the\s+)?next(?:\s+step)?|what\s+(?:do|should)\s+(?:i|we)\s+do(?:\s+next)?|what\s+now|what\s+comes?\s+next)\b/.test(
      t,
    )
  ) {
    return "next";
  }
  // "where am i", "where are we", "what step am I on"
  if (
    /\b(where\s+(?:am|are)\s+(?:i|we)|what\s+step\s+(?:am|are)\s+(?:i|we)|how\s+far\s+(?:am|are)\s+(?:i|we)|am\s+i\s+on\s+track)\b/.test(
      t,
    )
  ) {
    return "where";
  }
  // "what do we know", "what we have so far", "summary", "recap", "status"
  if (
    /\b(what\s+(?:do|have)\s+we\s+know|what\s+(?:do|have)\s+i\s+know|what\s+we\s+have\s+so\s+far|what(?:'s| is)?\s+(?:on\s+)?(?:the\s+)?(?:current\s+)?(?:incident\s+)?(?:summary|recap|status)|recap(?:\s+(?:so\s+far|the\s+incident))?|summary(?:\s+(?:so\s+far|please))?|status\s+update|catch\s+me\s+up|where\s+were\s+we)\b/.test(
      t,
    )
  ) {
    return "what";
  }
  return null;
}

const stageLabelFor = (stage: ChatTabStage): string => {
  switch (stage) {
    case "incident":
      return "The Situation";
    case "reflection":
      return "Understand";
    case "conversation":
      return "The Conversation";
    case "resolution":
      return "Resolution";
    case "extended":
      return "Extended Support";
  }
};

function buildMentorContextPreamble(
  incident: Incident,
  stepId: string,
): string {
  const name = incident.student_identifier?.trim() || "the student";
  const grade = formatGradeForDoc(incident.grade_level);
  const incidentDesc = incident.incident_description?.trim() || "Not described";
  const notes = incident.conversation_notes?.trim();
  const removal = incident.removal_type
    ? incident.removal_type.replace(/_/g, " ")
    : "Not determined";
  const iep = incident.iep_504_flag ? "Yes — IEP/504" : "No";
  let msg = `CONTEXT (do not repeat this back — use it to inform your response):\n`;
  msg += `I'm a school administrator using the Behavior Incident Toolkit. Keep it SHORT (3 bullets max). No filler.\n`;
  msg += `- Student: ${name}\n`;
  msg += `- Grade: ${grade}\n`;
  msg += `- IEP/504: ${iep}\n`;
  msg += `- Incident: ${incidentDesc}\n`;
  msg += `- Removal type: ${removal}\n`;
  if (incident.behavior_follow_up?.trim()) {
    msg += `- Follow-up so far: ${incident.behavior_follow_up.trim()}\n`;
  }
  if (notes) msg += `- Notes so far: ${notes}\n`;
  msg += `- Current step in my workflow: ${stepId}\n`;
  msg += `\nNow respond to what I'm actually asking:\n\n`;
  return msg;
}

type DocContent = { html: string; text: string };
type GradeGuidance = {
  tone: string;
  openingMoves: string[];
  keyQuestions: string[];
  watchFor: string;
  avoid: string;
};
type ReflectionPrompts = {
  intro: string;
  prompts: { question: string; hint: string }[];
  closing: string;
};

export type ChatTabStage =
  | "incident"
  | "reflection"
  | "conversation"
  | "resolution"
  | "extended";

type StepId =
  | "welcome"
  | "regulation"
  | "situation"
  | "askGrade"
  | "iep504"
  | "iep504Refine"
  | "askContext"
  | "askPrior"
  | "askTeacherAsk"
  | "understand"
  | "genOutputs"
  | "postConversation"
  | "resolution"
  | "askExtended"
  | "teacherFollowUp"
  | "issDocs"
  | "docOffer"
  | "genDoc"
  | "handoff"
  | "finish";

type QuickReply = {
  label: string;
  next: StepId;
  payload?: string;
  // When true, clicking toggles selection without advancing to `next`. Used
  // for multi-select lists (e.g. all the interventions a teacher has tried).
  multi?: boolean;
  // When true, this chip opens a mentor-chat request using a canned prompt
  // instead of advancing the flow. Keeps the user on the current step.
  askMentor?: boolean;
  // When true, this chip is visually grouped as an "action" (continue, skip,
  // jump forward, etc.) — rendered in a separate row below the option chips
  // with a slightly heavier style so it doesn't blend into the choices.
  isAction?: boolean;
};

type ChatMessage =
  | { kind: "bodhi"; id: string; html: string }
  | { kind: "user"; id: string; text: string }
  | {
      kind: "data-card";
      id: string;
      chips: { key: string; value: string }[];
    }
  | { kind: "iep504-guidance"; id: string; plan: "iep" | "504" | "both" | "bip" }
  | { kind: "out-reflection"; id: string }
  | { kind: "out-due-process"; id: string }
  | { kind: "out-coaching"; id: string; guideHtml: string; grade: GradeLevel; isAi?: boolean }
  | {
      kind: "motivation-card";
      id: string;
      hypotheses: MotivationResult["hypotheses"];
    }
  | {
      kind: "removal-decision-card";
      id: string;
      decision: RemovalDecisionResult;
    }
  | {
      kind: "debrief-card";
      id: string;
      debrief: DebriefResult;
    }
  | {
      kind: "missing-checks-card";
      id: string;
      ready: boolean;
      gaps: { field: string; why_it_matters: string; suggested_prompt: string }[];
      stage: string;
    }
  | { kind: "out-iss-docs"; id: string }
  | { kind: "out-documentation"; id: string }
  | { kind: "out-parent"; id: string }
  | { kind: "out-teacher-summary"; id: string }
  | { kind: "out-complete"; id: string; studentLabel: string }
  | {
      kind: "attachment";
      id: string;
      fileName: string;
      fileSize: number;
      mimeType: string;
      status: "uploading" | "uploaded" | "error";
      errorMessage?: string;
      fileId?: string;
    }
  | {
      kind: "quick-replies";
      id: string;
      options: QuickReply[];
      // Payloads (for multi chips) currently checked by the user. Updated in
      // place when the user toggles a multi chip.
      selected?: string[];
    };

// Generated artifacts the persistent Documents drawer can open. Each
// kind maps to either an in-chat side-panel or a direct download.
export type GeneratedArtifactKind =
  | "incident-packet"
  | "parent-comm"
  | "conversation-guide"
  | "student-reflection"
  | "iss-tasks"
  /** Full documentation sheet: polish, AI narrative, Notes (saved case notes, incl. narrative), PDF download */
  | "case-record";

export interface RestorationRoomChatHandle {
  jumpToStage: (stage: ChatTabStage) => void;
  uploadFile: (file: File) => Promise<void>;
  viewAttachment: (fileId: string) => void;
  removeAttachment: (fileId: string) => Promise<void>;
  openArtifact: (kind: GeneratedArtifactKind) => void;
}

interface RestorationRoomChatProps {
  incident: Incident;
  gradeGuidance: GradeGuidance;
  reflectionPrompts: ReflectionPrompts;
  buildConversationGuide: (incident: Incident) => DocContent;
  downloadIncidentPacketPDF: (incident: Incident) => void;
  onUpdateIncident: (fields: Partial<Incident>) => void;
  onCloseIncident: () => void;
  onNewIncident: () => void;
  onBackToDashboard: () => void;
  onStageChange: (stage: ChatTabStage) => void;
  isReadOnly?: boolean;
  // True when the incident has not yet been persisted to the DB. Used
  // to block side-effects that need a real row (file uploads), since
  // attachments are tied to incident.id and would orphan if the leader
  // backs out before promotion.
  isUnsavedDraft?: boolean;
  // Notifies the parent whenever the chat's attachments list changes so
  // the persistent Documents drawer can mirror the same data without
  // duplicating storageService calls or RLS logic.
  onAttachmentsChange?: (attachments: FileUpload[]) => void;
}

// ── Stage mapping ─────────────────────────────────────────────────────────
const stageForStep = (step: StepId): ChatTabStage => {
  switch (step) {
    case "welcome":
    case "regulation":
    case "situation":
    case "askGrade":
    case "iep504":
    case "iep504Refine":
    case "askContext":
    case "askPrior":
    case "askTeacherAsk":
      return "incident";
    case "understand":
    case "genOutputs":
      return "reflection";
    case "postConversation":
      return "conversation";
    case "resolution":
    case "askExtended":
    case "teacherFollowUp":
    case "docOffer":
    case "genDoc":
    case "handoff":
    case "finish":
      return "resolution";
    case "issDocs":
      return "extended";
  }
};

// Pull the visual "active stage" back to the first incomplete stage when
// the script's position has run ahead of the captured data. Mirrors the
// `effectiveActiveStage` memo used in the render path, so anything that
// needs to describe "where we are" (the digest builder, etc.) stays
// aligned with what the leader sees in the tracker / progress bar.
const clampStageToData = (
  scriptStage: ChatTabStage,
  incident: Incident,
): ChatTabStage => {
  const stages = [
    ...STAGE_META,
    ...(showsExtendedStage(incident) ? [EXTENDED_STAGE] : []),
  ];
  const completion = getStageCompletion(incident);
  const firstIncomplete = stages.find((s) => !completion[s.key]);
  if (!firstIncomplete) return scriptStage;
  const currentIdx = stages.findIndex((s) => s.key === scriptStage);
  const firstIncompleteIdx = stages.findIndex(
    (s) => s.key === firstIncomplete.key,
  );
  if (currentIdx < 0) return firstIncomplete.key;
  return currentIdx <= firstIncompleteIdx ? scriptStage : firstIncomplete.key;
};

// Anchor step for each tab (when user clicks a tab, we jump here)
const anchorStepForStage = (stage: ChatTabStage, incident: Incident): StepId => {
  switch (stage) {
    case "incident":
      if (!incident.incident_description) return "situation";
      return "iep504";
    case "reflection":
      return "understand";
    case "conversation":
      return "postConversation";
    case "resolution":
      return "resolution";
    case "extended":
      return "issDocs";
  }
};

// ── Helpers ───────────────────────────────────────────────────────────────
const genId = () => `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Given an existing incident (one the user is returning to), figure out
// the furthest step they had reached so we can resume instead of re-asking
// everything from scratch.
const resumeStepFor = (inc: Incident): StepId => {
  const desc = inc.incident_description?.trim();
  const name = inc.student_identifier?.trim();
  if (!desc && !name) return "welcome";

  if (inc.status === "closed") return "genDoc";

  const hasExtended =
    !!inc.removal_type ||
    !!inc.iss_work_plan?.trim() ||
    !!inc.return_to_class_plan?.trim() ||
    !!inc.reentry_conversation_needed;
  if (hasExtended) return "finish";

  if (inc.behavior_follow_up?.trim() || /^Outcome:/im.test(inc.conversation_notes || "")) {
    return "askExtended";
  }
  if (inc.conversation_completed) return "resolution";
  if (/^Motivation:/im.test(inc.conversation_notes || "")) return "genOutputs";

  const hasTeacherAsk =
    (inc.teacher_outcome_checked?.length ?? 0) > 0 || !!inc.teacher_requested_outcome?.trim();
  if (hasTeacherAsk) return "understand";

  const hasPrior =
    (inc.prior_interventions_checked?.length ?? 0) > 0 || !!inc.prior_interventions?.trim();
  if (hasPrior) return "askTeacherAsk";

  if (/^Trigger\/context:/im.test(inc.conversation_notes || "")) return "askPrior";

  return "askContext";
};

// Short label describing how far along the incident is, for the
// "welcome back" summary.
const progressLabelFor = (inc: Incident): string => {
  const s = resumeStepFor(inc);
  switch (s) {
    case "finish":
      return "incident closed / documentation complete";
    case "genDoc":
      return "incident closed — reviewing the final record";
    case "askExtended":
      return "outcome captured — ready for extended support check";
    case "resolution":
      return "conversation logged — ready for resolution";
    case "genOutputs":
      return "motivation captured — forms ready to review";
    case "understand":
      return "context gathered — ready to interpret the behavior";
    case "askTeacherAsk":
      return "prior interventions noted";
    case "askPrior":
      return "trigger/context noted";
    case "askContext":
      return "basic info captured — ready for more context";
    default:
      return "just getting started";
  }
};

const incidentHasData = (inc: Incident): boolean =>
  !!(inc.incident_description?.trim() && inc.student_identifier?.trim());

// Build an incident-specific reflection form by weaving what the adult
// shared (description, trigger, repeat, name) into the intro and adding
// a context-specific prompt so the form speaks to *this* situation.
const personalizeReflectionPrompts = (
  base: ReflectionPrompts,
  incident: Incident,
): ReflectionPrompts => {
  const name = incident.student_identifier?.trim();
  const desc = incident.incident_description?.trim();
  const opener = desc
    ? `Earlier today, something happened${
        name ? ` — and the adults want to hear ${name}'s side` : ""
      }. Here's what was reported: "${desc.length > 240 ? desc.slice(0, 237) + "…" : desc}"\n\n${base.intro}`
    : base.intro;

  const extras: { question: string; hint: string }[] = [];

  const hasTriggerNote = /trigger\/context:/i.test(incident.conversation_notes ?? "");
  if (hasTriggerNote) {
    extras.push({
      question: "What was going on for you right before this started?",
      hint: "Whatever was on your mind — home, a friend, feeling tired, hungry, overwhelmed. There's no wrong answer.",
    });
  }
  if (/\b(another student|with \w+|fight|classmate|peer)/i.test(desc ?? "")) {
    extras.push({
      question: "Thinking about the other person — what do you think it looked like from their side?",
      hint: "Honest about impact isn't the same as accepting all the blame.",
    });
  }

  return {
    intro: opener,
    prompts: [...base.prompts, ...extras],
    closing: base.closing,
  };
};

// Build printable HTML for the student reflection form (matches the
// same look-and-feel as ReflectionSharing's buildPDFHTML).
const buildReflectionFormHTML = (
  gradeLevel: GradeLevel,
  studentId: string,
  content: ReflectionPrompts,
): string => {
  const identifier = studentId?.trim() || "Student";
  const promptsHTML = content.prompts
    .map(
      (p, i) => `
      <div style="margin-bottom:18px;page-break-inside:avoid;">
        <div style="font-weight:600;font-size:11pt;color:#1a1a1a;margin-bottom:2px;">${i + 1}. ${p.question}</div>
        <div style="font-size:9pt;color:#6b7280;margin-bottom:6px;font-style:italic;">${p.hint}</div>
        <div style="border-bottom:1px solid #d1d5db;height:24px;margin-bottom:4px;"></div>
        <div style="border-bottom:1px solid #d1d5db;height:24px;margin-bottom:4px;"></div>
        <div style="border-bottom:1px solid #d1d5db;height:24px;margin-bottom:4px;"></div>
        <div style="border-bottom:1px solid #d1d5db;height:24px;"></div>
      </div>`,
    )
    .join("");
  return `
    <div style="font-family:Georgia,serif;padding:28px 36px;max-width:680px;margin:0 auto;color:#1a1a1a;">
      <div style="border-bottom:2px solid #0D4436;padding-bottom:8px;margin-bottom:14px;">
        <div style="font-size:9pt;text-transform:uppercase;letter-spacing:0.1em;color:#6b7280;margin-bottom:3px;">Student Reflection Form</div>
        <div style="font-size:15pt;font-weight:700;color:#0D4436;">Grade ${gradeLevel}</div>
        <div style="font-size:9pt;color:#6b7280;margin-top:3px;">Name/ID: ${identifier} · Date: _______________________</div>
      </div>
      <div style="background:#fefce8;border-left:3px solid #ca8a04;padding:8px 12px;margin-bottom:16px;border-radius:0 4px 4px 0;">
        <p style="margin:0;font-size:9.5pt;line-height:1.5;color:#713f12;white-space:pre-wrap;">${content.intro}</p>
      </div>
      ${promptsHTML}
      <div style="margin-top:16px;background:#f0fdf4;border:1px solid #86efac;border-radius:6px;padding:8px 12px;">
        <p style="margin:0;font-size:9.5pt;line-height:1.5;color:#166534;font-style:italic;">${content.closing}</p>
      </div>
      <div style="margin-top:14px;text-align:center;font-size:8pt;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:8px;">
        Generated by HeyBodhi · Provided by your school
      </div>
    </div>`;
};

const buildReflectionFormPlainText = (
  gradeLevel: GradeLevel,
  studentId: string,
  content: ReflectionPrompts,
): string => {
  const lines = [
    `STUDENT REFLECTION FORM — Grade ${gradeLevel}`,
    `Name/ID: ${studentId || "Student"} · Date: ___________`,
    ``,
    `────────────────────────────────────────`,
    ``,
    content.intro,
    ``,
    ...content.prompts.flatMap((p, i) => [
      `${i + 1}. ${p.question}`,
      `   (${p.hint})`,
      ``,
      `   ____________________________________________`,
      `   ____________________________________________`,
      `   ____________________________________________`,
      `   ____________________________________________`,
      ``,
    ]),
    `────────────────────────────────────────`,
    ``,
    content.closing,
  ];
  return lines.join("\n");
};

const studentSlug = (name: string) =>
  (name || "student").trim().replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "").toLowerCase();

// Wraps the raw coaching-guide HTML in print-friendly styling so PDF export
// and the side-panel preview use the same layout.
const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const buildTeacherSummaryPrintHtml = (
  summaryText: string,
  studentIdentifier: string,
  teacherName: string,
) => {
  const paragraphsHtml = summaryText
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map(
      (p) =>
        `<p>${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`,
    )
    .join("\n");
  const teacher = teacherName?.trim() || "the teacher";
  const student = studentIdentifier?.trim() || "Student";
  return `
  <div style="font-family:Georgia,serif;padding:32px 40px;max-width:680px;margin:0 auto;color:#1a1a1a;">
    <div style="border-bottom:2px solid #0D4436;padding-bottom:8px;margin-bottom:18px;">
      <div style="font-size:9pt;text-transform:uppercase;letter-spacing:0.1em;color:#6b7280;">Written Summary · For ${escapeHtml(teacher)}</div>
      <div style="font-size:14pt;font-weight:700;color:#0D4436;">Re: ${escapeHtml(student)}</div>
      <div style="font-size:9pt;color:#6b7280;margin-top:3px;">${new Date().toLocaleDateString()}</div>
    </div>
    <style>
      p { font-size: 11pt; line-height: 1.6; color:#1a1a1a; margin: 0 0 12px; }
    </style>
    ${paragraphsHtml}
    <div style="margin-top:22px;text-align:center;font-size:8pt;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:8px;">
      Drafted with HeyBodhi · Edit before sending
    </div>
  </div>`;
};

const buildCoachingPrintHtml = (
  guideHtml: string,
  grade: GradeLevel,
  studentIdentifier: string,
) => `
  <div style="font-family:Georgia,serif;padding:28px 36px;max-width:680px;margin:0 auto;color:#1a1a1a;">
    <div style="border-bottom:2px solid #0D4436;padding-bottom:8px;margin-bottom:14px;">
      <div style="font-size:9pt;text-transform:uppercase;letter-spacing:0.1em;color:#6b7280;">Facilitator Conversation Guide</div>
      <div style="font-size:15pt;font-weight:700;color:#0D4436;">Grade ${grade}${
        studentIdentifier ? ` · ${studentIdentifier}` : ""
      }</div>
      <div style="font-size:9pt;color:#6b7280;margin-top:3px;">For facilitator use only · ${new Date().toLocaleDateString()}</div>
    </div>
    <style>
      h3 { font-size: 13pt; color:#0D4436; margin: 14px 0 8px; }
      h4 { font-size: 10pt; color:#0D4436; text-transform:uppercase; letter-spacing:0.08em; margin: 12px 0 4px; }
      p, li { font-size: 10.5pt; line-height: 1.55; color:#1a1a1a; }
      ul { padding-left: 20px; }
      small { color:#6b7280; font-size: 8.5pt; }
    </style>
    ${guideHtml}
    <div style="margin-top:20px;text-align:center;font-size:8pt;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:8px;">
      Generated by HeyBodhi · Keep with incident record
    </div>
  </div>`;

const downloadHtmlAsPDF = async (html: string, filename: string) => {
  try {
    const mod = await import("html2pdf.js");
    const html2pdf = (mod as unknown as { default: () => unknown }).default;
    const element = document.createElement("div");
    element.innerHTML = html;
    (html2pdf() as {
      set: (opts: unknown) => {
        from: (el: HTMLElement) => { save: () => void };
      };
    })
      .set({
        margin: [0.3, 0.4, 0.3, 0.4],
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      })
      .from(element)
      .save();
  } catch (err) {
    console.error("[RestorationRoomChat] PDF generation failed", err);
    toast.error("Could not generate PDF — check your browser's download settings.");
  }
};

const printHtml = (html: string, title: string) => {
  const win = window.open("", "_blank", "noopener,noreferrer,width=900,height=1100");
  if (!win) {
    toast.error("Could not open print window — check your browser's popup settings.");
    return;
  }
  win.document.open();
  win.document.write(`<!DOCTYPE html><html><head><title>${title}</title><meta charset="utf-8"/></head><body>${html}</body></html>`);
  win.document.close();
  win.focus();
  window.setTimeout(() => {
    try { win.print(); } catch (e) { console.error(e); }
  }, 300);
};

const copyToClipboard = async (plain: string, rich?: string) => {
  try {
    if (rich && typeof ClipboardItem !== "undefined") {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([rich], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
    } else {
      await navigator.clipboard.writeText(plain);
    }
    toast.success("Copied to clipboard");
  } catch (err) {
    console.error(err);
    toast.error("Could not copy to clipboard");
  }
};

const formatHtml = (s: string) => {
  // Messages from the mentor-chat (Claude) edge function come pre-rendered
  // from markdown-it as <p>, <ul>, etc. with literal newlines between the
  // block tags. Don't convert those newlines to <br/> — it creates a double
  // gap after every paragraph. Scripted Bodhi copy ("**Name**, what happened?
  // \n\ne.g. Marcus...") still gets the bold/italic/newline treatment.
  const isRenderedHtml = /<(?:p|ul|ol|li|h[1-6]|blockquote|pre)\b/i.test(s);
  if (isRenderedHtml) {
    return s.replace(/>\s+</g, "><");
  }
  return s
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/\n/g, "<br/>");
};

const toGradeLevel = (
  gradeRaw: string | number | null,
): GradeLevel | null => {
  if (gradeRaw === null || gradeRaw === undefined) return null;
  if (typeof gradeRaw === "string" && gradeRaw.toUpperCase() === "K") return "K-2";
  const n = typeof gradeRaw === "number" ? gradeRaw : parseInt(String(gradeRaw), 10);
  if (Number.isNaN(n)) return null;
  if (n <= 2) return "K-2";
  if (n <= 5) return "3-5";
  if (n <= 8) return "6-8";
  return "9-12";
};

const parseYesNo = (text: string): "yes" | "no" | "unsure" | null => {
  const t = text.trim().toLowerCase();
  if (!t) return null;
  if (/\b(no|nope|nah|n\/a|none|negative)\b/.test(t)) return "no";
  if (/\b(not sure|unsure|maybe|don'?t know|idk|unclear|haven'?t checked)\b/.test(t))
    return "unsure";
  if (/\b(yes|yeah|yep|yup|correct|iep|504|bip|plan|has one|he does|she does|they do)\b/.test(t))
    return "yes";
  return null;
};

// Tier 1 / Step C — yes/no normalization.
//
// The regex parseYesNo above is rigid: it misses common phrasings
// ("kinda", "I think so", "definitely", "not really", "I'd say no"),
// and a leading word can lock the answer wrong ("no I think she does" →
// matches "no" first). The classifier's `yes_no_signal` field handles
// these cleanly because it actually reads the message.
//
// Strategy: AI-first when the classifier is confident AND the turn is
// an "answer" intent AND it returned a non-null signal. Falls through
// to the existing regex otherwise so behavior is identical when the
// classifier is unavailable or unsure. The regex stays in its current
// form as the safety net.
const resolveYesNo = (
  text: string,
  verdict: ClassifyVerdict | null,
): "yes" | "no" | "unsure" | null => {
  if (
    verdict &&
    verdict.confidence >= CLASSIFIER_CONFIDENCE_FLOOR &&
    verdict.intent === "answer" &&
    verdict.extracted.yes_no_signal
  ) {
    return verdict.extracted.yes_no_signal;
  }
  return parseYesNo(text);
};

type AccommodationSignals = {
  hasAny: boolean;
  hasIep: boolean;
  has504: boolean;
  hasBip: boolean;
  plan: "iep" | "504" | "both" | null;
  chipValue: string;
};

const detectAccommodationSignals = (text: string): AccommodationSignals => {
  const lo = text.toLowerCase();
  const hasIep = /\biep\b/.test(lo);
  const has504 = /\b504\b/.test(lo);
  const hasBip =
    /\bbip\b/.test(lo) ||
    /\bbehavior intervention plan\b/.test(lo) ||
    /\bbehavio[u]?r plan\b/.test(lo);
  const hasAny = hasIep || has504 || hasBip;
  const plan: "iep" | "504" | "both" | null =
    hasIep && has504 ? "both" : has504 ? "504" : hasIep ? "iep" : null;
  const parts: string[] = [];
  if (hasIep) parts.push("IEP");
  if (has504) parts.push("504");
  if (hasBip) parts.push("BIP");
  return {
    hasAny,
    hasIep,
    has504,
    hasBip,
    plan,
    chipValue: parts.join(" + ") || "Plan mentioned",
  };
};

type PlanDocSignals = {
  isPlanDoc: boolean;
  hasIep: boolean;
  has504: boolean;
  hasBip: boolean;
  chipValue: string;
};

const detectPlanDocSignals = (fileName: string, mimeType: string): PlanDocSignals => {
  const lo = `${fileName} ${mimeType}`.toLowerCase();
  const hasIep = /\biep\b/.test(lo) || /\bindividualized education program\b/.test(lo);
  const has504 = /\b504\b/.test(lo) || /\bsection 504\b/.test(lo);
  const hasBip =
    /\bbip\b/.test(lo) ||
    /\bbehavior intervention plan\b/.test(lo) ||
    /\bbehavio[u]?r plan\b/.test(lo);
  const isPlanDoc = hasIep || has504 || hasBip;
  const parts: string[] = [];
  if (hasIep) parts.push("IEP");
  if (has504) parts.push("504");
  if (hasBip) parts.push("BIP");
  return {
    isPlanDoc,
    hasIep,
    has504,
    hasBip,
    chipValue: parts.join(" + ") || "Plan doc",
  };
};

const LOCATION_WORDS = [
  "classroom",
  "hallway",
  "cafeteria",
  "bathroom",
  "restroom",
  "gym",
  "bus",
  "playground",
  "office",
  "library",
  "lunchroom",
  "field",
  "outside",
];

const NAME_STOPWORDS = new Set([
  // Grammar / common words that happen to land at the start of a sentence
  "i", "a", "the", "he", "she", "they", "we", "this", "that",
  "my", "our", "his", "her", "their", "is", "in", "at", "on",
  "to", "for", "and", "or", "but", "got", "had", "have", "was",
  "were", "with", "after", "before", "during", "just", "an",
  "it", "its", "from", "of", "by", "hit",
  // Institutional / role words that showed up as false-positive names
  // ("School behavior…" → extracted "School" as a student name).
  "school", "district", "class", "classroom", "building", "campus",
  "office", "admin", "administrator", "principal", "superintendent",
  "teacher", "instructor", "counselor", "dean", "aide", "paraprofessional",
  "staff", "parent", "mom", "mother", "father", "dad", "family",
  "home", "today", "yesterday", "tomorrow", "morning", "afternoon",
  "student", "kid", "child", "son", "daughter",
]);

// Acronyms / institutional initials that look name-shaped (2–4 caps, optional
// periods) but never refer to a student. Compared against the canonical form
// (uppercase, no periods) so "I.E.P.", "iep", "IEP" all match `IEP`.
const INITIAL_STOPLIST = new Set([
  // Disability / programming
  "IEP", "BIP", "FBA", "LRE", "MDR", "ESL", "ELL", "ESE", "OT", "PT", "ST",
  "AP", "IB", "AVID", "RTI", "MTSS", "PBIS", "ABA", "DCF", "CPS",
  // Discipline / law
  "ISS", "OSS", "ADA", "IDEA", "FERPA", "HIPAA", "BIE",
  // Common pronouns / Roman numerals / state codes that fit the pattern
  "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "TV", "DJ", "PE",
  "USA", "US", "UK", "TX", "CA", "NY", "FL", "NJ", "PA", "AZ", "GA", "OR",
  "WA", "MI", "MN", "OH", "NC", "SC", "VA", "WI", "IL", "IN",
]);

/** Canonical form of an initials token: drop periods and uppercase. */
const canonicalizeInitials = (raw: string): string =>
  raw.replace(/\./g, "").toUpperCase();

/** Token shape: 2–4 uppercase letters, optionally separated by single dots
 *  with an optional trailing dot. Examples: RF, R.F., B.J.K., H.G. */
const INITIALS_TOKEN_RE = /^[A-Z](?:\.?[A-Z]){1,3}\.?$/;

const looksLikeInitials = (token: string): boolean =>
  INITIALS_TOKEN_RE.test(token) && !INITIAL_STOPLIST.has(canonicalizeInitials(token));

/** Token shape: cap-lower first name (Bob, Sarah, Maddux). */
const FIRST_NAME_RE = /^[A-Z][a-z]+$/;

/** Either a normal first name or initials — the unit we treat as a
 *  student identifier when extracting from free text. */
const NAME_OR_INITIALS_SRC = `(?:[A-Z][a-z]+|[A-Z](?:\\.?[A-Z]){1,3}\\.?)`;

/** Validate a candidate string as either a name or non-blacklisted initials. */
const isValidNameOrInitials = (candidate: string): boolean => {
  if (!candidate) return false;
  if (FIRST_NAME_RE.test(candidate)) {
    return !NAME_STOPWORDS.has(candidate.toLowerCase());
  }
  return looksLikeInitials(candidate);
};

// Explicit name patterns — when the admin signals "this is the name", use
// that over token-scanning. Ordered by specificity: more explicit wins.
// Each pattern accepts BOTH normal first names ("Bob") and initials
// ("RF", "R.F.", "B.J.K.") as the captured identifier so a privacy-conscious
// leader can refer to students by initials and have them flow through.
function extractExplicitName(text: string): string | undefined {
  const patterns: RegExp[] = [
    // "his/her name is RF", "their name's Bob", "name is Bob Smith"
    new RegExp(
      `\\b(?:his|her|their|the\\s+kid'?s?|the\\s+student'?s?|the\\s+child'?s?|son'?s?|daughter'?s?)?\\s*name(?:'s)?\\s*(?:is|=|:)\\s+(${NAME_OR_INITIALS_SRC}(?:\\s+[A-Z][a-z]+)?)`,
      "i",
    ),
    // "the kid is RF", "the student is Bob"
    new RegExp(
      `\\b(?:the\\s+)?(?:kid|student|child)\\s+is\\s+(${NAME_OR_INITIALS_SRC}(?:\\s+[A-Z][a-z]+)?)`,
      "i",
    ),
    // "RF is my son", "Bob is the student", "RF is a 5th grader"
    new RegExp(
      `\\b(${NAME_OR_INITIALS_SRC})\\s+is\\s+(?:my\\s+(?:son|daughter|kid|child|student)|the\\s+(?:student|kid|child)|a\\s+\\d(?:st|nd|rd|th)?\\s*grader?)\\b`,
    ),
    // "about RF", "regarding Bob", "for RF" — "write it up for RF" framing
    new RegExp(
      `\\b(?:about|regarding|on|for|writing\\s+up|processing)\\s+(${NAME_OR_INITIALS_SRC})(?:[,.]|$|\\s+(?:the|a|an|who|and|but|because))`,
    ),
    // Possessive lead-in: "RF's mom ran away", "HG's dad is dying" — the
    // disclosure was the thing that surfaced the bug originally; once
    // identified that way, treat RF/HG as the student identifier.
    new RegExp(`\\b(${NAME_OR_INITIALS_SRC})['\u2019]s\\s+(?:mom|mother|dad|father|parent|guardian|grandma|grandmother|grandpa|grandfather|sister|brother|family)\\b`),
  ];
  for (const re of patterns) {
    const m = text.match(re);
    if (m && m[1]) {
      const candidate = m[1].trim().split(/\s+/)[0];
      if (isValidNameOrInitials(candidate)) {
        return candidate;
      }
    }
  }
  return undefined;
}

type Extracted = {
  student_identifier?: string;
  grade_level?: GradeLevel;
  incident_description?: string;
  repeat_incident?: boolean;
  safety_check?: SafetyCheck;
  location?: string;
};

// ── AI classifier verdict shape + auto-extraction helpers ──────────────────
//
// The shape of the verdict returned by the `restoration-classify-turn` edge
// function. Hoisted to module scope so both `classifyTurn` (the call site)
// and `applyAiExtractedFacts` (the gap-filler) can share it.
export type ClassifyVerdict = {
  intent:
    | "answer"
    | "orientation"
    | "navigation"
    | "mentor_help"
    | "add_context"
    | "correction";
  confidence: number;
  reasoning: string;
  extracted: {
    yes_no_signal: "yes" | "no" | "unsure" | null;
    navigation_target:
      | "exit_review"
      | "back"
      | "forward"
      | "skip"
      | "incident"
      | "reflection"
      | "conversation"
      | "resolution"
      | "extended"
      | null;
    orientation_kind: "where" | "next" | "what" | null;
    student_identifier: string | null;
    grade_level: GradeLevel | null;
    mentions_safety_issue: boolean;
    mentions_repeat_pattern: boolean;
    /** From `restoration-classify-turn` — substantive life/human context. */
    human_centered_pause?: boolean;
    /** 0–1 confidence in human_centered_pause (independent of intent confidence). */
    human_centered_confidence?: number;
  };
};

// Keep the safety-in-doubt floor in one place. The chat treats anything
// below this as "AI is guessing — fall back to regex / scripted handling".
export const CLASSIFIER_CONFIDENCE_FLOOR = 0.7;

// Tier 1 / Step B — auto-extraction takeover.
//
// When the leader volunteers structured facts in a free-form turn ("Sarah,
// 3rd grade, threw a chair at recess"), capture them up front so the
// scripted flow doesn't force them through three back-to-back single-field
// prompts they've effectively already answered.
//
// Conservative gap-filling rules:
//   - Existing fields are NOT overwritten unless the verdict's intent is
//     "correction" (the explicit "actually her name is Susie" case).
//   - Boolean signals (mentions_safety_issue, mentions_repeat_pattern)
//     only turn flags ON — never clear them.
//   - Returns a chip list so the caller can confirm to the leader exactly
//     what was inferred. A wrong inference shows up as a visible chip
//     ("Student: School") instead of getting silently saved.
//   - Skipped (returns empty) when the intent is something the auto-fill
//     has no business acting on (mentor_help, orientation, navigation).
const applyAiExtractedFacts = (
  verdict: ClassifyVerdict | null,
  current: Incident,
): {
  updates: Partial<Incident>;
  chips: { key: string; value: string }[];
} => {
  const updates: Partial<Incident> = {};
  const chips: { key: string; value: string }[] = [];
  if (!verdict || verdict.confidence < CLASSIFIER_CONFIDENCE_FLOOR) {
    return { updates, chips };
  }
  if (
    verdict.intent !== "answer" &&
    verdict.intent !== "add_context" &&
    verdict.intent !== "correction"
  ) {
    return { updates, chips };
  }
  const ex = verdict.extracted;
  const isCorrection = verdict.intent === "correction";

  if (
    ex.student_identifier &&
    (isCorrection || !current.student_identifier?.trim())
  ) {
    updates.student_identifier = ex.student_identifier;
    chips.push({ key: "Student", value: ex.student_identifier });
  }
  if (ex.grade_level && (isCorrection || !current.grade_level)) {
    updates.grade_level = ex.grade_level;
    chips.push({ key: "Grade", value: ex.grade_level });
  }
  // Intentionally NOT auto-flipping `safety_check = "pause_needed"` from
  // the AI signal alone. The classifier was over-eager for online/social
  // harm (cyberbullying, mean posts, name-calling), which then deferred
  // the reflection in cases where a written reflection is actually the
  // right move. `safety_check` should only flip to pause_needed for
  // IMMEDIATE PHYSICAL safety concerns — and those still get caught by
  // the regex hard-match on `weapon|threat|threaten|assault` in
  // `extractFromSituation`. The leader can also flip it explicitly.
  // The AI signal still surfaces as a chip so the leader sees what the
  // model noticed, but it doesn't gate the workflow.
  if (ex.mentions_safety_issue && !current.safety_check) {
    chips.push({ key: "AI flag", value: "Possible safety concern" });
  }
  if (ex.mentions_repeat_pattern && current.repeat_incident !== true) {
    updates.repeat_incident = true;
    chips.push({ key: "Repeat", value: "Yes" });
  }

  return { updates, chips };
};

const extractFromSituation = (text: string, current: Incident): Extracted => {
  const out: Extracted = {};
  const lo = text.toLowerCase();

  if (!current.grade_level) {
    const gradeMatch =
      text.match(/\b(?:grade\s*)(\d{1,2})\b/i) ||
      text.match(/\b(\d{1,2})(?:st|nd|rd|th)\s*grade\b/i) ||
      text.match(/\b(K|kinder(?:garten)?)\b/i) ||
      text.match(/\b(\d{1,2})(?:st|nd|rd|th)\b/i);
    if (gradeMatch) {
      const raw = gradeMatch[1];
      if (/^k/i.test(raw)) out.grade_level = "K-2";
      else {
        const parsed = toGradeLevel(raw);
        if (parsed) out.grade_level = parsed;
      }
    }
  }

  // First try explicit name signals ("the kid is Bob", "his name is Bob",
  // "Bob is my son"). Those take priority over dumb token-scanning and are
  // allowed to CORRECT a previous name if we got it wrong.
  const explicit = extractExplicitName(text);
  if (explicit) {
    // Only set if we don't already have a name, OR the explicit pattern is
    // clearly trying to correct one ("the kid is Bob" when current is
    // "School"). Safe default: trust explicit signals always.
    if (!current.student_identifier || explicit !== current.student_identifier) {
      out.student_identifier = explicit;
    }
  } else if (!current.student_identifier) {
    // Fall back to token-scanning: first capitalized non-stopword word OR
    // a short all-caps initials block (RF, R.F., B.J.K.) so leaders who
    // refer to students by initials still get a populated identifier.
    // Stopwords + the initials acronym blacklist keep this from grabbing
    // institutional terms ("School") or program acronyms ("IEP", "ESL").
    // Strip most punctuation but PRESERVE periods so initials like "R.F."
    // survive tokenization.
    const tokens = text.replace(/[,!?;:"'()]/g, "").split(/\s+/);
    for (const w of tokens) {
      const cleaned = w.replace(/[.,!?;:"'()]+$/g, "");
      if (cleaned.length > 1 && FIRST_NAME_RE.test(cleaned) && !NAME_STOPWORDS.has(cleaned.toLowerCase())) {
        out.student_identifier = cleaned;
        break;
      }
      if (looksLikeInitials(w) || looksLikeInitials(cleaned)) {
        out.student_identifier = looksLikeInitials(w) ? w : cleaned;
        break;
      }
    }
  }

  for (const loc of LOCATION_WORDS) {
    if (lo.includes(loc)) {
      out.location = loc;
      break;
    }
  }

  if (/\b(again|repeat|second time|third time|pattern|keeps|always|chronic)\b/i.test(text)) {
    out.repeat_incident = true;
  }

  if (/\b(weapon|threat|threaten|assault)\b/i.test(text)) {
    out.safety_check = "pause_needed";
  }

  if (!current.incident_description && text.trim().length > 0) {
    out.incident_description = text.trim();
  }

  return out;
};

const extractExtendedSupport = (
  text: string,
): { removal_type: Incident["removal_type"]; needs_iss: boolean } | null => {
  const t = text.trim().toLowerCase();
  if (!t) return null;
  if (
    /\b(iss|in[- ]school suspension|oss|suspension|suspend|removal|removed|sent home|pickup|half[- ]day|multi[- ]day|full day)\b/.test(
      t,
    )
  ) {
    return { removal_type: "formal_removal", needs_iss: true };
  }
  if (/\b(office|redirect|brief|short|quick|reset|calm down|came back|same period|same day)\b/.test(t)) {
    return { removal_type: "administrative_redirect", needs_iss: false };
  }
  if (/\b(no|nope|nah|didn'?t|stayed in class|none)\b/.test(t)) {
    return { removal_type: "", needs_iss: false };
  }
  return null;
};

// ── Typing delay ──────────────────────────────────────────────────────────
const typingDelayFor = (html: string) => {
  const plain = html.replace(/<[^>]+>/g, "");
  return Math.min(1400, 500 + plain.length * 2);
};

// ── UI: message bubbles ───────────────────────────────────────────────────
const BodhiBubble = ({ html }: { html: string }) => (
  <div className="self-start max-w-[78%] animate-in fade-in slide-in-from-bottom-1 duration-300">
    <div
      className="bg-white border border-gray-200 text-gray-900 rounded-2xl rounded-bl-sm px-4 py-3 text-sm leading-relaxed shadow-md [&>p]:my-1.5 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0 [&_ul]:my-1.5 [&_ul]:pl-5 [&_ul]:list-disc [&_ol]:my-1.5 [&_ol]:pl-5 [&_ol]:list-decimal [&_li]:my-0.5 [&_strong]:font-semibold"
      dangerouslySetInnerHTML={{ __html: formatHtml(html) }}
    />
  </div>
);

const UserBubble = ({ text }: { text: string }) => (
  <div className="self-end max-w-[78%] animate-in fade-in slide-in-from-bottom-1 duration-300">
    <div className="bg-bodhi-teal text-white rounded-2xl rounded-br-sm px-4 py-3 text-sm leading-relaxed">
      {text}
    </div>
  </div>
);

const TypingIndicator = () => (
  <div className="self-start">
    <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1 shadow-md">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:0ms]" />
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:150ms]" />
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:300ms]" />
    </div>
  </div>
);

const DataCard = ({
  chips,
}: {
  chips: { key: string; value: string }[];
}) => (
  <div className="self-start max-w-[85%] bg-white border border-bodhi-teal/40 rounded-xl px-4 py-3 shadow-sm animate-in fade-in slide-in-from-bottom-1 duration-300">
    <div className="text-[10px] uppercase tracking-wider text-bodhi-teal mb-2 font-semibold">
      ✦ Captured from what you shared
    </div>
    <div className="flex flex-wrap gap-1.5">
      {chips.map((c, i) => (
        <div
          key={i}
          className="bg-bodhi-teal/5 border border-bodhi-teal/30 rounded-lg px-2.5 py-1 text-xs text-bodhi-teal flex gap-1.5 items-center"
        >
          <span className="text-gray-600 text-[10px]">{c.key}:</span>
          <span className="font-semibold">{c.value}</span>
        </div>
      ))}
    </div>
  </div>
);

// Error boundary that wraps each output card so a single broken card never
// silently collapses to a thin border — it shows a visible error we can act on.
class CardErrorBoundary extends React.Component<
  { label: string; children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { label: string; children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(
      `[RestorationRoomChat] ${this.props.label} failed to render`,
      error,
      info,
    );
  }
  render() {
    if (this.state.error) {
      return (
        <div className="self-start w-full max-w-[96%] bg-red-50 border-2 border-red-300 rounded-2xl p-4 shadow-md">
          <div className="text-xs uppercase tracking-wider text-red-700 font-semibold mb-1">
            ⚠ {this.props.label} couldn't render
          </div>
          <p className="text-sm text-red-900 leading-relaxed">
            Something broke while building this card. Details are in the browser
            console.
          </p>
          <pre className="mt-2 text-[11px] text-red-800 bg-red-100 border border-red-200 rounded px-2 py-1.5 overflow-x-auto whitespace-pre-wrap">
            {this.state.error.message}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

const OutCardShell = ({
  eyebrow,
  title,
  tone = "teal",
  children,
}: {
  eyebrow: string;
  title: string;
  tone?: "teal" | "amber";
  children: React.ReactNode;
}) => {
  return (
  <div className="self-start shrink-0 w-full max-w-[96%] bg-white border border-gray-300 rounded-2xl overflow-hidden shadow-lg animate-in fade-in slide-in-from-bottom-1 duration-300">
    <div
      className={cn(
        "px-5 py-3 text-white border-b",
        tone === "amber" ? "bg-amber-600 border-amber-600" : "bg-bodhi-teal border-bodhi-teal",
      )}
    >
      <div className="text-[10px] uppercase tracking-wider text-white/80 font-semibold">
        {eyebrow}
      </div>
      <div className="text-base font-semibold text-white font-literata mt-0.5">{title}</div>
    </div>
    <div className="p-5 bg-white text-gray-900">{children}</div>
  </div>
  );
};

// Compact clickable tile that opens a full-content side panel. Used for
// documents (reflection, coaching guide, documentation, parent comms) that
// are too long to render inline in the chat log.
const PreviewTile = ({
  icon,
  eyebrow,
  title,
  summary,
  primaryLabel = "Open",
  onPrimary,
  secondaryActions,
}: {
  icon: React.ReactNode;
  eyebrow: string;
  title: string;
  summary: string;
  primaryLabel?: string;
  onPrimary: () => void;
  secondaryActions?: React.ReactNode;
}) => (
  <div className="self-start shrink-0 w-full max-w-[96%] bg-white border border-gray-300 rounded-2xl shadow-md animate-in fade-in slide-in-from-bottom-1 duration-300 overflow-hidden">
    <button
      type="button"
      onClick={onPrimary}
      className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-bodhi-teal/5 transition-colors group"
    >
      <div className="w-10 h-10 rounded-xl bg-bodhi-teal text-white flex items-center justify-center flex-shrink-0 shadow-sm">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-bodhi-teal font-semibold">
          {eyebrow}
        </div>
        <div className="text-sm font-semibold text-gray-900 font-literata leading-tight mt-0.5 truncate">
          {title}
        </div>
        <div className="text-xs text-gray-600 leading-snug mt-0.5 line-clamp-2">
          {summary}
        </div>
      </div>
      <div className="flex-shrink-0 flex items-center gap-1 text-bodhi-teal text-xs font-medium group-hover:translate-x-0.5 transition-transform">
        <span>{primaryLabel}</span>
        <ExternalLink className="w-3.5 h-3.5" aria-hidden />
      </div>
    </button>
    {secondaryActions && (
      <div className="px-4 py-2 border-t border-gray-100 bg-gray-50/60 flex flex-wrap gap-1.5">
        {secondaryActions}
      </div>
    )}
  </div>
);

// Claude-generated hypotheses for "what was actually going on for this
// student". Rendered at the `understand` step alongside the static
// quick-reply chips so the admin can either pick a read directly or keep
// scrolling to their own instinct.
const MotivationCard = ({
  hypotheses,
  onPick,
  disabled,
}: {
  hypotheses: MotivationResult["hypotheses"];
  onPick: (h: MotivationResult["hypotheses"][number]) => void;
  disabled?: boolean;
}) => (
  <div className="self-start shrink-0 w-full max-w-[96%] bg-gradient-to-br from-bodhi-teal/5 via-white to-bodhi-gold/5 border border-bodhi-teal/30 rounded-2xl shadow-md animate-in fade-in slide-in-from-bottom-1 duration-300 overflow-hidden">
    <div className="px-4 py-2.5 border-b border-bodhi-teal/20 bg-bodhi-teal/10">
      <div className="text-[10px] uppercase tracking-wider text-bodhi-teal font-semibold">
        ✨ Possible reads · for you to consider
      </div>
      <div className="text-sm text-gray-700 mt-0.5">
        Hypotheses based on what you described. Pick one, probe it in
        conversation, or stick with your own instinct below.
      </div>
    </div>
    <div className="p-3 space-y-2.5">
      {hypotheses.map((h, i) => (
        <div
          key={`${h.label}-${i}`}
          className="rounded-lg border border-gray-200 bg-white p-3 hover:border-bodhi-teal/40 transition-colors"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900">
                {h.label}
              </div>
              <div className="text-xs text-gray-700 mt-1 leading-relaxed">
                {h.why}
              </div>
              <div className="mt-2 text-xs text-bodhi-teal italic border-l-2 border-bodhi-teal/40 pl-2">
                Try asking: &ldquo;{h.probe}&rdquo;
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => onPick(h)}
              className="flex-shrink-0 text-xs border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal hover:text-white"
            >
              Use this read
            </Button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Claude's recommendation at the ISS / office-redirect / stay-in-class fork.
// Not an auto-decide — the admin still picks from the chips below it. The
// recommendation line maps 1:1 to one of the chip labels so it's obvious
// which option Claude leans toward.
const RemovalDecisionCard = ({
  decision,
}: {
  decision: RemovalDecisionResult;
}) => {
  const recLabelByKey: Record<RemovalDecisionResult["recommendation"], string> =
    {
      stay_in_class: "Stayed in class",
      office_redirect: "Brief office redirect",
      iss_or_formal_removal: "ISS / formal removal — needs plan",
    };
  const recLabel = recLabelByKey[decision.recommendation] ?? "—";
  return (
    <div className="self-start shrink-0 w-full max-w-[96%] bg-gradient-to-br from-bodhi-teal/5 via-white to-bodhi-gold/5 border border-bodhi-teal/30 rounded-2xl shadow-md animate-in fade-in slide-in-from-bottom-1 duration-300 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-bodhi-teal/20 bg-bodhi-teal/10">
        <div className="text-[10px] uppercase tracking-wider text-bodhi-teal font-semibold">
          ✨ Your AI read on next step
        </div>
        <div className="text-sm font-semibold text-gray-900 mt-0.5 font-literata">
          Lean toward: <span className="text-bodhi-teal">{recLabel}</span>
        </div>
      </div>
      <div className="p-4 space-y-3 text-sm text-gray-800">
        {decision.headline && (
          <p className="text-sm font-medium text-gray-900">
            {decision.headline}
          </p>
        )}
        {decision.rationale && (
          <p className="text-sm leading-relaxed text-gray-700">
            {decision.rationale}
          </p>
        )}
        {decision.considerations?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
              Consider
            </div>
            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
              {decision.considerations.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </div>
        )}
        {decision.flags?.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1">
              ⚠ Flags
            </div>
            <ul className="list-disc pl-5 space-y-0.5 text-xs text-amber-900">
              {decision.flags.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-[11px] italic text-gray-500">
          Your call — this is a second set of eyes, not a verdict.
        </p>
      </div>
    </div>
  );
};

// Post-conversation debrief — what worked, watch-for, and time-sequenced
// follow-up steps. Shows up after the admin describes the conversation at
// the `resolution` step.
const DebriefCard = ({ debrief }: { debrief: DebriefResult }) => (
  <div className="self-start shrink-0 w-full max-w-[96%] bg-white border border-bodhi-teal/30 rounded-2xl shadow-md animate-in fade-in slide-in-from-bottom-1 duration-300 overflow-hidden">
    <div className="px-4 py-2.5 border-b border-bodhi-teal/20 bg-bodhi-teal/10">
      <div className="text-[10px] uppercase tracking-wider text-bodhi-teal font-semibold">
        ✨ Post-conversation debrief
      </div>
      <div className="text-sm text-gray-700 mt-0.5">
        Quick read on how that landed, plus a follow-up plan.
      </div>
    </div>
    <div className="p-4 space-y-4 text-sm text-gray-800">
      {debrief.what_worked?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">
            What worked
          </div>
          <ul className="list-disc pl-5 space-y-0.5 text-sm text-gray-700">
            {debrief.what_worked.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
      {debrief.watch_for?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1">
            Watch for
          </div>
          <ul className="list-disc pl-5 space-y-0.5 text-sm text-gray-700">
            {debrief.watch_for.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
      {debrief.follow_up_steps?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-bodhi-teal font-semibold mb-1">
            Follow-up plan
          </div>
          <ul className="space-y-1.5">
            {debrief.follow_up_steps.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="inline-block px-1.5 py-0.5 rounded bg-bodhi-teal/10 text-bodhi-teal text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap">
                  {s.when}
                </span>
                <span className="text-gray-700">{s.action}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  </div>
);

// "What am I missing?" card — surfaces 0-3 gaps in the record so the admin
// can answer them inline. The `suggested_prompt` from Claude is rewritten as
// a direct question TO the admin (not an AI scratchpad). Answers flow into
// conversation_notes with a labeled header so they show up in the record.
const MissingChecksCard = ({
  ready,
  gaps,
  onDismiss,
  onAnswer,
  onComplete,
}: {
  ready: boolean;
  gaps: { field: string; why_it_matters: string; suggested_prompt: string }[];
  onDismiss: () => void;
  onAnswer: (gapField: string, answer: string) => void;
  onComplete?: (summary: { answered: number; skipped: number }) => void;
}) => {
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [savedIdx, setSavedIdx] = useState<Record<number, boolean>>({});
  const [skippedIdx, setSkippedIdx] = useState<Record<number, boolean>>({});
  const [continued, setContinued] = useState(false);

  const handleSave = (idx: number, field: string) => {
    const value = drafts[idx]?.trim();
    if (!value) return;
    onAnswer(field, value);
    setSavedIdx((s) => ({ ...s, [idx]: true }));
  };

  const handleSkip = (idx: number) => {
    setSkippedIdx((s) => ({ ...s, [idx]: true }));
    setSavedIdx((s) => ({ ...s, [idx]: true }));
  };

  const handledCount = gaps.reduce(
    (acc, _, i) => acc + (savedIdx[i] ? 1 : 0),
    0,
  );
  const answeredCount = gaps.reduce(
    (acc, _, i) => acc + (savedIdx[i] && !skippedIdx[i] ? 1 : 0),
    0,
  );
  const skippedCount = gaps.reduce(
    (acc, _, i) => acc + (skippedIdx[i] ? 1 : 0),
    0,
  );
  const allHandled = gaps.length > 0 && handledCount === gaps.length;
  const anyHandled = handledCount > 0;

  const handleContinue = () => {
    if (continued) return;
    setContinued(true);
    onComplete?.({ answered: answeredCount, skipped: skippedCount });
  };

  return (
    <div className="self-start shrink-0 w-full max-w-[96%] bg-white border border-amber-300/60 rounded-2xl shadow-sm animate-in fade-in slide-in-from-bottom-1 duration-300 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-amber-200 bg-amber-50">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">
              ✨ What am I missing?
            </div>
            <div className="text-sm text-gray-700 mt-0.5">
              {ready
                ? "Record looks solid — one or two things worth adding."
                : "A few things worth filling in. Answer the ones you know, skip the rest."}
            </div>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="text-[11px] text-amber-800/70 hover:text-amber-900 underline decoration-dotted underline-offset-2"
          >
            Dismiss
          </button>
        </div>
      </div>
      <div className="p-4 space-y-3 text-sm text-gray-800">
        {gaps.map((g, i) => {
          const saved = !!savedIdx[i];
          return (
            <div
              key={i}
              className={cn(
                "rounded-md border p-3 transition-colors",
                saved
                  ? "border-emerald-300/70 bg-emerald-50/60"
                  : "border-amber-200/60 bg-amber-50/40",
              )}
            >
              <div
                className={cn(
                  "text-[11px] font-semibold uppercase tracking-wide",
                  saved ? "text-emerald-800" : "text-amber-900",
                )}
              >
                {g.field} {saved && "· Saved ✓"}
              </div>
              <div className="text-sm text-gray-700 mt-0.5 leading-snug">
                {g.why_it_matters}
              </div>
              {g.suggested_prompt && !saved && (
                <div className="mt-2 text-sm text-gray-800 font-medium">
                  {g.suggested_prompt}
                </div>
              )}
              {saved ? (
                <div className="mt-2 text-sm text-gray-700 italic">
                  "{drafts[i]?.trim()}"
                </div>
              ) : (
                <>
                  <Textarea
                    rows={2}
                    value={drafts[i] ?? ""}
                    onChange={(e) =>
                      setDrafts((d) => ({ ...d, [i]: e.target.value }))
                    }
                    placeholder="Type your answer here…"
                    className="mt-2 text-sm bg-white"
                  />
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      onClick={() => handleSave(i, g.field)}
                      disabled={!drafts[i]?.trim()}
                      className="h-7 text-xs bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40"
                    >
                      Save answer
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleSkip(i)}
                      className="h-7 text-xs text-gray-500"
                    >
                      Skip
                    </Button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      {/* Footer: always visible once the admin has handled at least one gap.
          Turns into a prominent "continue" affordance when all are handled,
          otherwise lets them jump back to the workflow at any time. */}
      {anyHandled && (
        <div className="px-4 pb-4 pt-1 border-t border-amber-200/60 bg-amber-50/30 flex items-center justify-between gap-3">
          <div className="text-[11px] text-gray-600">
            {allHandled
              ? `All set — ${answeredCount} answered${
                  skippedCount ? `, ${skippedCount} skipped` : ""
                }.`
              : `${handledCount} of ${gaps.length} handled.`}
          </div>
          <Button
            size="sm"
            onClick={handleContinue}
            disabled={continued}
            className={cn(
              "h-8 text-xs",
              allHandled
                ? "bg-bodhi-teal text-white hover:bg-bodhi-teal/90"
                : "bg-white text-bodhi-teal border-2 border-bodhi-teal/60 hover:bg-bodhi-teal hover:text-white",
            )}
          >
            {continued
              ? "Continued ✓"
              : allHandled
                ? "Done — continue"
                : "Continue anyway"}
          </Button>
        </div>
      )}
    </div>
  );
};

const QuickReplies = ({
  options,
  onPick,
  disabled,
  selected,
}: {
  options: QuickReply[];
  onPick: (o: QuickReply) => void;
  disabled: boolean;
  selected?: string[];
}) => {
  // Split options into two visual rows so "action" chips (Done / Skip /
  // Jump forward) don't blend into the list of choices. Options render
  // first, actions render below with a bit of air between them.
  const optionChips = options.filter((o) => !o.isAction);
  const actionChips = options.filter((o) => o.isAction);

  const renderChip = (o: QuickReply, i: number, action = false) => {
    const isChecked =
      !!o.multi && !!o.payload && (selected ?? []).includes(o.payload);
    return (
      <button
        key={`${action ? "a" : "o"}-${i}`}
        disabled={disabled}
        onClick={() => onPick(o)}
        aria-pressed={o.multi ? isChecked : undefined}
        className={cn(
          "rounded-full px-3.5 py-1.5 text-xs font-medium shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-2",
          action
            ? "bg-bodhi-teal text-white border-bodhi-teal hover:bg-bodhi-teal/90"
            : isChecked
              ? "bg-bodhi-teal text-white border-bodhi-teal hover:bg-bodhi-teal/90"
              : "bg-white text-bodhi-teal border-bodhi-teal/60 hover:bg-bodhi-teal hover:text-white hover:border-bodhi-teal",
        )}
      >
        {isChecked ? `✓ ${o.label}` : o.label}
      </button>
    );
  };

  return (
    <div className="self-start flex flex-col gap-2 max-w-[92%] animate-in fade-in slide-in-from-bottom-1 duration-300">
      {optionChips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {optionChips.map((o, i) => renderChip(o, i))}
        </div>
      )}
      {actionChips.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1 mt-1 border-t border-gray-200">
          {actionChips.map((o, i) => renderChip(o, i, true))}
        </div>
      )}
    </div>
  );
};

// ── Attachment helpers ────────────────────────────────────────────────────
const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileTypeIcon = ({ mimeType }: { mimeType: string }) => {
  if (mimeType.startsWith("image/")) {
    return <ImageIcon className="w-4 h-4" aria-hidden />;
  }
  return <FileText className="w-4 h-4" aria-hidden />;
};

const AttachmentCard = ({
  fileName,
  fileSize,
  mimeType,
  status,
  errorMessage,
  fileId,
  onView,
  onRemove,
  isReadOnly,
}: {
  fileName: string;
  fileSize: number;
  mimeType: string;
  status: "uploading" | "uploaded" | "error";
  errorMessage?: string;
  fileId?: string;
  onView?: (fileId: string) => void;
  onRemove?: (fileId: string) => void;
  isReadOnly?: boolean;
}) => {
  const border =
    status === "error"
      ? "border-red-200"
      : status === "uploading"
        ? "border-gray-200"
        : "border-bodhi-teal/40";
  const bg =
    status === "error"
      ? "bg-red-50"
      : status === "uploading"
        ? "bg-gray-50"
        : "bg-white";
  return (
    <div
      className={cn(
        "self-start w-full max-w-[96%] flex items-center gap-3 rounded-xl border px-3.5 py-2.5 shadow-sm animate-in fade-in slide-in-from-bottom-1 duration-200",
        border,
        bg,
      )}
    >
      <div
        className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
          status === "error"
            ? "bg-red-100 text-red-600"
            : "bg-bodhi-teal/10 text-bodhi-teal",
        )}
      >
        {status === "uploading" ? (
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
        ) : (
          <FileTypeIcon mimeType={mimeType} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">{fileName}</div>
        <div className="text-[11px] text-gray-500">
          {status === "uploading"
            ? "Uploading…"
            : status === "error"
              ? errorMessage ?? "Upload failed"
              : `${formatFileSize(fileSize)} · Attached to incident`}
        </div>
      </div>
      {status === "uploaded" && fileId && (
        <div className="flex items-center gap-1 flex-shrink-0">
          {onView && (
            <button
              type="button"
              onClick={() => onView(fileId)}
              className="text-bodhi-teal hover:bg-bodhi-teal/10 rounded-md px-2 py-1 text-xs font-medium flex items-center gap-1 transition-colors"
              aria-label={`Open ${fileName}`}
            >
              <Download className="w-3.5 h-3.5" aria-hidden />
              View
            </button>
          )}
          {onRemove && !isReadOnly && (
            <button
              type="button"
              onClick={() => onRemove(fileId)}
              className="text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md px-2 py-1 text-xs font-medium flex items-center gap-1 transition-colors"
              aria-label={`Remove ${fileName}`}
            >
              <Trash2 className="w-3.5 h-3.5" aria-hidden />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const CompleteBanner = ({
  studentLabel,
  onNewIncident,
}: {
  studentLabel: string;
  onNewIncident: () => void;
}) => (
  <div className="self-start w-full max-w-[96%] bg-bodhi-teal text-white rounded-2xl p-7 animate-in fade-in slide-in-from-bottom-1 duration-300">
    <div className="text-[10px] uppercase tracking-wider text-white/60 mb-2 font-medium">
      ✦ Incident complete
    </div>
    <h3 className="text-xl font-literata mb-2">You walked through this with care.</h3>
    <p className="text-sm text-white/80 leading-relaxed">
      {studentLabel} had someone who held both accountability and their humanity at the same time.
      This record has been saved. You can find it any time under Behavior Incident Toolkit → Incidents.
    </p>
    <div className="mt-5">
      <Button
        onClick={onNewIncident}
        variant="outline"
        size="sm"
        className="bg-white/10 text-white border-white/40 hover:bg-white/20"
      >
        + Start a new incident
      </Button>
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────
export const RestorationRoomChat = forwardRef<
  RestorationRoomChatHandle,
  RestorationRoomChatProps
>(function RestorationRoomChat(
  {
    incident,
    gradeGuidance,
    reflectionPrompts,
    buildConversationGuide,
    downloadIncidentPacketPDF,
    onUpdateIncident,
    onCloseIncident,
    onNewIncident,
    onBackToDashboard,
    onStageChange,
    isReadOnly = false,
    isUnsavedDraft = false,
    onAttachmentsChange,
  },
  ref,
) {
  const { session, userData } = useAuth();
  // Display name attached to handoff notes so the next person knows who
  // saved the incident. Falls back to the auth email's local-part, then
  // a generic label.
  const currentUserName =
    userData?.user_name?.trim() ||
    session?.user?.email?.split("@")[0] ||
    "A colleague";
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messagesRef = useRef<ChatMessage[]>([]);
  const [step, setStep] = useState<StepId>("welcome");
  const [input, setInput] = useState("");
  const [inputPlaceholder, setInputPlaceholder] = useState("Share what's on your mind...");
  const [isTyping, setIsTyping] = useState(false);
  const [quickRepliesDisabled, setQuickRepliesDisabled] = useState(false);
  const [attachments, setAttachments] = useState<FileUpload[]>([]);

  // Rolling history replayed to the Claude-backed `mentor-chat` edge function
  // for in-flow coaching ("help me with what to say right now"). Claude has no
  // server-side thread memory, so we send recent turns each call.
  const mentorHistoryRef = useRef<
    { role: "user" | "assistant"; content: string }[]
  >([]);
  const mentorContextSentRef = useRef(false);
  // `goto` is declared below `handleFilePicked`; keep a ref so uploads can
  // advance the scripted flow (e.g. skip redundant iep504 after a BIP PDF).
  const gotoRef = useRef<((next: StepId, flowEpoch?: number) => Promise<void>) | null>(null);

  // Which output is currently open in the right-side Sheet. Keeps the chat
  // log scannable — full documents render in the panel instead of inline.
  type PanelState =
    | null
    | { kind: "reflection" }
    | { kind: "coaching"; guideHtml: string; grade: GradeLevel; isAi?: boolean }
    | { kind: "iss-docs" }
    | { kind: "documentation" }
    | { kind: "parent"; initialTab?: "email" | "phone_script" | "student_call" }
    | { kind: "teacher-summary" };
  const [openPanel, setOpenPanel] = useState<PanelState>(null);
  // Edit toggles for the right-side Sheet panels so admins can correct
  // documentation and reflection copy without jumping back through the chat.
  const [docEditing, setDocEditing] = useState(false);
  const [reflectionEditing, setReflectionEditing] = useState(false);
  const [teacherSummaryEditing, setTeacherSummaryEditing] = useState(false);
  // Session-scoped teacher written-summary draft. We generate the first
  // version via the mentor, then let the leader edit/copy/download/print
  // it from the panel. State, not persisted — re-generation overwrites.
  const [teacherSummary, setTeacherSummary] = useState<{
    text: string;
    isAi: boolean;
    generatedAt: string;
  } | null>(null);
  const [teacherSummaryGenerating, setTeacherSummaryGenerating] = useState(false);
  // Session-scoped overrides for the reflection form (intro / prompts /
  // closing). Not persisted — they rebuild the rendered form for the user
  // while the panel is open and keyed to the current incident.
  const [reflectionOverrides, setReflectionOverrides] = useState<{
    intro?: string;
    closing?: string;
    prompts?: { question: string; hint: string }[];
  } | null>(null);

  // Claude-generated conversation guide + reflection prompts for the active
  // incident. Null = not yet generated (or generation failed, and we fall back
  // to the templated outputs). Keyed implicitly by incident id — we reset
  // these in the effect below whenever the incident switches.
  const [aiGuide, setAiGuide] = useState<{ markdown: string; html: string } | null>(null);
  const [aiReflection, setAiReflection] = useState<ReflectionPromptsResult | null>(null);
  // Ref mirrors so file-upload and quick-reply handlers avoid stale closures
  // when deciding whether Tier-2 "regenerate with plan text" should appear.
  const aiGuideRef = useRef<typeof aiGuide>(null);
  const aiReflectionRef = useRef<typeof aiReflection>(null);
  useEffect(() => {
    aiGuideRef.current = aiGuide;
  }, [aiGuide]);
  useEffect(() => {
    aiReflectionRef.current = aiReflection;
  }, [aiReflection]);
  // Tracks the incident id we last reset AI state for, so switching to a new
  // incident discards stale generations before rendering them.
  const aiIncidentIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (aiIncidentIdRef.current !== incident.id) {
      aiIncidentIdRef.current = incident.id;
      setAiGuide(null);
      setAiReflection(null);
      materialsGenRef.current = null;
      pendingGuideHtmlRef.current = null;
      pendingGuideIsAiRef.current = false;
    }
  }, [incident.id]);

  // Guards so we only kick off each one-shot generation once per incident +
  // step entry, even if the user bounces between steps or the component
  // re-renders mid-await. Each key is the incident id it last fired for.
  const motivationGenRef = useRef<string | null>(null);
  const materialsGenRef = useRef<string | null>(null);
  const removalDecisionGenRef = useRef<string | null>(null);
  const debriefGenRef = useRef<string | null>(null);
  // Holds the drafted conversation-guide HTML between genOutputs phase A
  // (materials drafted) and phase B (user taps "Show me the conversation
  // guide"). Lets us keep the two phases paced without re-calling Claude.
  const pendingGuideHtmlRef = useRef<string | null>(null);
  const pendingGuideIsAiRef = useRef<boolean>(false);
  const pendingReflectionDeferReasonRef = useRef<string | null>(null);
  // Monotonic turn counter used to suppress stale async emissions from older
  // scripted runs (e.g. genOutputs phase A) after a newer user turn arrives.
  const flowEpochRef = useRef(0);
  // When non-null, the leader is "revisiting" a completed stage to read /
  // add to what's there — NOT to re-answer scripted questions. Typed text
  // is appended to the record as a review note instead of being routed
  // through the step's normal text handler. Cleared automatically by any
  // jump back to a non-review step (goto, continue, jumping to current).
  const reviewStageRef = useRef<ChatTabStage | null>(null);
  // Plain-text snapshot of the last thing Bodhi said. Used as context for
  // the AI intent classifier so it can tell e.g. an answer to a yes/no
  // question apart from a generic comment that happens to contain "yeah".
  // Captured in `sayBodhi`. HTML tags are stripped so the classifier
  // prompt stays clean.
  const lastBodhiPromptRef = useRef<string | null>(null);
  const [reentryDrafting, setReentryDrafting] = useState(false);
  const [polishingDesc, setPolishingDesc] = useState(false);
  const [descPolishDraft, setDescPolishDraft] = useState<string | null>(null);
  const [draftingNarrative, setDraftingNarrative] = useState(false);
  const [narrativeDraft, setNarrativeDraft] = useState<string | null>(null);
  // Last polish/narrative the leader explicitly accepted. Used to suppress
  // the auto-draft loop after acceptance: if `incident.incident_description`
  // already equals the accepted polish, or `conversation_notes` already
  // contains the accepted narrative, we don't auto-redraft. Persisted to
  // localStorage alongside the drafts so reopening the panel doesn't
  // resurrect the "Use this version" / "Add to case notes" prompt.
  const [acceptedPolish, setAcceptedPolish] = useState<string | null>(null);
  const [acceptedNarrative, setAcceptedNarrative] = useState<string | null>(null);
  const docAutoDraftedRef = useRef<{ polish: boolean; narrative: boolean }>({
    polish: false,
    narrative: false,
  });
  const docContextSigRef = useRef<string>("");
  const [checkingMissing, setCheckingMissing] = useState(false);
  const [missingResult, setMissingResult] = useState<MissingChecksResult | null>(null);
  const missingAutoCheckRef = useRef<string | null>(null);
  const [parentDrafting, setParentDrafting] = useState<
    null | "email" | "phone_script" | "student_call"
  >(null);

  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Tracks the incident id we have already bootstrapped for, so StrictMode
  // double-invokes in development don't duplicate the welcome message.
  const bootstrappedForIdRef = useRef<string | null>(null);
  // While true, the auto-scroll-to-bottom effect is paused and we instead
  // pin the chat to the top. Set on every incident-id change so a freshly
  // loaded chat opens at its first message (instead of being pushed
  // partway down by the welcome + quick-reply chips fitting in the panel),
  // and cleared as soon as the user actually engages (sends, picks a chip,
  // or types).
  const pinToTopRef = useRef<boolean>(true);
  // Track current step inside callbacks without re-creating them
  const stepRef = useRef<StepId>("welcome");
  useEffect(() => {
    stepRef.current = step;
  }, [step]);
  const incidentRef = useRef(incident);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  useEffect(() => {
    incidentRef.current = incident;
  }, [incident]);

  useEffect(() => {
    onStageChange(stageForStep(step));
  }, [step, onStageChange]);

  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;
    // Right after an incident loads we want the user reading from the
    // top of the chat, not pinned to the bottom of the welcome + chips.
    // pinToTopRef stays true until the first user-driven turn.
    if (pinToTopRef.current) {
      el.scrollTop = 0;
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [messages, isTyping]);

  const append = useCallback((msg: ChatMessage) => {
    setMessages((m) => [...m, msg]);
  }, []);

  // Ensure the amber Federal Guidance / IDEA-implications card is shown
  // exactly once per incident as soon as we know an IEP/504/BIP is in
  // play — regardless of whether it was confirmed by chip, by free text,
  // or by uploading the plan PDF. Without this, leaders who skipped the
  // "Yes — IEP" chip path (e.g. by uploading the document and then
  // confirming on iep504Refine) never saw the manifestation/services
  // reminders, which is the exact gap leaders flagged.
  const ensureFederalGuidanceShown = useCallback(
    (plan: "iep" | "504" | "both" | "bip") => {
      const alreadyShown = messagesRef.current.some(
        (m) => m.kind === "iep504-guidance",
      );
      if (alreadyShown) return;
      append({ kind: "iep504-guidance", id: genId(), plan });
    },
    [append],
  );
  const beginFlowEpoch = useCallback(() => {
    flowEpochRef.current += 1;
    return flowEpochRef.current;
  }, []);
  const isStaleFlow = useCallback(
    (epoch: number) => flowEpochRef.current !== epoch,
    [],
  );

  const sayBodhi = useCallback(
    (html: string) =>
      new Promise<void>((resolve) => {
        setIsTyping(true);
        const delay = typingDelayFor(html);
        // Snapshot a plain-text version of what Bodhi is about to say so
        // the AI classifier knows what was just asked. We set this before
        // the typing-delay timeout so any racing classifier call already
        // sees the new prompt as "what Bodhi just said."
        lastBodhiPromptRef.current = html
          .replace(/<[^>]+>/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        window.setTimeout(() => {
          setIsTyping(false);
          append({ kind: "bodhi", id: genId(), html });
          resolve();
        }, delay);
      }),
    [append],
  );

  const sayUser = useCallback(
    (text: string) => append({ kind: "user", id: genId(), text }),
    [append],
  );

  // Call the Claude-backed `mentor-chat` edge function with the user's free-
  // text message. Returns rendered HTML (via markdown-it) for the reply, or
  // null on failure. The first user turn is prefixed with an incident context
  // block so Claude understands the student/grade/removal situation.
  const askMentor = useCallback(
    async (
      userText: string,
    ): Promise<{ html: string } | { error: string } | null> => {
      if (!session?.access_token) {
        return { error: "You need to be signed in to reach the mentor." };
      }
      const cur = incidentRef.current;
      const curStep = stepRef.current;

      const payloadText = mentorContextSentRef.current
        ? userText
        : buildMentorContextPreamble(cur, curStep) + userText;
      mentorContextSentRef.current = true;

      mentorHistoryRef.current = [
        ...mentorHistoryRef.current,
        { role: "user" as const, content: payloadText },
      ].slice(-20);

      try {
        setIsTyping(true);
        const invokeResult = await supabase.functions.invoke("mentor-chat", {
          body: { messages: mentorHistoryRef.current },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });
        const { data, error } = invokeResult;
        // supabase-js v2.70+ also hands us the raw Response at the top level,
        // even when the function returned a non-2xx. Use it to pull out the
        // real error message the edge function wrote (e.g. "Anthropic:
        // invalid_api_key"). `error.context` is the same Response as a
        // fallback on older clients.
        const rawResponse =
          (invokeResult as unknown as { response?: Response }).response ??
          ((error as unknown as { context?: Response })?.context);

        if (error) {
          let detail = error instanceof Error ? error.message : String(error);
          try {
            if (rawResponse && typeof rawResponse.clone === "function") {
              const text = await rawResponse.clone().text();
              if (text) {
                try {
                  const parsed = JSON.parse(text);
                  if (parsed?.error) {
                    detail = String(parsed.error);
                  } else if (parsed?.message) {
                    detail = String(parsed.message);
                  } else {
                    detail = `HTTP ${rawResponse.status}: ${text.slice(0, 200)}`;
                  }
                } catch {
                  detail = `HTTP ${rawResponse.status}: ${text.slice(0, 200)}`;
                }
              } else {
                detail = `HTTP ${rawResponse.status}`;
              }
            }
          } catch (parseErr) {
            console.warn(
              "[RestorationRoomChat] could not parse mentor error body",
              parseErr,
            );
          }
          console.error(
            "[RestorationRoomChat] mentor-chat invoke error:",
            error,
            "status=",
            rawResponse?.status,
            "→",
            detail,
          );
          return { error: detail };
        }
        if (data?.error) {
          console.error("[RestorationRoomChat] mentor-chat data error:", data.error);
          return { error: String(data.error) };
        }
        const replyText: string = data?.content || "";
        if (!replyText) return { error: "Mentor returned an empty response." };
        mentorHistoryRef.current = [
          ...mentorHistoryRef.current,
          { role: "assistant" as const, content: replyText },
        ].slice(-20);
        return { html: mentorMd.render(replyText) };
      } catch (err) {
        console.error("[RestorationRoomChat] mentor-chat error:", err);
        const msg = err instanceof Error ? err.message : String(err);
        return { error: msg };
      } finally {
        setIsTyping(false);
      }
    },
    [session?.access_token],
  );

  // ── AI intent classifier (Tier 1, Step A) ────────────────────────────────
  // Calls the `restoration-classify-turn` edge function with the user's
  // latest message + minimal workflow context. Returns a structured
  // verdict (intent + confidence + extracted fields) or null on any
  // failure. The client only routes by AI verdict when confidence is
  // high enough AND the intent is one of the categories we've decided
  // to trust in this phase (orientation + review-mode navigation today).
  // Every other intent falls through to the existing scripted/regex
  // logic — the AI verdict is logged regardless so we can tune before
  // deleting any of those paths.
  const classifyTurn = useCallback(
    async (
      text: string,
    ): Promise<ClassifyVerdict | null> => {
      if (!session?.access_token) return null;
      const cur = incidentRef.current;
      const curStep = stepRef.current;
      try {
        const start = performance.now();
        const { data, error } = await supabase.functions.invoke(
          "restoration-classify-turn",
          {
            body: {
              text,
              context: {
                currentStep: curStep,
                currentStage: stageForStep(curStep),
                lastBodhiPrompt: lastBodhiPromptRef.current,
                isInReviewMode: !!reviewStageRef.current,
                reviewStage: reviewStageRef.current,
                knownFacts: {
                  studentName: cur.student_identifier ?? null,
                  grade: cur.grade_level || null,
                  incidentDescription: cur.incident_description ?? null,
                  iep504: cur.iep_504_flag ?? null,
                },
              },
            },
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          },
        );
        const elapsed = Math.round(performance.now() - start);
        if (error) {
          console.warn(
            "[classify-turn] invoke error after",
            elapsed,
            "ms:",
            error,
          );
          return null;
        }
        if (!data || data.error) {
          console.warn(
            "[classify-turn] empty/error response after",
            elapsed,
            "ms:",
            data?.error,
          );
          return null;
        }
        const verdict = data as ClassifyVerdict;
        // Defensive shape check — the edge function normalizes, but if
        // it's ever bypassed (network proxy, misdeploy, etc.) we don't
        // want a missing field to throw.
        if (
          !verdict.intent ||
          typeof verdict.confidence !== "number" ||
          !verdict.extracted
        ) {
          console.warn("[classify-turn] malformed verdict:", verdict);
          return null;
        }
        verdict.extracted = {
          ...verdict.extracted,
          human_centered_pause: !!verdict.extracted.human_centered_pause,
          human_centered_confidence:
            typeof verdict.extracted.human_centered_confidence === "number"
              ? Math.max(
                  0,
                  Math.min(1, verdict.extracted.human_centered_confidence),
                )
              : 0,
        };
        console.log(
          `[classify-turn] step=${curStep} intent=${verdict.intent} ` +
            `conf=${verdict.confidence.toFixed(2)} elapsed=${elapsed}ms ` +
            `humanPause=${verdict.extracted.human_centered_pause} ` +
            `humanConf=${verdict.extracted.human_centered_confidence.toFixed(2)} ` +
            `reasoning="${verdict.reasoning}"`,
        );
        return verdict;
      } catch (err) {
        console.warn("[classify-turn] threw:", err);
        return null;
      }
    },
    [session?.access_token],
  );

  // ── AI generation helpers ───────────────────────────────────────────────
  // Claude-generated motivation hypotheses — fires when the admin lands on
  // the "understand" step. Non-blocking: the existing quick-reply chips stay
  // available while this runs. If generation fails, we silently drop it (the
  // admin still has the chips). The card only renders if the admin is still
  // on the `understand` step when Claude replies — otherwise it would show up
  // below a later step's prompts and be confusing.
  const generateMotivationIfNeeded = useCallback(
    async (incidentId: string): Promise<{ posted: boolean }> => {
      if (motivationGenRef.current === incidentId) return { posted: false };
      motivationGenRef.current = incidentId;
      const res = await generate(
        "motivation",
        { incident: incidentRef.current },
        session?.access_token,
      );
      if (!res.ok) {
        console.warn("[RestorationRoomChat] motivation generation failed:", res.error);
        return { posted: false };
      }
      if (res.content?.kind !== "motivation") return { posted: false };
      const hypotheses = res.content.data.hypotheses?.filter(
        (h) => h && h.label && h.why && h.probe,
      );
      if (!hypotheses?.length) return { posted: false };
      if (stepRef.current !== "understand") return { posted: false };
      append({ kind: "motivation-card", id: genId(), hypotheses });
      return { posted: true };
    },
    [session?.access_token, append],
  );

  // Claude-generated conversation guide + reflection prompts for the
  // `genOutputs` step. Awaited so the resulting cards carry the AI output
  // (rather than the templated fallback) from the moment they appear.
  // Returns `{ guideHtml, reflection }` — either may be null if that
  // specific generation failed, in which case the caller uses its fallback.
  const generateMaterials = useCallback(
    async (
      incidentId: string,
      opts?: { force?: boolean },
    ): Promise<{
      guideHtml: string | null;
      reflection: ReflectionPromptsResult | null;
    }> => {
      const force = !!opts?.force;
      if (
        !force &&
        materialsGenRef.current === incidentId &&
        (aiGuide || aiReflection)
      ) {
        return {
          guideHtml: aiGuide?.html ?? null,
          reflection: aiReflection ?? null,
        };
      }
      materialsGenRef.current = incidentId;

      const token = session?.access_token;
      const cur = incidentRef.current;
      const [guideRes, reflectionRes] = await Promise.all([
        generate("conversation_guide", { incident: cur }, token),
        generate("reflection_prompts", { incident: cur }, token),
      ]);

      let guideHtml: string | null = null;
      if (guideRes.ok && guideRes.content?.kind === "markdown") {
        // Reuse the mentor markdown renderer — its defaults (html disabled,
        // linkify on) are right for Claude output and it already powers the
        // inline coaching bubbles, so headings/lists render identically.
        guideHtml = mentorMd.render(guideRes.content.markdown);
        setAiGuide({ markdown: guideRes.content.markdown, html: guideHtml });
      } else if (!guideRes.ok) {
        console.warn(
          "[RestorationRoomChat] conversation_guide failed:",
          guideRes.error,
        );
      }

      let reflection: ReflectionPromptsResult | null = null;
      if (
        reflectionRes.ok &&
        reflectionRes.content?.kind === "reflection_prompts"
      ) {
        const data = reflectionRes.content.data;
        if (data?.prompts?.length) {
          reflection = data;
          setAiReflection(data);
        }
      } else if (!reflectionRes.ok) {
        console.warn(
          "[RestorationRoomChat] reflection_prompts failed:",
          reflectionRes.error,
        );
      }

      return { guideHtml, reflection };
    },
    [session?.access_token, aiGuide, aiReflection],
  );

  // Tier 2 — after a BIP/IEP/504 upload, an extra free-text turn, or a
  // human-context disclosure has added new info to `conversation_notes`,
  // the leader's first generated guide + reflection couldn't have seen
  // that context. Re-run both edge calls and surface the fresh artifacts
  // by **posting new tiles** (not silently swapping the old one's HTML)
  // and **auto-opening the coaching panel** so the leader is shown the
  // new doc instead of having to dig for it.
  const regenerateMaterialsWithLatestRecord = useCallback(async () => {
    const id = incidentRef.current.id;
    if (!id) {
      toast.error("Save the incident before regenerating materials.");
      return;
    }
    if (!aiGuideRef.current && !aiReflectionRef.current) {
      toast.info("Generate the reflection and guide first — then you can refresh them.");
      return;
    }
    setIsTyping(true);
    try {
      const cur = incidentRef.current;
      const { guideHtml: aiGuideHtml, reflection: aiReflectionData } =
        await generateMaterials(id, { force: true });
      const fallbackGuide = buildConversationGuide(cur);
      const html = aiGuideHtml ?? fallbackGuide.html;
      pendingGuideHtmlRef.current = html;
      pendingGuideIsAiRef.current = !!aiGuideHtml;
      const reflectionGrade = effectiveGrade(cur.grade_level);

      setIsTyping(false);
      // Tell the leader explicitly what changed and why this fresh pass
      // is different from the original. We surface a short, specific
      // line about the new context the regen consumed so the artifact
      // doesn't feel mysteriously "the same."
      const newContext = extractCriticalContextSnippet(cur);
      await sayBodhi(
        newContext
          ? `Updated the **conversation guide** and **student reflection** using your latest record — including the new note: *${newContext}*. Posting fresh versions and opening the guide now.`
          : "Updated the **conversation guide** and **student reflection** using your latest record — including any plan text and notes you've added since the first pass. Posting fresh versions and opening the guide now.",
      );

      // Append new artifact tiles so the leader can see the regenerated
      // guide and reflection as their own entries in the chat. Leaving
      // the old tiles in place gives a visible audit trail.
      append({
        kind: "out-coaching",
        id: genId(),
        guideHtml: html,
        grade: reflectionGrade,
        isAi: !!aiGuideHtml,
      });
      append({ kind: "out-reflection", id: genId() });

      // Auto-open the coaching side sheet so the leader is *shown* the
      // new content rather than just notified of it. If a panel is
      // already open, swap it to the fresh guide; otherwise pop the
      // sheet open with the new html.
      setOpenPanel({
        kind: "coaching",
        guideHtml: html,
        grade: reflectionGrade,
        isAi: !!aiGuideHtml,
      });

      // Surface what else can be regenerated with the fresh context.
      // Documentation and parent-comm panels read live incident state on
      // open, so just pointing the leader at them is enough — no
      // pre-warm step needed.
      const followUpChips: QuickReply[] = [];
      const hasDocTile = messagesRef.current.some(
        (m) => m.kind === "out-documentation",
      );
      const hasParentTile = messagesRef.current.some(
        (m) => m.kind === "out-parent",
      );
      if (hasDocTile) {
        followUpChips.push({
          label: "Open updated documentation",
          next: stepRef.current,
          payload: "open_doc_after_regen",
          isAction: true,
        });
      }
      if (hasParentTile) {
        followUpChips.push({
          label: "Open updated parent comm",
          next: stepRef.current,
          payload: "open_parent_after_regen",
          isAction: true,
        });
      }
      followUpChips.push({
        label: "Open the new reflection form",
        next: stepRef.current,
        payload: "open_reflection_after_regen",
        isAction: true,
      });
      // Inline `offerQuickReplies` here. Naming it directly would create
      // a temporal-dead-zone reference because its `useCallback` is
      // declared further down the component body (and React evaluates
      // this hook's dependency array eagerly on every render).
      setQuickRepliesDisabled(false);
      append({ kind: "quick-replies", id: genId(), options: followUpChips });

      void aiReflectionData; // ensures the variable is used; state is set inside generateMaterials.
      toast.success("Reflection + guide updated with latest context.");
    } catch (err) {
      console.error("[RestorationRoomChat] regenerate materials:", err);
      toast.error("Couldn't regenerate materials. Try again.");
    } finally {
      setIsTyping(false);
    }
  }, [
    generateMaterials,
    buildConversationGuide,
    sayBodhi,
    append,
  ]);

  // ── Teacher written summary ────────────────────────────────────────────
  // Generates a short, send-ready note the leader can give to the
  // referring teacher. Editable / copyable / downloadable from the
  // teacher-summary side panel. We re-use askMentor for consistency
  // with the conversation guide / parent communication tone, and fall
  // back to a templated summary if the mentor call fails.
  const generateTeacherSummary = useCallback(
    async (opts?: { openPanelAfter?: boolean }) => {
      const cur = incidentRef.current;
      const studentName = cur.student_identifier?.trim() || "the student";
      const teacherName = cur.teacher_name?.trim() || "the teacher";
      const followUpPlan = cur.behavior_follow_up?.trim() || "";
      setTeacherSummaryGenerating(true);
      try {
        const prompt =
          `Write a short (4–7 sentences) **written summary** the school admin can send to **${teacherName}**, the referring teacher, about the behavior incident with **${studentName}**.\n\n` +
          `Tone: collegial, warm, professional. Plain text — no headers, no bullets, no bold.\n\n` +
          `Cover, in order:\n` +
          `1) Briefly acknowledge what happened and that it's been addressed today.\n` +
          `2) What came out of the conversation with ${studentName} (1–2 sentences, no quotes from the student, no new private disclosures).\n` +
          `3) The follow-up plan${followUpPlan ? ` (use what the admin already wrote: "${followUpPlan}")` : ""}.\n` +
          `4) What ${teacherName} can expect or do when ${studentName} returns to class.\n` +
          `5) Close with a brief offer to debrief further if useful.\n\n` +
          `Important: do NOT prescribe specific instructional moves or imply the teacher caused the incident. Stay district-neutral. Do not mention IEP / 504 / BIP details unless they are already in the notes — and if you do, name them generally, not by clause.\n\n` +
          `Return only the summary text — no preamble, no closing tags.`;
        const result = await askMentor(prompt);
        let summaryText = "";
        let isAi = false;
        if (result && "html" in result) {
          summaryText = result.html
            .replace(/<\/p>/gi, "\n\n")
            .replace(/<br\s*\/?>/gi, "\n")
            .replace(/<[^>]+>/g, "")
            .replace(/&nbsp;/g, " ")
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/[ \t]+\n/g, "\n")
            .replace(/\n{3,}/g, "\n\n")
            .trim();
          isAi = !!summaryText;
        }
        if (!summaryText) {
          summaryText =
            `Hi ${teacherName === "the teacher" ? "there" : teacherName.split(" ")[0]},\n\n` +
            `Wanted to give you a quick update on ${studentName} after today's behavior referral. ` +
            `I had a conversation with ${studentName} and we've addressed it for today.\n\n` +
            (followUpPlan
              ? `Here's the follow-up plan we landed on: ${followUpPlan}\n\n`
              : `We have a follow-up plan in place to support ${studentName} going forward.\n\n`) +
            `When ${studentName} returns to class, please treat it as a fresh start. ` +
            `If anything comes up or you want to debrief further, grab me anytime.\n\n` +
            `Thanks for the referral and for staying patient with this.`;
        }
        setTeacherSummary({
          text: summaryText,
          isAi,
          generatedAt: new Date().toISOString(),
        });
        setTeacherSummaryEditing(false);
        // Post the tile so the leader has a visible artifact in chat,
        // not just a panel they have to remember to open.
        const hasTile = messagesRef.current.some(
          (m) => m.kind === "out-teacher-summary",
        );
        if (!hasTile) {
          append({ kind: "out-teacher-summary", id: genId() });
        }
        await sayBodhi(
          `Drafted a short written summary for **${teacherName}** about ${studentName}. ` +
            `You can edit, copy, print, or download it from the panel — review before you send.`,
        );
        if (opts?.openPanelAfter) {
          setOpenPanel({ kind: "teacher-summary" });
        }
        // Always leave the leader with a clear next move so they aren't
        // stranded on teacherFollowUp once the summary is in front of
        // them.
        setQuickRepliesDisabled(false);
        append({
          kind: "quick-replies",
          id: genId(),
          options: [
            {
              label: "Open the teacher summary",
              next: stepRef.current,
              payload: "open_teacher_summary",
              isAction: true,
            },
            {
              label: "↻ Regenerate the summary",
              next: stepRef.current,
              payload: "regen_teacher_summary",
              isAction: true,
            },
            {
              label: "→ Continue to documentation",
              next: "docOffer",
              payload: "continue_after_summary",
              isAction: true,
            },
          ],
        });
        setPlaceholder(
          "Tweak the summary in the panel, or pick a chip above…",
        );
      } catch (err) {
        console.error("[RestorationRoomChat] generateTeacherSummary failed", err);
        toast.error("Couldn't draft the summary. Try regenerating.");
      } finally {
        setTeacherSummaryGenerating(false);
      }
    },
    [askMentor, sayBodhi, append],
  );

  // Removal-decision recommendation — fires on arrival at `askExtended`.
  // Card appears below the chips with Claude's read; admin still picks.
  const generateRemovalDecisionIfNeeded = useCallback(
    async (incidentId: string): Promise<{ posted: boolean }> => {
      if (removalDecisionGenRef.current === incidentId) return { posted: false };
      removalDecisionGenRef.current = incidentId;
      const res = await generate(
        "removal_decision",
        { incident: incidentRef.current },
        session?.access_token,
      );
      if (!res.ok) {
        console.warn(
          "[RestorationRoomChat] removal_decision failed:",
          res.error,
        );
        return { posted: false };
      }
      if (res.content?.kind !== "removal_decision") return { posted: false };
      if (stepRef.current !== "askExtended") return { posted: false };
      append({
        kind: "removal-decision-card",
        id: genId(),
        decision: res.content.data,
      });
      return { posted: true };
    },
    [session?.access_token, append],
  );

  // One-click "Draft with AI" for the ISS panel. Populates work plan,
  // check-in schedule, and return-to-class fields, then drops the re-entry
  // script and any extra supports into `conversation_notes` so they're not
  // lost (those don't have dedicated fields).
  const handleDraftReentryPlan = useCallback(async () => {
    setReentryDrafting(true);
    try {
      const cur = incidentRef.current;
      const res = await generate(
        "reentry_plan",
        { incident: cur },
        session?.access_token,
      );
      if (!res.ok) {
        toast.error(`Couldn't draft the re-entry plan — ${res.error}`);
        return;
      }
      if (res.content?.kind !== "reentry_plan") {
        toast.error("Couldn't draft the re-entry plan — unexpected response.");
        return;
      }
      const plan: ReentryPlanResult = res.content.data;
      const fields: Partial<Incident> = {};

      // Only fill fields that are empty — admin edits take precedence.
      if (plan.work_plan && !cur.iss_work_plan?.trim()) {
        fields.iss_work_plan = plan.work_plan;
      }
      if (plan.checkin_schedule && !cur.reentry_checkin_schedule?.trim()) {
        fields.reentry_checkin_schedule = plan.checkin_schedule;
      }
      if (
        plan.supports?.length &&
        !cur.return_to_class_plan?.trim()
      ) {
        fields.return_to_class_plan = plan.supports.map((s) => `• ${s}`).join("\n");
      }
      // Re-entry script → drop into conversation_notes so it survives in the
      // record. Marked with a header so it reads clearly in the final doc.
      if (plan.reentry_script) {
        const existing = cur.conversation_notes?.trim() ?? "";
        const header = `Re-entry script (AI draft):\n${plan.reentry_script}`;
        fields.conversation_notes = existing ? `${existing}\n\n${header}` : header;
      }
      if (Object.keys(fields).length > 0) {
        onUpdateIncident(fields);
        toast.success("Re-entry plan drafted — review and edit as needed.");
      } else {
        toast.info("Plan drafted. Fields already had content, so nothing was overwritten.");
      }
    } catch (err) {
      console.error("[RestorationRoomChat] reentry plan error:", err);
      toast.error("Something went wrong drafting the re-entry plan.");
    } finally {
      setReentryDrafting(false);
    }
  }, [session?.access_token, onUpdateIncident]);

  // Phase 4a: Polish the admin's raw incident description into a neutral,
  // factual version suitable for the permanent record. Non-destructive —
  // surfaces a preview the admin can accept (overwrite) or discard.
  const handlePolishDescription = useCallback(async (opts?: { silent?: boolean }) => {
    const cur = incidentRef.current;
    if (!cur.incident_description?.trim()) {
      if (!opts?.silent) {
        toast.info("Write the description first — then I can polish it.");
      }
      return;
    }
    setPolishingDesc(true);
    try {
      const res = await generate(
        "incident_polish",
        { incident: cur },
        session?.access_token,
      );
      if (!res.ok) {
        if (!opts?.silent) {
          toast.error(`Couldn't polish the description — ${res.error}`);
        }
        return;
      }
      if (res.content?.kind !== "markdown") {
        if (!opts?.silent) {
          toast.error("Couldn't polish the description — unexpected response.");
        }
        return;
      }
      setDescPolishDraft(res.content.markdown.trim());
    } catch (err) {
      console.error("[RestorationRoomChat] polish error:", err);
      if (!opts?.silent) {
        toast.error("Something went wrong polishing the description.");
      }
    } finally {
      setPolishingDesc(false);
    }
  }, [session?.access_token]);

  // Phase 4c: Draft the narrative section of the final incident record —
  // a dignified, neutral summary a future reader could use to understand
  // what happened and how the school responded.
  const handleDraftNarrative = useCallback(async (opts?: { silent?: boolean }) => {
    const cur = incidentRef.current;
    if (!cur.incident_description?.trim()) {
      if (!opts?.silent) {
        toast.info("Fill in the description first — then I can draft the narrative.");
      }
      return;
    }
    setDraftingNarrative(true);
    try {
      const res = await generate(
        "doc_narrative",
        { incident: cur },
        session?.access_token,
      );
      if (!res.ok) {
        if (!opts?.silent) {
          toast.error(`Couldn't draft the narrative — ${res.error}`);
        }
        return;
      }
      if (res.content?.kind !== "markdown") {
        if (!opts?.silent) {
          toast.error("Couldn't draft the narrative — unexpected response.");
        }
        return;
      }
      setNarrativeDraft(res.content.markdown.trim());
    } catch (err) {
      console.error("[RestorationRoomChat] narrative error:", err);
      if (!opts?.silent) {
        toast.error("Something went wrong drafting the narrative.");
      }
    } finally {
      setDraftingNarrative(false);
    }
  }, [session?.access_token]);

  /** Inserts the AI narrative into the persistent **Notes** field so it stays in the
   * record, the documentation panel, the PDF support section, and future mentor context.
   * Once added, we close the draft block AND remember the accepted text so the
   * auto-redraft loop doesn't immediately resurface the same "Add to case notes" prompt. */
  const handleAddNarrativeToCaseNotes = useCallback(() => {
    const nd = narrativeDraft?.trim();
    if (!nd) return;
    const cur = incidentRef.current.conversation_notes?.trim() ?? "";
    if (cur.includes(nd)) {
      toast.info("This narrative is already in your case notes.");
      setAcceptedNarrative(nd);
      setNarrativeDraft(null);
      return;
    }
    const header = "## Documentation narrative (AI)\n\n";
    const next = cur ? `${header}${nd}\n\n${cur}` : `${header}${nd}`;
    onUpdateIncident({ conversation_notes: next });
    setAcceptedNarrative(nd);
    setNarrativeDraft(null);
    mentorContextSentRef.current = false;
    toast.success("Added to case notes — edit anytime under Notes, or in Edit.");
  }, [narrativeDraft, onUpdateIncident]);

  // Phase 4b: Run the "what am I missing?" check at the current stage.
  // `stage` is a free-text label we hand the model so it can calibrate what
  // to flag (intake, pre-conversation, pre-removal, pre-close, etc.).
  const runMissingChecks = useCallback(
    async (stage: string): Promise<MissingChecksResult | null> => {
      setCheckingMissing(true);
      try {
        const res = await generate(
          "missing_checks",
          { incident: incidentRef.current, extras: { stage } },
          session?.access_token,
        );
        if (!res.ok) {
          toast.error(`Couldn't run the check — ${res.error}`);
          return null;
        }
        if (res.content?.kind !== "missing_checks") {
          toast.error("Couldn't run the check — unexpected response.");
          return null;
        }
        setMissingResult(res.content.data);
        return res.content.data;
      } catch (err) {
        console.error("[RestorationRoomChat] missing-checks error:", err);
        toast.error("Something went wrong running the check.");
        return null;
      } finally {
        setCheckingMissing(false);
      }
    },
    [session?.access_token],
  );

  const docDraftStorageKey = useMemo(
    () => `rr-doc-ai:${session?.user?.id ?? "anonymous"}:${incident.id}`,
    [session?.user?.id, incident.id],
  );

  const docContextSig = useMemo(
    () =>
      JSON.stringify({
        description: incident.incident_description,
        notes: incident.conversation_notes,
        followUp: incident.behavior_follow_up,
        student: incident.student_identifier,
        grade: incident.grade_level,
      }),
    [
      incident.incident_description,
      incident.conversation_notes,
      incident.behavior_follow_up,
      incident.student_identifier,
      incident.grade_level,
    ],
  );

  // Restore persisted documentation drafts when reopening an incident.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(docDraftStorageKey);
      if (!raw) return;
      const saved = JSON.parse(raw) as {
        descPolishDraft?: string | null;
        narrativeDraft?: string | null;
        acceptedPolish?: string | null;
        acceptedNarrative?: string | null;
      };
      if (typeof saved.descPolishDraft === "string") {
        setDescPolishDraft(saved.descPolishDraft);
      }
      if (typeof saved.narrativeDraft === "string") {
        setNarrativeDraft(saved.narrativeDraft);
      }
      if (typeof saved.acceptedPolish === "string") {
        setAcceptedPolish(saved.acceptedPolish);
      }
      if (typeof saved.acceptedNarrative === "string") {
        setAcceptedNarrative(saved.acceptedNarrative);
      }
    } catch (e) {
      console.warn("[RestorationRoomChat] failed to restore doc drafts", e);
    }
  }, [docDraftStorageKey]);

  // Persist documentation drafts so they don't disappear after panel closes.
  useEffect(() => {
    try {
      localStorage.setItem(
        docDraftStorageKey,
        JSON.stringify({
          descPolishDraft,
          narrativeDraft,
          acceptedPolish,
          acceptedNarrative,
        }),
      );
    } catch (e) {
      console.warn("[RestorationRoomChat] failed to persist doc drafts", e);
    }
  }, [
    docDraftStorageKey,
    descPolishDraft,
    narrativeDraft,
    acceptedPolish,
    acceptedNarrative,
  ]);

  // Reset one-time auto-draft guards if incident context materially changes.
  useEffect(() => {
    if (docContextSigRef.current === docContextSig) return;
    docContextSigRef.current = docContextSig;
    docAutoDraftedRef.current = { polish: false, narrative: false };
  }, [docContextSig]);

  // AI-first docs: when documentation panel opens, auto-draft polish +
  // narrative once for current context (with silent fallback behavior).
  // We skip auto-drafting if the leader has already explicitly accepted a
  // version that matches the current state — accepting a polish or adding
  // the narrative to case notes shouldn't immediately resurface the same
  // "Use this version" / "Add to case notes" prompt.
  useEffect(() => {
    if (openPanel?.kind !== "documentation") return;
    if (!session?.access_token) return;
    const descTrim = (incident.incident_description ?? "").trim();
    if (!descTrim) return;
    const polishAlreadyAccepted =
      !!acceptedPolish && acceptedPolish.trim() === descTrim;
    const notesHaveAcceptedNarrative =
      !!acceptedNarrative?.trim() &&
      (incident.conversation_notes ?? "").includes(acceptedNarrative.trim());
    if (
      !descPolishDraft &&
      !polishingDesc &&
      !docAutoDraftedRef.current.polish &&
      !polishAlreadyAccepted
    ) {
      docAutoDraftedRef.current.polish = true;
      void handlePolishDescription({ silent: true });
    }
    if (
      !narrativeDraft &&
      !draftingNarrative &&
      !docAutoDraftedRef.current.narrative &&
      !notesHaveAcceptedNarrative
    ) {
      docAutoDraftedRef.current.narrative = true;
      void handleDraftNarrative({ silent: true });
    }
  }, [
    openPanel,
    session?.access_token,
    incident.incident_description,
    incident.conversation_notes,
    descPolishDraft,
    polishingDesc,
    narrativeDraft,
    draftingNarrative,
    acceptedPolish,
    acceptedNarrative,
    handlePolishDescription,
    handleDraftNarrative,
  ]);

  // Non-blocking background check fired from genOutputs: runs once per
  // incident id, and if gaps are found, drops a card into the chat the
  // admin can react to (fill in or dismiss). Won't post a card if the model
  // says we're ready — no nagging when things are fine.
  const maybeCheckBeforeMaterials = useCallback(
    async (incidentId: string): Promise<{ found: boolean }> => {
      // Guard: only run once per incident. If already run, we report
      // "no new gaps" so on-demand callers can message sensibly.
      if (missingAutoCheckRef.current === incidentId) return { found: false };
      missingAutoCheckRef.current = incidentId;
      try {
        const res = await generate(
          "missing_checks",
          {
            incident: incidentRef.current,
            extras: { stage: "pre-conversation" },
          },
          session?.access_token,
        );
        if (!res.ok || res.content?.kind !== "missing_checks") return { found: false };
        const data = res.content.data;
        if (data.ready && (!data.gaps || data.gaps.length === 0)) return { found: false };
        if (!data.gaps?.length) return { found: false };
        append({
          kind: "missing-checks-card",
          id: genId(),
          ready: data.ready,
          gaps: data.gaps,
          stage: "pre-conversation",
        });
        return { found: true };
      } catch (err) {
        console.warn("[RestorationRoomChat] auto missing-check skipped:", err);
        return { found: false };
      }
    },
    [session?.access_token, append],
  );

  // Post-conversation debrief — fires when admin arrives at `resolution`
  // (right after they describe what happened with the student, or they can
  // skip and get a skeleton debrief). Shows what worked / watch-for /
  // follow-up steps.
  //
  // The optional `isAborted` predicate is checked right before we append
  // the debrief card so a timed-out wrap-up flow can guarantee the
  // debrief does NOT land out of order, scrolling the next question
  // (docOffer) up out of view.
  const generateDebriefIfNeeded = useCallback(
    async (
      incidentId: string,
      outcomeText?: string,
      isAborted?: () => boolean,
    ) => {
      if (debriefGenRef.current === incidentId) return;
      debriefGenRef.current = incidentId;
      const res = await generate(
        "post_convo_debrief",
        { incident: incidentRef.current, extras: { outcome_text: outcomeText } },
        session?.access_token,
      );
      if (isAborted?.()) return;
      if (!res.ok) {
        console.warn("[RestorationRoomChat] debrief failed:", res.error);
        return;
      }
      if (res.content?.kind !== "debrief") return;
      append({
        kind: "debrief-card",
        id: genId(),
        debrief: res.content.data,
      });
    },
    [session?.access_token, append],
  );

  // Runs the debrief generation with a typing indicator + a safety cap so
  // the chat never gets stuck on a slow/unreachable Claude. Used after
  // the leader answers the "extended time out of class?" question — we
  // intentionally hold the debrief until that disposition is captured so
  // the debrief reflects the final outcome, not a half-formed picture.
  //
  // If the safety cap wins the race, we set `aborted = true` so the
  // pending debrief append is dropped — otherwise it would land later
  // and scroll the follow-up "docOffer" question out of view, exactly
  // the bug leaders reported (the question disappears under a long
  // debrief that arrives a few seconds afterward).
  const runDebriefBlocking = useCallback(
    async (outcomeHint?: string) => {
      const id = incidentRef.current.id;
      if (!id) return;
      setIsTyping(true);
      let aborted = false;
      try {
        const debriefPromise = generateDebriefIfNeeded(
          id,
          outcomeHint,
          () => aborted,
        );
        const timeoutPromise = new Promise<void>((resolve) =>
          window.setTimeout(() => {
            aborted = true;
            resolve();
          }, 15000),
        );
        await Promise.race([debriefPromise, timeoutPromise]);
      } catch (err) {
        console.warn("[RestorationRoomChat] debrief fetch failed:", err);
      } finally {
        setIsTyping(false);
      }
    },
    [generateDebriefIfNeeded],
  );

  // ── Attachments ─────────────────────────────────────────────────────────
  // Load any existing attachments already tied to this incident when we boot.
  useEffect(() => {
    let cancelled = false;
    if (!incident.id) {
      setAttachments([]);
      return;
    }
    (async () => {
      try {
        // Pulls own + school-shared rows for this incident in one query.
        // The RLS policy decides what's visible, so the caller only
        // needs to filter by context.
        const tiedToThisIncident = await storageService.getFilesByContext(
          "restoration_incident",
          incident.id,
        );
        if (cancelled) return;
        setAttachments(tiedToThisIncident);
      } catch (err) {
        console.error("Failed to load incident attachments:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [incident.id]);

  const handleViewAttachment = useCallback(async (fileId: string) => {
    try {
      const url = await storageService.getSignedUrl(fileId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Failed to open attachment:", err);
      toast.error("Couldn't open that file. It may have been moved.");
    }
  }, []);

  const handleRemoveAttachment = useCallback(async (fileId: string) => {
    try {
      await storageService.deleteFile(fileId);
      setAttachments((prev) => prev.filter((a) => a.id !== fileId));
      setMessages((prev) =>
        prev.map((m) =>
          m.kind === "attachment" && m.fileId === fileId
            ? { ...m, status: "error" as const, errorMessage: "Removed", fileId: undefined }
            : m,
        ),
      );
      toast.success("File removed");
    } catch (err) {
      console.error("Failed to remove attachment:", err);
      toast.error("Couldn't remove that file.");
    }
  }, []);

  const handleFilePicked = useCallback(
    async (file: File) => {
      if (isReadOnly) return;
      if (!incident.id) {
        toast.error("Save the incident first before attaching files.");
        return;
      }
      // Drafts have no persisted row yet; uploads tied to a draft id
      // would orphan if the leader backs out before promotion.
      if (isUnsavedDraft) {
        toast.info(
          "Add the student name and what happened first — then you can attach files.",
        );
        return;
      }
      const messageId = genId();
      append({
        kind: "attachment",
        id: messageId,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        status: "uploading",
      });
      try {
        const uploaded = await storageService.uploadFile(
          file,
          "restoration",
          "restoration_incident",
          incident.id,
          {},
          // School scope so colleagues at the same school can see this
          // file in the Documents drawer. Per-row toggle in the drawer
          // can flip individual files back to private.
          "school",
        );
        // Append to the end so the drawer's chronological order
        // (oldest → newest) stays correct after an optimistic insert.
        setAttachments((prev) => [...prev, uploaded]);
        setMessages((prev) =>
          prev.map((m) =>
            m.kind === "attachment" && m.id === messageId
              ? { ...m, status: "uploaded" as const, fileId: uploaded.id }
              : m,
          ),
        );
        const planDoc = detectPlanDocSignals(file.name, file.type);
        if (planDoc.isPlanDoc) {
          const existing = incidentRef.current.conversation_notes?.trim() ?? "";
          let headerNote = `Plan document attached: ${file.name} (${planDoc.chipValue})`;
          let extracted: string | null = null;
          try {
            const { extractPlanAttachmentText } = await import(
              "./extractPlanAttachmentText"
            );
            extracted = await extractPlanAttachmentText(file);
          } catch (err) {
            console.warn("[RestorationRoomChat] plan doc text extraction failed:", err);
          }
          if (extracted) {
            headerNote += `\n\nUploaded plan document — extracted text (may be partial OCR):\n${extracted}`;
          }
          const nextNotes = existing ? `${headerNote}\n\n${existing}` : headerNote;
          onUpdateIncident({ iep_504_flag: true, conversation_notes: nextNotes });
          // Next mentor turn must re-send full CONTEXT — notes now carry the
          // extracted plan text so Claude isn't blind to uploads mid-thread.
          mentorContextSentRef.current = false;
          append({
            kind: "data-card",
            id: genId(),
            chips: [{ key: "Plan doc attached", value: `${planDoc.chipValue} (${file.name})` }],
          });
          // Drop the federal-guidance card right after the upload data-card
          // so the IDEA / manifestation / services reminders are visible
          // immediately. We pick the most specific plan label we can derive
          // from the filename — "both" if both IEP and 504 cues appear,
          // otherwise the strongest single signal, falling back to "bip"
          // when the upload only matched the BIP pattern.
          const planForGuidance: "iep" | "504" | "both" | "bip" =
            planDoc.hasIep && planDoc.has504
              ? "both"
              : planDoc.has504
                ? "504"
                : planDoc.hasIep
                  ? "iep"
                  : "bip";
          ensureFederalGuidanceShown(planForGuidance);
          void sayBodhi(
            extracted
              ? `I pulled readable text from **${file.name}** into this incident record. The mentor and generated materials can use it now — you can still paste anything the scan missed.`
              : `Noted the **${planDoc.chipValue}** document. I couldn't read text from this file type automatically — paste key supports/accommodations here if you want them in the record.`,
          );
          // If they're staring at the generic iep504 yes/no but already handed
          // us a BIP/IEP/504 file, move to the refine step instead of making
          // them re-answer from zero.
          if (stepRef.current === "iep504") {
            window.setTimeout(() => void gotoRef.current?.("iep504Refine"), 400);
          }
          // Tier 2 — first materials pass can't have seen this extract yet.
          const sid = incidentRef.current.id;
          const hadMaterialsBeforeExtract =
            !!extracted &&
            !!sid &&
            materialsGenRef.current === sid &&
            (!!aiGuideRef.current || !!aiReflectionRef.current);
          if (hadMaterialsBeforeExtract) {
            append({
              kind: "quick-replies",
              id: genId(),
              options: [
                {
                  label: "↻ Regenerate guide + reflection (use plan text)",
                  next: stepRef.current,
                  payload: "regen_materials_plan",
                  isAction: true,
                },
              ],
            });
          }
        }
        toast.success(`Attached ${file.name}`);
      } catch (err) {
        const message =
          err instanceof StorageServiceError
            ? err.message
            : "Upload failed. Please try again.";
        console.error("Attachment upload failed:", err);
        setMessages((prev) =>
          prev.map((m) =>
            m.kind === "attachment" && m.id === messageId
              ? { ...m, status: "error" as const, errorMessage: message }
              : m,
          ),
        );
        toast.error(message);
      }
    },
    [append, incident.id, isReadOnly, isUnsavedDraft, onUpdateIncident, sayBodhi],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = "";
      files.forEach((file) => {
        void handleFilePicked(file);
      });
    },
    [handleFilePicked],
  );

  const offerQuickReplies = useCallback(
    (options: QuickReply[]) => {
      setQuickRepliesDisabled(false);
      append({ kind: "quick-replies", id: genId(), options });
    },
    [append],
  );

  const setPlaceholder = useCallback((placeholder: string) => {
    setInputPlaceholder(placeholder);
    window.setTimeout(() => inputRef.current?.focus(), 60);
  }, []);

  // ── Flow drivers ────────────────────────────────────────────────────────
  const goto = useCallback(
    async (next: StepId, flowEpoch?: number) => {
      const epoch = flowEpoch ?? flowEpochRef.current;
      const stale = () => isStaleFlow(epoch);
      // Any scripted navigation exits review mode — review only applies
      // when the leader is actively reading / annotating a completed stage.
      reviewStageRef.current = null;
      setStep(next);
      const cur = incidentRef.current;
      const name = cur.student_identifier || "the student";

      if (next === "regulation") {
        // The admin has signaled they need to ground before diving in. Ask
        // the mentor for a genuine, tailored response rather than the canned
        // "that makes sense" line. We frame the prompt so Claude knows the
        // admin hasn't described the incident yet — they're just asking for
        // a moment to land before they start the incident write-up.
        const probe =
          "I'm an administrator about to document a student behavior incident. I just asked for a moment to ground before diving in. I haven't described the incident yet — I just need you to meet me where I am for a moment before I start the write-up. Keep it short (3 sentences max). No bullets. Calm, real, collegial — not clinical.";
        const result = await askMentor(probe);
        if (result && "html" in result) {
          append({ kind: "bodhi", id: genId(), html: result.html });
        } else {
          await sayBodhi(
            "That makes sense. You're holding something real — and the fact that it affected you means you care deeply about this work. Take a breath.",
          );
        }
        await new Promise((r) => window.setTimeout(r, 900));
        await sayBodhi(
          "When you're ready — **tell me what happened.** Who's the student, what grade, and what did they do?",
        );
        setPlaceholder(
          "e.g. Marcus, 7th grade, got into a fight in the hallway after lunch...",
        );
        setStep("situation");
        return;
      }

      if (next === "situation") {
        await sayBodhi(
          "Good. **Tell me what happened** — who's the student, what grade, and what did they do?\n\n*At any point, you can tap the 📎 paperclip to attach a referral, witness statement, photo, or anything else relevant. It'll be saved to the incident record.*",
        );
        setPlaceholder("e.g. Ethan, 2nd grade, hit another student on the playground...");
        return;
      }

      if (next === "askGrade") {
        await sayBodhi(`Got the situation. Just one thing I missed — what grade is ${name} in?`);
        setPlaceholder("Grade (e.g. K, 2nd, 7th, 10th)...");
        return;
      }

      if (next === "iep504") {
        await sayBodhi(
          `Quick check before we go further: **is ${name} on an IEP, 504, or BIP?**\n\nNot here to judge — it just changes what's required of you (and what's fair to ask of ${name}). If you're not sure, "I need to check" is a totally valid answer and we'll keep moving.`,
        );
        offerQuickReplies([
          { label: "Yes — IEP", next: "askContext", payload: "iep" },
          { label: "Yes — 504", next: "askContext", payload: "504" },
          { label: "Yes — BIP", next: "askContext", payload: "bip" },
          { label: "Yes — both IEP + 504", next: "askContext", payload: "both" },
          { label: "No", next: "askContext", payload: "none" },
          { label: "Not sure — I'll check", next: "askContext", payload: "unsure" },
        ]);
        setPlaceholder("Or just type what you know — e.g. 'IEP for emotional disturbance', 'No', 'I'll check'…");
        return;
      }

      if (next === "iep504Refine") {
        await sayBodhi(
          `You already shared that ${name} has a learning/behavior plan context. Anything else I should note here — 504, BIP, or key behavior supports from the plan?\n\n"Just the IEP" is totally fine and we'll keep moving.`,
        );
        offerQuickReplies([
          { label: "Just the IEP", next: "askContext", payload: "just_iep" },
          { label: "Just the BIP", next: "askContext", payload: "just_bip" },
          { label: "Also has a 504", next: "askContext", payload: "also_504" },
          { label: "Also has a BIP", next: "askContext", payload: "also_bip" },
          { label: "I'll add plan goals/supports", next: "askContext", payload: "share_supports" },
          { label: "Not sure yet", next: "askContext", payload: "unsure" },
        ]);
        setPlaceholder(
          `e.g. "BIP includes break card + check-in every 20 min", "IEP only", "still verifying"…`,
        );
        return;
      }

      if (next === "askContext") {
        await sayBodhi(
          `Before I put together materials for ${name} and your conversation prep, I want to make sure they're actually useful.\n\n**What was happening right before this started?** Was there a trigger, a rough morning, a buildup — or did it seem to come out of nowhere?`,
        );
        offerQuickReplies([
          { label: "Came out of nowhere", next: "askPrior", payload: "out_of_nowhere" },
          { label: "Building all day / week", next: "askPrior", payload: "buildup" },
          { label: "Specific trigger — I'll describe", next: "askPrior", payload: "specific" },
          { label: "Skip this — continue", next: "askPrior", payload: "skip", isAction: true },
          { label: "⚡ Just generate the forms now", next: "genOutputs", payload: "skip", isAction: true },
        ]);
        setPlaceholder(`e.g. "${name} had a rough morning at home, then lunchroom got loud..."`);
        return;
      }

      if (next === "askPrior") {
        await sayBodhi(
          `Got it. **What has the teacher already tried with ${name}?** Tap all that apply — teachers usually try lots of things before a referral. You can also type anything I'm missing (counselor notes, past calls home, etc.), then hit **Done — continue** when you've captured it all.`,
        );
        offerQuickReplies([
          { label: "Multiple redirections", next: "askTeacherAsk", payload: "redirections", multi: true },
          { label: "Written / verbal warnings", next: "askTeacherAsk", payload: "warnings", multi: true },
          { label: "Breaks / cool-down space", next: "askTeacherAsk", payload: "breaks", multi: true },
          { label: "Seat change", next: "askTeacherAsk", payload: "seat_change", multi: true },
          { label: "Proximity / check-ins", next: "askTeacherAsk", payload: "proximity", multi: true },
          { label: "Behavior chart / tracking", next: "askTeacherAsk", payload: "chart", multi: true },
          { label: "Peer / partner pairing", next: "askTeacherAsk", payload: "partner", multi: true },
          { label: "Parent contact", next: "askTeacherAsk", payload: "parent", multi: true },
          { label: "Counselor / support team", next: "askTeacherAsk", payload: "counselor", multi: true },
          { label: "Prior admin referral", next: "askTeacherAsk", payload: "admin", multi: true },
          { label: "Nothing yet — first time", next: "askTeacherAsk", payload: "none" },
          { label: "Done — continue →", next: "askTeacherAsk", payload: "continue", isAction: true },
          { label: "Skip — continue", next: "askTeacherAsk", payload: "skip", isAction: true },
          { label: "⚡ Just generate the forms now", next: "genOutputs", payload: "skip_all", isAction: true },
        ]);
        setPlaceholder("Or type anything else that's been tried — e.g. 'check-in/check-out system since October, counselor meets weekly...'");
        return;
      }

      if (next === "askTeacherAsk") {
        await sayBodhi(
          `Last context question. **What is the teacher specifically asking for from this referral?**\n\nPick all that apply — a reset and back to class, formal documentation, an admin conversation, counseling, parent contact. The more you check, the better I can shape what comes next.`,
        );
        offerQuickReplies([
          { label: "Reset & back to class", next: "askTeacherAsk", payload: "reset", multi: true },
          { label: "Formal documentation", next: "askTeacherAsk", payload: "formal_doc", multi: true },
          { label: "Admin conversation", next: "askTeacherAsk", payload: "admin_convo", multi: true },
          { label: "Counseling referral", next: "askTeacherAsk", payload: "counseling", multi: true },
          { label: "Parent contact requested", next: "askTeacherAsk", payload: "parent_loop", multi: true },
          { label: "Done — continue →", next: "understand", payload: "continue", isAction: true },
          { label: "Not sure — continue", next: "understand", payload: "unsure", isAction: true },
        ]);
        setPlaceholder("Or type one ask per line — e.g. 'Formal documentation. Parent meeting this week.'");
        return;
      }

      if (next === "understand") {
        await sayBodhi(
          `Before you decide anything — **what do you think was actually going on for ${name}?**\n\nNot just what they did. What were they trying to get, avoid, or protect in that moment? Your honest read. Pick everything that fits — more than one is okay.`,
        );
        // The motivation card used to fire automatically here, which meant
        // Claude's read could land mid-thought while the admin was still
        // answering. It's now opt-in via the "💡 Want my read?" chip below so
        // the admin stays in the driver's seat.
        offerQuickReplies([
          { label: "Seeking connection / attention", next: "understand", payload: "seeking_connection", multi: true },
          { label: "Escape or avoid something hard", next: "understand", payload: "escape", multi: true },
          { label: "Needs sensory regulation", next: "understand", payload: "sensory", multi: true },
          { label: "Big emotions they can't name", next: "understand", payload: "emotional", multi: true },
          { label: "Power / autonomy struggle", next: "understand", payload: "power", multi: true },
          { label: "Peer conflict / social pressure", next: "understand", payload: "peer", multi: true },
          { label: "💡 Want my read?", next: "understand", payload: "ai_read", isAction: true },
          { label: "Done — continue →", next: "genOutputs", payload: "continue", isAction: true },
          { label: "Not sure — generate the materials", next: "genOutputs", payload: "unsure", isAction: true },
        ]);
        setPlaceholder(`Or type your read — what do you think was driving ${name}'s behavior...`);
        return;
      }

      if (next === "genOutputs") {
        const grade = cur.grade_level;

        // Phase A — draft + reveal the student-side materials (reflection
        // form + due-process card), then PAUSE. We used to cascade reflection
        // → due process → conversation guide → "how did it go?" in rapid fire,
        // which felt like the bot was pushing information instead of walking
        // the admin through it. Now we stop here and let the admin tap when
        // they're ready for the conversation guide.
        await sayBodhi(
          `Let me draft materials tailored to ${name} — what happened, what's been tried, and what you're asking for. One moment…`,
        );
        if (stale()) return;
        setIsTyping(true);
        const { guideHtml: aiGuideHtml, reflection: aiReflectionData } = cur.id
          ? await generateMaterials(cur.id)
          : { guideHtml: null, reflection: null };
        setIsTyping(false);
        if (stale()) return;

        // Stash the drafted guide so phase B can render it without re-calling
        // Claude. `aiGuide` state is already set inside generateMaterials;
        // we capture the html here for the fallback branch.
        const fallbackGuide = buildConversationGuide(cur);
        pendingGuideHtmlRef.current = aiGuideHtml ?? fallbackGuide.html;
        pendingGuideIsAiRef.current = !!aiGuideHtml;
        const reflectionDecision = assessReflectionNeed(cur);
        pendingReflectionDeferReasonRef.current = reflectionDecision.recommend
          ? null
          : reflectionDecision.reason;

        if (!reflectionDecision.recommend) {
          await sayBodhi(
            `Based on what you've shared, a written student reflection is optional right now — ${reflectionDecision.reason}.`,
          );
          if (stale()) return;
          offerQuickReplies([
            { label: "Generate reflection now", next: "genOutputs", payload: "reflection_now", isAction: true },
            { label: "Skip reflection for now", next: "genOutputs", payload: "reflection_skip", isAction: true },
            { label: "Show me the conversation guide", next: "genOutputs", payload: "show_guide" },
            { label: "✨ What am I missing?", next: "genOutputs", payload: "check_missing" },
          ]);
          setPlaceholder("Choose whether to do reflection now, or continue to guide prep…");
          return;
        }

        // Reflection is recommended. If we picked up a soft note (e.g. acute
        // life context surfaced during intake), surface it alongside so the
        // leader can choose to start verbal-first if they want.
        if (reflectionDecision.note) {
          await sayBodhi(`Heads up: ${reflectionDecision.note}`);
          if (stale()) return;
        }

        await sayBodhi(
          aiReflectionData
            ? `Here's the student reflection form for ${name} — I wove in what you told me about this incident.${
                grade === "K-2"
                  ? " Because they're young, read these questions aloud to them."
                  : ""
              }`
            : `Here's the student reflection form for ${name}.${
                grade === "K-2"
                  ? " Because they're young, read these questions aloud to them — not something they fill in alone."
                  : " They can complete this while you're preparing for the conversation."
              }`,
        );
        if (stale()) return;
        append({ kind: "out-reflection", id: genId() });

        await new Promise((r) => window.setTimeout(r, 500));
        if (stale()) return;
        append({ kind: "out-due-process", id: genId() });

        await new Promise((r) => window.setTimeout(r, 500));
        if (stale()) return;
        await sayBodhi(
          `Both of these are for the student's side — the reflection form for them, the due-process check for the record. **Take your time reviewing them.**\n\nWhen you're ready, I'll bring up the conversation guide for *you*.`,
        );
        if (stale()) return;
        offerQuickReplies([
          { label: "Show me the conversation guide", next: "genOutputs", payload: "show_guide" },
          { label: "✨ What am I missing?", next: "genOutputs", payload: "check_missing" },
          {
            label: "💬 AI Advice first",
            next: "genOutputs",
            askMentor: true,
            payload:
              "Before I go into the conversation, help me think through what's most important to say and how to frame it for this student.",
          },
        ]);
        setPlaceholder("Or ask me anything about the reflection or due-process step…");
        return;
      }

      if (next === "resolution") {
        await sayBodhi(
          `What was the outcome — and what's your follow-up plan for ${name}?\n\nTell me in your own words. I'll pull what I need for the documentation.\n\n*Stuck or something the student said is throwing you? Type it here — I'll coach you. Or tap 💬 AI Advice.*`,
        );
        offerQuickReplies([
          {
            label: "💬 AI Advice",
            next: "resolution",
            askMentor: true,
            payload:
              "I need coaching on what just happened with the student or what to do next. Help me think through my move.",
          },
          { label: "Skip outcome — continue", next: "askExtended" },
        ]);
        setPlaceholder(
          "e.g. Conference with student, counselor follow-up Thursday, called parent this afternoon...",
        );
        return;
      }

      if (next === "askExtended") {
        await sayBodhi(
          `One more important question — **did this require extended time out of class?**\n\nOffice redirect (brief, same day)? ISS or formal removal? Or did ${name} stay in class?\n\nThis matters for documentation and, if applicable, gets us into ISS/re-entry planning.`,
        );
        // Removal-decision card is now opt-in via the "💡 What do you think?"
        // chip below. The old auto-fire could land a recommendation mid-
        // thought while the admin was still weighing their own call.
        offerQuickReplies([
          { label: "Stayed in class", next: "teacherFollowUp", payload: "none" },
          { label: "Brief office redirect", next: "teacherFollowUp", payload: "administrative_redirect" },
          { label: "ISS / formal removal — needs plan", next: "issDocs", payload: "formal_removal" },
          { label: "Sent home / half-day pickup", next: "issDocs", payload: "formal_removal" },
          { label: "💡 What do you think?", next: "askExtended", payload: "ai_read" },
          {
            label: "💬 AI Advice",
            next: "askExtended",
            askMentor: true,
            payload:
              "Help me decide whether this warrants a formal removal vs. brief redirect vs. staying in class, given the incident and what's been tried.",
          },
        ]);
        setPlaceholder("Or describe it — e.g. 'Half day in ISS, back tomorrow with re-entry meeting'...");
        return;
      }

      if (next === "teacherFollowUp") {
        const teacher = cur.teacher_name?.trim() || "the referring teacher";
        // If the leader already typed a follow-up plan at `resolution`,
        // don't ask them to start over — surface the plan they wrote and
        // ask only what still needs to happen for the teacher. Avoids
        // the "asking for a follow-up plan after I gave one" loop.
        const existingPlanRaw = cur.behavior_follow_up?.trim() ?? "";
        const existingPlan =
          existingPlanRaw.length > 240
            ? `${existingPlanRaw.slice(0, 237)}…`
            : existingPlanRaw;
        if (existingPlan) {
          await sayBodhi(
            `You already wrote the follow-up plan for ${name}:\n\n> ${existingPlan}\n\n` +
              `For **${teacher}** — anything else they need from you on top of that? ` +
              `If the plan above already loops them in, pick **"Plan already covers it"** and I'll log that. ` +
              `Or pick a chip below for the next move.`,
          );
        } else {
          await sayBodhi(
            `Before we wrap, what about **${teacher}** — the teacher who referred this? Most behavior incidents leave the teacher needing a quick reconnect too: what happened in the conversation, what to expect when ${name} comes back, anything they can do differently next time.\n\nWhat do they need from you?`,
          );
        }
        const teacherFollowUpChips: QuickReply[] = [
          ...(existingPlan
            ? ([
                {
                  label: "Plan already covers it",
                  next: "docOffer",
                  payload: "already_done",
                  isAction: true,
                },
              ] as QuickReply[])
            : ([
                { label: "Already looped them in", next: "docOffer", payload: "already_done" },
              ] as QuickReply[])),
          { label: "Send a written summary", next: "teacherFollowUp", payload: "written_summary" },
          { label: "Quick chat / pop-in today", next: "docOffer", payload: "quick_chat" },
          { label: "Schedule a longer meeting", next: "docOffer", payload: "scheduled_meeting" },
          { label: "I'll handle after I close this", next: "docOffer", payload: "later", isAction: true },
          { label: "Skip — not needed", next: "docOffer", payload: "skip", isAction: true },
        ];
        offerQuickReplies(teacherFollowUpChips);
        setPlaceholder(
          existingPlan
            ? `Or add what's still needed for ${teacher} on top of the plan above…`
            : `Or describe — e.g. "Pop in 5th period to debrief, send written summary by end of day"…`,
        );
        return;
      }

      if (next === "handoff") {
        await sayBodhi(
          `**Saving this for handoff.** Anything specific the next person should know — what's done, what's still open, who's owning what?\n\nTotally fine to skip if everything's in the record already.`,
        );
        offerQuickReplies([
          { label: "Skip — record is enough", next: "handoff", payload: "skip", isAction: true },
        ]);
        setPlaceholder(`e.g. "Conversation done, parent call still pending. Tag me when you're back."`);
        return;
      }

      if (next === "issDocs") {
        if (!cur.removal_type) {
          onUpdateIncident({ removal_type: "formal_removal" });
        }
        await sayBodhi(
          `Because ${name} spent extended time out of class, we need to document the ISS work plan and re-entry plan.${
            cur.iep_504_flag
              ? `\n\n**Reminder:** ${name} has an IEP/504. Federal law requires additional documentation for removals — keep your special education coordinator in the loop.`
              : ""
          }\n\nFill in what applies below. You can keep talking in the chat too — anything you type becomes part of the notes.`,
        );
        append({ kind: "out-iss-docs", id: genId() });
        setPlaceholder("Any additional context about the removal or re-entry...");
        return;
      }

      if (next === "docOffer") {
        await sayBodhi(
          `Would you like me to pull together the documentation summary and a parent communication draft?`,
        );
        offerQuickReplies([
          { label: "Yes — generate it", next: "genDoc" },
          { label: "I'm done for now", next: "finish" },
        ]);
        setPlaceholder("Or type anything else you want in the record...");
        return;
      }

      if (next === "genDoc") {
        await sayBodhi(
          `Here's your **incident documentation** tile and **parent communication** draft below.\n\nThe documentation side panel can open automatically — at the top, use **Polish** for a neutral, file-ready description and **Narrative** for the formal record (both use your full case notes, including any text pulled from uploaded BIP/IEP/504 files). Then review parent outreach on the next tile.`,
        );
        append({ kind: "out-documentation", id: genId() });
        // Tier 2 — surface AI record tools immediately; leaders were missing
        // polish/narrative because polish lived behind "Edit" only.
        window.setTimeout(() => setOpenPanel({ kind: "documentation" }), 450);
        await new Promise((r) => window.setTimeout(r, 400));
        append({ kind: "out-parent", id: genId() });
        await new Promise((r) => window.setTimeout(r, 700));
        setStep("finish");
        goto("finish");
        return;
      }

      if (next === "finish") {
        append({ kind: "out-complete", id: genId(), studentLabel: name });
        return;
      }
    },
    [
      sayBodhi,
      setPlaceholder,
      append,
      buildConversationGuide,
      offerQuickReplies,
      onUpdateIncident,
      generateMaterials,
      isStaleFlow,
      setOpenPanel,
    ],
  );

  useEffect(() => {
    gotoRef.current = goto;
  }, [goto]);

  // ── Expose jumpToStage to parent ────────────────────────────────────────
  // The toolkit is explicitly sequential: leaders can revisit any stage
  // they've completed, but cannot skip forward into a stage whose
  // prerequisites aren't done. This guard mirrors the StageTracker UI
  // so parent-side calls (header pills) are also enforced here in case
  // a future code path bypasses the visual lock.
  useImperativeHandle(
    ref,
    () => ({
      jumpToStage: (stage: ChatTabStage) => {
        if (isReadOnly) return;
        const label: Record<ChatTabStage, string> = {
          incident: "The Situation",
          reflection: "Understand",
          conversation: "The Conversation",
          resolution: "Resolution",
          extended: "Extended Support",
        };
        const inc = incidentRef.current;
        const stages = [
          ...STAGE_META,
          ...(showsExtendedStage(inc) ? [EXTENDED_STAGE] : []),
        ];
        const completion = getStageCompletion(inc);
        const activeStage = stageForStep(stepRef.current);
        if (!isStageReachable(stage, activeStage, completion, stages)) {
          // Surface a small toast so the click isn't silently swallowed.
          toast.info(`Finish ${label[activeStage]} first.`, {
            description: `You can come back to ${label[stage]} once the prior steps are done.`,
          });
          return;
        }
        void (async () => {
          await sayBodhi(`Jumping back to **${label[stage]}** — you can revise anything here.`);
          const target = anchorStepForStage(stage, inc);
          goto(target);
        })();
      },
      // Exposed so the persistent Documents drawer can drive uploads/views/
      // removals through the same code path the chat itself uses. Keeps a
      // single source of truth for attachment state and chat side-effects.
      uploadFile: (file: File) => handleFilePicked(file),
      viewAttachment: (fileId: string) => {
        void handleViewAttachment(fileId);
      },
      removeAttachment: (fileId: string) => handleRemoveAttachment(fileId),
      // Opens a generated artifact's panel (or triggers a download) on
      // behalf of the Documents drawer. Falls back gracefully when the
      // artifact isn't ready yet so the drawer doesn't have to duplicate
      // availability logic.
      openArtifact: (kind: GeneratedArtifactKind) => {
        const inc = incidentRef.current;
        switch (kind) {
          case "incident-packet":
            try {
              downloadIncidentPacketPDF(inc);
            } catch (err) {
              console.error("Failed to download incident packet:", err);
              toast.error("Couldn't generate the incident packet.");
            }
            return;
          case "parent-comm":
            setOpenPanel({ kind: "parent" });
            return;
          case "student-reflection":
            setOpenPanel({ kind: "reflection" });
            return;
          case "iss-tasks":
            setOpenPanel({ kind: "iss-docs" });
            return;
          case "case-record":
            setOpenPanel({ kind: "documentation" });
            return;
          case "conversation-guide": {
            // The coaching guide is generated as part of the chat flow and
            // stored on the message. Look up the most recent one rather
            // than re-running the LLM here.
            const coaching = [...messages]
              .reverse()
              .find((m) => m.kind === "out-coaching");
            if (coaching && coaching.kind === "out-coaching") {
              setOpenPanel({
                kind: "coaching",
                guideHtml: coaching.guideHtml,
                grade: coaching.grade,
              });
            } else {
              toast.info(
                "The conversation guide hasn't been generated yet — finish Step 2 first.",
              );
            }
            return;
          }
        }
      },
    }),
    [
      goto,
      sayBodhi,
      isReadOnly,
      handleFilePicked,
      handleViewAttachment,
      handleRemoveAttachment,
      downloadIncidentPacketPDF,
      messages,
    ],
  );

  // Mirror attachments up to the parent (RestorationRoomPage) so the
  // documents drawer sees the same list without re-querying storage.
  useEffect(() => {
    onAttachmentsChange?.(attachments);
  }, [attachments, onAttachmentsChange]);

  // ── Boot / reset whenever the active incident changes ──────────────────
  // Every new-incident click (or switching to a different incident in the
  // list) changes incident.id. We fully reset the chat so it never appends
  // onto the previous session's messages. The bootstrappedForIdRef guards
  // against React StrictMode's synthetic double-invoke in development.
  useEffect(() => {
    if (bootstrappedForIdRef.current === incident.id) return;
    bootstrappedForIdRef.current = incident.id;

    setMessages([]);
    setStep("welcome");
    stepRef.current = "welcome";
    setInput("");
    setIsTyping(false);
    setQuickRepliesDisabled(false);
    setAttachments([]);
    setInputPlaceholder("Share what's on your mind...");
    mentorHistoryRef.current = [];
    mentorContextSentRef.current = false;
    setDocEditing(false);
    setReflectionEditing(false);
    setReflectionOverrides(null);

    // Pin chat to the top until the user actually engages, so a freshly
    // loaded incident shows the first message (welcome / handoff /
    // welcome-back) at the top of the chat panel — not pushed down by
    // the bottom-pinning effect once the welcome + chips render.
    pinToTopRef.current = true;

    // Also reset any scrollable ancestors (e.g. PageContent) and the
    // window so any previous view's scroll offset doesn't carry over.
    // Run on the next frame so layout has settled after the reset above.
    requestAnimationFrame(() => {
      const el = chatRef.current;
      if (!el) return;
      el.scrollTop = 0;
      let parent: HTMLElement | null = el.parentElement;
      while (parent && parent !== document.body) {
        const overflowY = window.getComputedStyle(parent).overflowY;
        if (
          (overflowY === "auto" || overflowY === "scroll") &&
          parent.scrollHeight > parent.clientHeight
        ) {
          parent.scrollTop = 0;
        }
        parent = parent.parentElement;
      }
      window.scrollTo({ top: 0, behavior: "auto" });
    });

    if (isReadOnly) {
      void (async () => {
        await sayBodhi(
          `This is a read-only view of a colleague's incident. Here's the documentation and parent communication tools.`,
        );
        append({ kind: "out-documentation", id: genId() });
        await new Promise((r) => window.setTimeout(r, 400));
        append({ kind: "out-parent", id: genId() });
        setStep("finish");
      })();
      return;
    }

    // Returning to an existing incident — resume instead of re-asking. The
    // "What we have so far" summary card above the chat is the primary
    // grounding now, so the welcome-back message is intentionally short
    // and just offers the next move.
    if (incidentHasData(incident)) {
      const resume = resumeStepFor(incident);
      const label = progressLabelFor(incident);
      const name = incident.student_identifier || "this student";
      // If a previous user paused this incident with a handoff note,
      // surface that first so the new user sees what's pending before
      // anything else.
      const handoffMatches = (incident.conversation_notes || "").match(
        /^Handoff note[^\n]*$/gim,
      );
      const lastHandoffLine =
        handoffMatches && handoffMatches.length > 0
          ? handoffMatches[handoffMatches.length - 1]
          : null;
      const wasPaused = incident.status === "paused";
      void (async () => {
        if (lastHandoffLine && wasPaused) {
          // Render the raw line — it already includes the author + timestamp
          // we wrote in the handoff handler, so it reads naturally as a
          // quoted note inside Bodhi's bubble.
          await sayBodhi(
            `**Picking up a paused incident.** Last person left this note:\n\n> ${lastHandoffLine}\n\nFlipping the status back to active so you can continue.`,
          );
          // Move the incident off "paused" so dashboard counts and the
          // chat's own gating reflect that someone is actively working it
          // again. The handoff note remains in conversation_notes.
          onUpdateIncident({ status: "followup" });
        }
        await sayBodhi(
          `**Welcome back to ${name}'s incident.** Where we left off: *${label}.*\n\nThe **What we have so far** panel up top has everything that's already on the record — you can also revisit any earlier step from there or from the tabs.\n\nWhat do you want to do?`,
        );
        const options: QuickReply[] = [
          { label: `→ Continue from where I left off`, next: resume, isAction: true },
          { label: "⚡ Generate / review the forms", next: "genOutputs" },
          { label: "Log outcome of the conversation", next: "resolution" },
          { label: "Start over from scratch", next: "situation" },
        ];
        offerQuickReplies(options);
        setPlaceholder("Or type to add detail or ask me where you are…");
      })();
      return;
    }

    // Brand new incident — fresh welcome.
    // Opening is incident-focused: names the tool, the four steps, and what
    // the leader walks out with. A small "ground first" off-ramp stays for
    // leaders who genuinely need a moment — but it isn't the lead framing.
    void (async () => {
      await sayBodhi(
        "You're in the **Behavior Incident Toolkit**.\n\nTell me what happened and I'll walk you through it in four steps — **the situation → what's underneath → the conversation → the resolution**. You'll leave with the incident documentation, a conversation plan for the student, and clear next steps.\n\nWhat do you want to do first?",
      );
      offerQuickReplies([
        { label: "Start — what happened?", next: "situation" },
        { label: "I need to ground first", next: "regulation" },
      ]);
      setPlaceholder("Or just start typing what happened...");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident.id, isReadOnly]);

  // ── Quick reply handler ─────────────────────────────────────────────────
  const handleQuickReply = async (option: QuickReply, messageId?: string) => {
    if (quickRepliesDisabled) return;
    // First user action releases the boot-time top pin so subsequent
    // turns scroll to the bottom as a chat normally does.
    pinToTopRef.current = false;
    const turnEpoch = beginFlowEpoch();

    const curStep = stepRef.current;

    // Review-mode "Back to X" — exit review without re-running the
    // previous step's scripted prompt. Just set the step quietly so any
    // subsequent typing routes through the right text-handler again.
    if (option.payload === "review_back") {
      sayUser(option.label);
      reviewStageRef.current = null;
      setStep(option.next);
      await sayBodhi(`Back where you were. Pick up wherever you'd like.`);
      setQuickRepliesDisabled(false);
      return;
    }

    // Explicit "AI Advice" chip — user is requesting coaching without typing.
    // After the advice arrives we re-engage the leader with a soft loop-back
    // (one short follow-up question + two chips) so they're never stranded
    // staring at coaching text with no clear next move. They can always type
    // a free-form follow-up too.
    if (option.askMentor) {
      sayUser(option.label);
      const canned =
        option.payload ||
        `I need coaching right now. I'm on the "${curStep}" step of the Behavior Incident Toolkit. Help me think through what to say / do next for this student.`;
      const result = await askMentor(canned);
      const adviceWorked = !!(result && "html" in result);
      if (adviceWorked) {
        append({ kind: "bodhi", id: genId(), html: (result as { html: string }).html });
      } else {
        const detail = result && "error" in result ? result.error : "";
        await sayBodhi(
          `I couldn't reach the mentor just now${
            detail ? ` (${detail})` : ""
          }. Try again, or type your question.`,
        );
      }

      // Loop-back chips so the leader stays in the flow. First chip lets
      // them dig deeper into the same advice; second chip moves them
      // forward (to the chip's `next` step if it pointed forward, or just
      // re-anchors the current step otherwise).
      const followUps: QuickReply[] = [];
      if (adviceWorked) {
        followUps.push({
          label: "💬 Tell me more",
          next: curStep,
          askMentor: true,
          payload:
            "Go deeper on that — what should I be paying special attention to with this student given everything I've shared so far?",
        });
      } else {
        followUps.push({
          label: "🔄 Try AI Advice again",
          next: curStep,
          askMentor: true,
          payload: canned,
        });
      }
      if (option.next && option.next !== curStep) {
        followUps.push({
          label: "→ Continue from here",
          next: option.next,
          isAction: true,
        });
      } else {
        followUps.push({
          label: "→ Keep going",
          next: curStep,
          isAction: true,
        });
      }
      if (adviceWorked) {
        await sayBodhi(
          "Want to dig into anything else, or ready to keep going?",
        );
      }
      offerQuickReplies(followUps);
      setPlaceholder("Ask a follow-up, type more context, or pick a chip…");
      return;
    }

    // Tier 2 — regenerate reflection + conversation guide after a plan PDF
    // added extracted text to notes *after* the first materials pass.
    if (option.payload === "regen_materials_plan" || option.payload === "regen_materials_context") {
      sayUser(option.label);
      setQuickRepliesDisabled(true);
      try {
        await regenerateMaterialsWithLatestRecord();
        // regen now offers its own follow-up chips (open updated doc /
        // parent comm / reflection), so we don't re-offer the generic
        // step-resume chips on top.
      } finally {
        setQuickRepliesDisabled(false);
      }
      return;
    }

    // Follow-up chips after a regen — open the relevant panel AND keep
    // the chat moving by re-posting next-move chips. Without the fresh
    // chip set, the user is left staring at the chat with the old chips
    // already consumed and no obvious path forward (the dead-end the
    // user reported when they clicked "Open the new reflection form").
    const offerPostRegenNextSteps = (kind: "documentation" | "parent" | "reflection") => {
      const label =
        kind === "documentation"
          ? "documentation"
          : kind === "parent"
            ? "parent communication"
            : "reflection form";
      void sayBodhi(
        `Opened the updated **${label}** — review it on the right. When you're ready, pick the next move below.`,
      );
      const nextChips: QuickReply[] = [
        { label: "→ I had the conversation, log it", next: "postConversation", isAction: true },
        { label: "Show me the conversation guide", next: "genOutputs", payload: "show_guide" },
        {
          label: "💬 AI Advice",
          next: stepRef.current,
          askMentor: true,
          payload:
            "Walk me through what to focus on now that I have the updated guide / reflection / documentation in front of me.",
        },
      ];
      setQuickRepliesDisabled(false);
      append({ kind: "quick-replies", id: genId(), options: nextChips });
      setPlaceholder("Pick a chip above, or type what you need next…");
    };
    if (option.payload === "open_doc_after_regen") {
      sayUser(option.label);
      setOpenPanel({ kind: "documentation" });
      offerPostRegenNextSteps("documentation");
      return;
    }
    if (option.payload === "open_parent_after_regen") {
      sayUser(option.label);
      setOpenPanel({ kind: "parent" });
      offerPostRegenNextSteps("parent");
      return;
    }
    if (option.payload === "open_reflection_after_regen") {
      sayUser(option.label);
      setOpenPanel({ kind: "reflection" });
      offerPostRegenNextSteps("reflection");
      return;
    }

    // Teacher written-summary follow-up chips. Same shape as the
    // post-regen flow — open the panel and re-offer next moves so the
    // leader is never stranded.
    if (option.payload === "open_teacher_summary") {
      sayUser(option.label);
      setOpenPanel({ kind: "teacher-summary" });
      setQuickRepliesDisabled(false);
      append({
        kind: "quick-replies",
        id: genId(),
        options: [
          {
            label: "↻ Regenerate the summary",
            next: stepRef.current,
            payload: "regen_teacher_summary",
            isAction: true,
          },
          {
            label: "→ Continue to documentation",
            next: "docOffer",
            payload: "continue_after_summary",
            isAction: true,
          },
        ],
      });
      setPlaceholder("Edit in the panel, or pick a chip above…");
      return;
    }
    if (option.payload === "regen_teacher_summary") {
      sayUser(option.label);
      setQuickRepliesDisabled(true);
      void generateTeacherSummary({ openPanelAfter: true });
      return;
    }
    if (option.payload === "continue_after_summary") {
      sayUser(option.label);
      const existing = incidentRef.current.conversation_notes?.trim() ?? "";
      const noteLine = `Teacher follow-up: Written summary drafted in chat (see record).`;
      onUpdateIncident({
        conversation_notes: existing && !existing.includes("Written summary drafted")
          ? `${existing}\n\n${noteLine}`
          : existing || noteLine,
      });
      await runDebriefBlocking(
        incidentRef.current.behavior_follow_up || "Written summary drafted for teacher.",
      );
      window.setTimeout(() => goto("docOffer"), 300);
      return;
    }

    // Multi-select chip — toggle selection without advancing. Click again to
    // uncheck. User finishes by clicking the explicit "Done — continue" chip
    // or by sending free text. We only update the underlying field and the
    // chip's own `selected` array (so the chip flips color and prepends a
    // ✓), matching the existing askPrior pattern — no extra user-bubble
    // echo.
    if (option.multi) {
      const reflectChipToggle = (isOn: boolean) => {
        if (!messageId) return;
        setMessages((msgs) =>
          msgs.map((m) => {
            if (m.kind !== "quick-replies" || m.id !== messageId) return m;
            const prev = m.selected ?? [];
            const selected = isOn
              ? prev.filter((p) => p !== option.payload)
              : [...prev, option.payload as string];
            return { ...m, selected };
          }),
        );
      };

      if (curStep === "askPrior" && option.payload) {
        const labelMap: Record<string, string> = {
          redirections: "Multiple teacher redirections",
          warnings: "Written / verbal warnings on file",
          breaks: "Breaks / cool-down space offered",
          seat_change: "Seat change",
          proximity: "Proximity / check-ins from teacher",
          chart: "Behavior chart / daily tracking sheet",
          partner: "Peer / partner pairing or mediation",
          parent: "Prior parent contact",
          counselor: "Counselor / support-team referral",
          admin: "Prior admin referral / conversation",
          accommodations: "IEP / 504 accommodations already in place",
        };
        const note = labelMap[option.payload];
        if (note) {
          const existing = incidentRef.current.prior_interventions_checked ?? [];
          const isOn = existing.includes(note);
          const nextList = isOn
            ? existing.filter((x) => x !== note)
            : [...existing, note];
          onUpdateIncident({ prior_interventions_checked: nextList });
          reflectChipToggle(isOn);
        }
        return;
      }

      // Teacher's ask — toggles persist into `teacher_outcome_checked` so
      // the leader can pick more than one (e.g. formal documentation +
      // parent contact) and have all of them carry through to the AI's
      // downstream materials.
      if (curStep === "askTeacherAsk" && option.payload) {
        const labelMap: Record<string, string> = {
          reset: "Reset and return to class",
          formal_doc: "Formal documentation of the incident",
          admin_convo: "Conversation with admin / school leader",
          counseling: "Counseling or support-team referral",
          parent_loop: "Parent contact / loop-in",
        };
        const note = labelMap[option.payload];
        if (note) {
          const existing = incidentRef.current.teacher_outcome_checked ?? [];
          const isOn = existing.includes(note);
          const nextList = isOn
            ? existing.filter((x) => x !== note)
            : [...existing, note];
          onUpdateIncident({ teacher_outcome_checked: nextList });
          reflectChipToggle(isOn);
        }
        return;
      }

      // "What was actually going on" — toggles append/remove a tagged
      // `Motivation:` line in conversation_notes so the leader can pick
      // multiple working hypotheses. We don't have a dedicated array
      // column for this, so we keep the tagged-line convention the rest
      // of the toolkit already uses (renderConversationNotesHtml,
      // motivation extraction in the AI pipeline).
      if (curStep === "understand" && option.payload) {
        const labelMap: Record<string, string> = {
          seeking_connection: "Seeking connection or attention",
          escape: "Trying to escape or avoid something hard",
          sensory: "Needs sensory regulation",
          emotional: "Big emotions they couldn't name",
          power: "Power / autonomy struggle",
          peer: "Peer conflict / social pressure",
        };
        const note = labelMap[option.payload];
        if (note) {
          const existing = incidentRef.current.conversation_notes?.trim() ?? "";
          const tagged = `Motivation: ${note}`;
          const lines = existing.split(/\n/);
          const isOn = lines.some((l) => l.trim() === tagged);
          const nextNotes = isOn
            ? lines.filter((l) => l.trim() !== tagged).join("\n").trim()
            : existing
              ? `${tagged}\n\n${existing}`
              : tagged;
          onUpdateIncident({ conversation_notes: nextNotes });
          reflectChipToggle(isOn);
        }
        return;
      }
      return;
    }

    if (option.payload === "continue_with_context") {
      setQuickRepliesDisabled(true);
      sayUser(option.label);
      const acute = extractCriticalContextSnippet(incidentRef.current);
      const prefix = acute
        ? `Keeping this centered: **${acute}**.\n\n`
        : "Keeping this centered around the acute context you shared.\n\n";

      if (curStep === "askPrior") {
        await sayBodhi(
          `${prefix}What have adults already done today to stabilize support for ${
            incidentRef.current.student_identifier || "the student"
          }? Pick what already happened, or skip if you're still in the immediate moment.`,
        );
        offerQuickReplies([
          { label: "Multiple redirections", next: "askPrior", payload: "redirections", multi: true },
          { label: "Written / verbal warnings", next: "askPrior", payload: "warnings", multi: true },
          { label: "Breaks / cool-down space", next: "askPrior", payload: "breaks", multi: true },
          { label: "Parent contact", next: "askPrior", payload: "parent", multi: true },
          { label: "Counselor / support team", next: "askPrior", payload: "counselor", multi: true },
          { label: "Done — continue →", next: "askTeacherAsk", payload: "done", isAction: true },
          { label: "Skip — continue", next: "askTeacherAsk", payload: "skip", isAction: true },
        ]);
        setPlaceholder("Add anything adults have already tried today...");
        setQuickRepliesDisabled(false);
        return;
      }

      if (curStep === "understand") {
        await sayBodhi(
          `${prefix}Given this context, what's your best read on what ${
            incidentRef.current.student_identifier || "the student"
          } is trying to protect right now? Pick everything that fits.`,
        );
        offerQuickReplies([
          { label: "Big emotions they can't name", next: "understand", payload: "emotional", multi: true },
          { label: "Needs safety / connection first", next: "understand", payload: "seeking_connection", multi: true },
          { label: "Escape or avoid overload", next: "understand", payload: "escape", multi: true },
          { label: "💬 AI Advice first", next: "understand", askMentor: true, isAction: true },
          { label: "Done — continue →", next: "genOutputs", payload: "continue", isAction: true },
          { label: "Not sure — generate the materials", next: "genOutputs", payload: "unsure", isAction: true },
        ]);
        setPlaceholder("Your read (one or more lines — what they were protecting)...");
        setQuickRepliesDisabled(false);
        return;
      }

      if (curStep === "askTeacherAsk") {
        await sayBodhi(
          `${prefix}Last context question so I can tailor outputs: what is the teacher specifically asking from this referral? Pick all that apply.`,
        );
        offerQuickReplies([
          { label: "Reset & back to class", next: "askTeacherAsk", payload: "reset", multi: true },
          { label: "Formal documentation", next: "askTeacherAsk", payload: "formal_doc", multi: true },
          { label: "Admin conversation", next: "askTeacherAsk", payload: "admin_convo", multi: true },
          { label: "Parent contact requested", next: "askTeacherAsk", payload: "parent_loop", multi: true },
          { label: "Counseling referral", next: "askTeacherAsk", payload: "counseling", multi: true },
          { label: "Done — continue →", next: "understand", payload: "continue", isAction: true },
          { label: "Not sure — continue", next: "understand", payload: "unsure", isAction: true },
        ]);
        setPlaceholder("Type one ask per line, or pick chips above...");
        setQuickRepliesDisabled(false);
        return;
      }

      if (curStep === "askContext") {
        await sayBodhi(
          `${prefix}**What was happening right before the behavior** — knowing what we know now? A trigger, a rough morning, a slow buildup, or something that felt sudden? Use your own words, or tap a chip.`,
        );
        offerQuickReplies([
          { label: "Came out of nowhere", next: "askPrior", payload: "out_of_nowhere" },
          { label: "Building all day / week", next: "askPrior", payload: "buildup" },
          { label: "Specific trigger — I'll describe", next: "askPrior", payload: "specific" },
          { label: "Skip this — continue", next: "askPrior", payload: "skip", isAction: true },
        ]);
        setPlaceholder("What happened right before? (light touch — family context already noted.)");
        setQuickRepliesDisabled(false);
        return;
      }

      // Default: replay the current step's scripted prompt so the leader
      // gets a clear restated question + chips, instead of being told
      // "I'll restate it" with nothing actually following. We skip the
      // replay for steps where it would be destructive or just noisy:
      //   - genOutputs would re-run material generation
      //   - postConversation / finish have no scripted prompt to replay
      //     (they're driven by the step-resume chips instead)
      await sayBodhi(`${prefix}Picking up from here.`);
      const replayUnsafe =
        curStep === "genOutputs" ||
        curStep === "postConversation" ||
        curStep === "finish";
      if (replayUnsafe) {
        offerStepResumeChips(curStep);
        setQuickRepliesDisabled(false);
        return;
      }
      setQuickRepliesDisabled(false);
      void goto(curStep);
      return;
    }

    setQuickRepliesDisabled(true);
    sayUser(option.label);

    if (curStep === "iep504" && option.payload) {
      const flag =
        option.payload === "iep" ||
        option.payload === "504" ||
        option.payload === "both" ||
        option.payload === "bip";
      onUpdateIncident({ iep_504_flag: flag });
      if (flag) {
        if (option.payload === "bip") {
          append({
            kind: "data-card",
            id: genId(),
            chips: [{ key: "Plans", value: "BIP" }],
          });
          const existing = incidentRef.current.conversation_notes?.trim() ?? "";
          const note = "IEP/504 context noted: BIP only";
          onUpdateIncident({
            conversation_notes: existing ? `${note}\n\n${existing}` : note,
          });
          ensureFederalGuidanceShown("bip");
        } else {
          const plan =
            option.payload === "both"
              ? "both"
              : option.payload === "504"
                ? "504"
                : "iep";
          ensureFederalGuidanceShown(plan);
        }
      } else if (option.payload === "unsure") {
        window.setTimeout(
          () =>
            sayBodhi(
              "No problem — check the student's records when you can. I'll note 'needs verification' in the record.",
            ),
          400,
        );
        const existing = incidentRef.current.conversation_notes?.trim() ?? "";
        onUpdateIncident({
          conversation_notes: existing
            ? `IEP/504 status: needs verification\n\n${existing}`
            : "IEP/504 status: needs verification",
        });
      }
    }

    if (curStep === "iep504Refine" && option.payload) {
      const existing = incidentRef.current.conversation_notes?.trim() ?? "";
      if (
        option.payload === "also_504" ||
        option.payload === "just_iep" ||
        option.payload === "just_bip"
      ) {
        onUpdateIncident({ iep_504_flag: true });
      }
      if (option.payload === "just_bip") {
        const note = "IEP/504 context noted: BIP only";
        onUpdateIncident({
          conversation_notes: existing ? `${note}\n\n${existing}` : note,
        });
        append({
          kind: "data-card",
          id: genId(),
          chips: [{ key: "Plans", value: "BIP" }],
        });
        ensureFederalGuidanceShown("bip");
      } else if (option.payload === "also_504") {
        const note = "IEP/504 context noted: also has 504";
        onUpdateIncident({
          conversation_notes: existing ? `${note}\n\n${existing}` : note,
        });
        // The refine step is reached when an IEP was already mentioned;
        // confirming "also 504" means both protections apply.
        ensureFederalGuidanceShown("both");
      } else if (option.payload === "just_iep") {
        ensureFederalGuidanceShown("iep");
      } else if (option.payload === "also_bip") {
        const note = "IEP/504 context noted: BIP mentioned (consider attaching BIP document)";
        onUpdateIncident({
          iep_504_flag: true,
          conversation_notes: existing ? `${note}\n\n${existing}` : note,
        });
        // We don't know which underlying plan (IEP vs 504) is in play
        // when the leader confirms "also has a BIP" — fall back to the
        // IEP framing (the more common pairing) so the manifestation /
        // services reminders are still surfaced.
        ensureFederalGuidanceShown("iep");
      } else if (option.payload === "share_supports") {
        window.setTimeout(
          () =>
            sayBodhi(
              "Share the behavior supports/goals you want kept front-and-center. If you have the BIP handy, you can also attach it with the paperclip so it stays in this incident's documents.",
            ),
          250,
        );
      } else if (option.payload === "unsure") {
        const note = "IEP/504/BIP status: needs verification";
        onUpdateIncident({
          conversation_notes: existing ? `${note}\n\n${existing}` : note,
        });
      }
    }

    // Teacher follow-up step — log the chosen approach as a note. We use
    // conversation_notes (with a "Teacher follow-up:" prefix) so we don't
    // need a new DB column and the line is searchable across the record.
    if (curStep === "teacherFollowUp" && option.payload === "written_summary") {
      // Special case — instead of just logging "Will send written
      // summary" and bouncing forward, generate the actual summary the
      // leader can edit / copy / download / print, and stay on the step
      // so they can review it before continuing.
      const existing = incidentRef.current.conversation_notes?.trim() ?? "";
      const noteLine = `Teacher follow-up: Will send written summary (drafted in chat)`;
      onUpdateIncident({
        conversation_notes: existing ? `${existing}\n\n${noteLine}` : noteLine,
      });
      await generateTeacherSummary({ openPanelAfter: true });
      return;
    }

    if (curStep === "teacherFollowUp" && option.payload) {
      const labels: Record<string, string> = {
        already_done: "Already looped in (no further action needed)",
        written_summary: "Will send written summary",
        quick_chat: "Quick chat / pop-in today",
        scheduled_meeting: "Scheduled longer meeting",
        later: "Will handle after closing the incident",
        skip: "No teacher follow-up needed",
      };
      const note = labels[option.payload];
      if (note) {
        const existing = incidentRef.current.conversation_notes?.trim() ?? "";
        const noteLine = `Teacher follow-up: ${note}`;
        onUpdateIncident({
          conversation_notes: existing ? `${existing}\n\n${noteLine}` : noteLine,
        });
      }
      // Wrap-up moment: now that situation, conversation, disposition AND
      // teacher follow-up are all on the record, run the debrief before
      // advancing to docOffer so the synthesis reflects the full picture.
      await runDebriefBlocking(
        incidentRef.current.behavior_follow_up || option.label,
      );
      window.setTimeout(() => goto(option.next), 300);
      return;
    }

    // Handoff step — "Skip — record is enough" chip. Persist the handoff
    // marker and exit to the dashboard so a colleague can pick it up.
    if (curStep === "handoff" && option.payload === "skip") {
      const author = currentUserName || "A colleague";
      const stamp = new Date().toLocaleString();
      const existing = incidentRef.current.conversation_notes?.trim() ?? "";
      const noteLine = `Handoff note (${author} · ${stamp}): saved with no extra context — see record above.`;
      onUpdateIncident({
        conversation_notes: existing ? `${existing}\n\n${noteLine}` : noteLine,
        status: "paused",
      });
      toast.success("Saved for handoff", {
        description: "Anyone with access can pick this up from the dashboard.",
      });
      window.setTimeout(() => onBackToDashboard(), 400);
      return;
    }

    if (curStep === "askExtended" && option.payload) {
      if (option.payload === "none") {
        onUpdateIncident({ removal_type: "" });
      } else if (option.payload === "administrative_redirect") {
        onUpdateIncident({ removal_type: "administrative_redirect" });
      } else if (option.payload === "formal_removal") {
        onUpdateIncident({ removal_type: "formal_removal" });
      }
      // Don't run the debrief here — we still have the teacher follow-up
      // question to answer next. The debrief is held until the leader
      // finishes teacherFollowUp so it lands as the actual wrap-up
      // synthesis, not in the middle of the resolution flow.
      window.setTimeout(() => goto(option.next), 300);
      return;
    }

    if (curStep === "askContext" && option.payload && option.payload !== "skip") {
      // "specific" is the one case where we DON'T just log a canned label —
      // the admin explicitly said they'd describe the trigger. Give them a
      // text input and stay on askContext. Their free text will flow through
      // the askContext text handler below and advance from there.
      if (option.payload === "specific") {
        void (async () => {
          await sayBodhi(
            `Go ahead — **what was the trigger?** A sentence or two is plenty. Anything that preceded the incident that you think mattered — a comment from a peer, a transition, a correction, something from earlier in the day, an external stressor.`,
          );
          setPlaceholder(
            `e.g. "Got called on to read aloud after failing a quiz that morning"`,
          );
        })();
        return; // Do NOT advance via option.next — wait for the admin's typed answer.
      }

      const labelMap: Record<string, string> = {
        out_of_nowhere: "Came out of nowhere — no clear trigger observed.",
        buildup: "Building all day/week — escalating over time.",
      };
      const note = labelMap[option.payload];
      if (note) {
        const existing = incidentRef.current.conversation_notes?.trim() ?? "";
        onUpdateIncident({
          conversation_notes: existing
            ? `Trigger/context: ${note}\n\n${existing}`
            : `Trigger/context: ${note}`,
        });
      }
    }

    if (curStep === "askPrior" && option.payload === "none") {
      const note = "First time — no prior interventions tried yet";
      const existingChecked = incidentRef.current.prior_interventions_checked ?? [];
      const next = existingChecked.includes(note) ? existingChecked : [note];
      onUpdateIncident({ prior_interventions_checked: next });
    }

    if (curStep === "askTeacherAsk" && option.payload) {
      const labelMap: Record<string, string> = {
        reset: "Reset and return to class",
        formal_doc: "Formal documentation of the incident",
        admin_convo: "Conversation with admin / school leader",
        counseling: "Counseling or support-team referral",
        parent_loop: "Parent contact / loop-in",
        unsure: "Not specified",
      };
      const note = labelMap[option.payload];
      if (note && option.payload !== "unsure") {
        const existingChecked = incidentRef.current.teacher_outcome_checked ?? [];
        const next = existingChecked.includes(note) ? existingChecked : [...existingChecked, note];
        onUpdateIncident({ teacher_outcome_checked: next });
      }
    }

    if (curStep === "understand" && option.payload) {
      const labelMap: Record<string, string> = {
        seeking_connection: "Seeking connection or attention",
        escape: "Trying to escape or avoid something hard",
        sensory: "Needs sensory regulation",
        emotional: "Big emotions they couldn't name",
        power: "Power / autonomy struggle",
        peer: "Peer conflict / social pressure",
        unsure: "Facilitator unsure — to be explored in conversation",
      };
      const note = labelMap[option.payload];
      if (note) {
        const existing = incidentRef.current.conversation_notes?.trim() ?? "";
        const line = `Motivation: ${note}`;
        onUpdateIncident({
          conversation_notes: existing ? `${line}\n\n${existing}` : line,
        });
      }
    }

    // ── Opt-in AI intercepts ────────────────────────────────────────────
    // These chips don't advance the flow — they surface an AI-generated
    // card in place and leave the admin on the current step, still able to
    // answer the open question or tap another chip. This replaces the old
    // auto-fire behavior where Claude's read would land unbidden while the
    // admin was still thinking.

    // "💡 Want my read?" on the understand step — fire motivation on demand.
    if (curStep === "understand" && option.payload === "ai_read") {
      if (incidentRef.current.id) {
        await sayBodhi("Let me think about what might be driving this…");
        setIsTyping(true);
        let posted = false;
        try {
          const res = await generateMotivationIfNeeded(incidentRef.current.id);
          posted = res.posted;
        } finally {
          setIsTyping(false);
        }
        if (posted) {
          await sayBodhi(
            "That's just my read — doesn't have to match yours. **What do you think?** Pick a chip or type your own.",
          );
        } else {
          await sayBodhi(
            "I couldn't put together a clean read just now. Trust your gut — **what do you think was going on?**",
          );
        }
      }
      setQuickRepliesDisabled(false);
      return;
    }

    // "💡 What do you think?" on the askExtended step — fire the removal
    // decision recommendation on demand rather than at step entry.
    if (curStep === "askExtended" && option.payload === "ai_read") {
      if (incidentRef.current.id) {
        await sayBodhi("Give me a second to weigh this…");
        setIsTyping(true);
        let posted = false;
        try {
          const res = await generateRemovalDecisionIfNeeded(incidentRef.current.id);
          posted = res.posted;
        } finally {
          setIsTyping(false);
        }
        if (posted) {
          await sayBodhi(
            "That's what I'd lean toward — but you know this kid and this day better than I do. **What's your call?**",
          );
        } else {
          await sayBodhi(
            "I couldn't land a clean recommendation just now. Your call — **what does the situation warrant?**",
          );
        }
      }
      setQuickRepliesDisabled(false);
      return;
    }

    // "✨ What am I missing?" on genOutputs — run the gap check on demand.
    if (curStep === "genOutputs" && option.payload === "force_generate_forms") {
      await goto("genOutputs", turnEpoch);
      setQuickRepliesDisabled(false);
      return;
    }

    if (curStep === "genOutputs" && option.payload === "reflection_now") {
      await sayBodhi(
        `Got it — generating the student reflection form now.`,
      );
      append({ kind: "out-reflection", id: genId() });
      await new Promise((r) => window.setTimeout(r, 300));
      append({ kind: "out-due-process", id: genId() });
      await new Promise((r) => window.setTimeout(r, 300));
      await sayBodhi(
        `When you're ready, I'll bring up the conversation guide for you.`,
      );
      offerQuickReplies([
        { label: "Show me the conversation guide", next: "genOutputs", payload: "show_guide" },
        { label: "✨ What am I missing?", next: "genOutputs", payload: "check_missing" },
        {
          label: "💬 AI Advice first",
          next: "genOutputs",
          askMentor: true,
          payload:
            "Before I go into the conversation, help me think through what's most important to say and how to frame it for this student.",
        },
      ]);
      setPlaceholder("Or ask me anything about the reflection or due-process step…");
      setQuickRepliesDisabled(false);
      return;
    }

    if (curStep === "genOutputs" && option.payload === "reflection_skip") {
      const reason = pendingReflectionDeferReasonRef.current;
      if (reason) {
        const existing = incidentRef.current.conversation_notes?.trim() ?? "";
        const note = `Reflection deferred: ${reason}`;
        onUpdateIncident({
          conversation_notes: existing ? `${existing}\n\n${note}` : note,
        });
      }
      await sayBodhi(
        "No problem — reflection is deferred for now. We can still use the conversation guide and revisit reflection later.",
      );
      offerQuickReplies([
        { label: "Show me the conversation guide", next: "genOutputs", payload: "show_guide" },
        { label: "Generate reflection now", next: "genOutputs", payload: "reflection_now" },
        { label: "✨ What am I missing?", next: "genOutputs", payload: "check_missing" },
      ]);
      setPlaceholder("Continue with guide prep, or generate reflection when ready…");
      setQuickRepliesDisabled(false);
      return;
    }

    // "✨ What am I missing?" on genOutputs — run the gap check on demand.
    if (curStep === "genOutputs" && option.payload === "check_missing") {
      if (incidentRef.current.id) {
        await sayBodhi("Scanning the record for gaps — one moment…");
        setIsTyping(true);
        let found = false;
        try {
          const res = await maybeCheckBeforeMaterials(incidentRef.current.id);
          found = res.found;
        } finally {
          setIsTyping(false);
        }
        if (!found) {
          await sayBodhi(
            "Record looks solid — I don't see anything I'd flag as missing before the conversation.",
          );
        }
      }
      setQuickRepliesDisabled(false);
      return;
    }

    // ── Paced reveal of the conversation guide (genOutputs phase B) ─────
    // In phase A we show the reflection form + due-process card, then wait
    // here. When the admin taps "Show me the conversation guide", we reveal
    // the guide, then ask a *question* about where they are in the process
    // before moving on — rather than immediately cascading into the next
    // prompt.
    if (curStep === "genOutputs" && option.payload === "show_guide") {
      const stale = () => isStaleFlow(turnEpoch);
      const guideHtml = pendingGuideHtmlRef.current;
      const isAi = pendingGuideIsAiRef.current;
      const curIncident = incidentRef.current;
      const gradeLevel = effectiveGrade(curIncident.grade_level);
      const curName = curIncident.student_identifier?.trim() || "the student";

      await sayBodhi(
        isAi
          ? `Here's your conversation guide — written around this specific incident, what's already been tried, and ${curName}'s grade${
              curIncident.iep_504_flag ? " and learning plan" : ""
            }.`
          : `Here's your conversation guide — adapted for Grade ${gradeLevel}${
              curIncident.iep_504_flag ? ", with their IEP/504 context in mind" : ""
            }.`,
      );
      if (stale()) {
        setQuickRepliesDisabled(false);
        return;
      }
      append({
        kind: "out-coaching",
        id: genId(),
        guideHtml: guideHtml ?? buildConversationGuide(curIncident).html,
        grade: gradeLevel,
        isAi,
      });

      await new Promise((r) => window.setTimeout(r, 500));
      if (stale()) {
        setQuickRepliesDisabled(false);
        return;
      }
      setStep("postConversation");
      await sayBodhi(
        `Read through it. **Where are you with ${curName} right now** — still prepping, about to go in, or just came out? Whatever's true, tell me and I'll meet you there.\n\n*Type in your own words, or tap below.*`,
      );
      if (stale()) {
        setQuickRepliesDisabled(false);
        return;
      }
      offerQuickReplies([
        { label: "Still prepping — help me get ready", next: "postConversation", askMentor: true, payload: `Help me prep for the conversation with ${curName}. What should I lead with? What should I avoid? Give me 2–3 concrete moves.` },
        { label: "About to go in", next: "resolution" },
        { label: "Just finished — here's what happened", next: "resolution" },
        { label: "It went well", next: "resolution" },
        { label: "It was hard", next: "resolution" },
        {
          label: "💬 AI Advice",
          next: "postConversation",
          askMentor: true,
          payload:
            "I'd like coaching on this conversation. Give me 2-3 concrete things to try right now based on the student and what's happened so far.",
        },
      ]);
      setPlaceholder("Share where you are in the process…");
      setQuickRepliesDisabled(false);
      return;
    }

    window.setTimeout(() => goto(option.next, turnEpoch), 300);
  };

  // ── Step-appropriate "what now" chips ───────────────────────────────────
  // Called after a mentor-style coaching response so the leader is never
  // left staring at an empty input with no path forward. We surface the
  // natural "advance" move(s) for the current step and update the
  // placeholder so it's clear they can also keep typing follow-ups.
  const offerStepResumeChips = (curStep: StepId) => {
    switch (curStep) {
      case "understand":
        offerQuickReplies([
          { label: "→ Show me the conversation guide", next: "genOutputs", payload: "show_guide" },
        ]);
        setPlaceholder("Or ask another question…");
        break;
      case "postConversation": {
        const notes = incidentRef.current.conversation_notes ?? "";
        const hasPlanExtract = notes.includes("extracted text");
        const hasMat =
          !!aiGuideRef.current || !!aiReflectionRef.current;
        const regenChip: QuickReply[] =
          hasPlanExtract && hasMat
            ? [
                {
                  label: "↻ Refresh reflection + guide (plan on file)",
                  next: "postConversation",
                  payload: "regen_materials_plan",
                  isAction: true,
                },
              ]
            : [];
        offerQuickReplies([
          ...regenChip,
          { label: "→ Log the outcome", next: "resolution", isAction: true },
        ]);
        setPlaceholder("Or ask another question about the conversation…");
        break;
      }
      case "resolution": {
        const notesR = incidentRef.current.conversation_notes ?? "";
        const hasPlanExtractR = notesR.includes("extracted text");
        const hasMatR =
          !!aiGuideRef.current || !!aiReflectionRef.current;
        const regenChipR: QuickReply[] =
          hasPlanExtractR && hasMatR
            ? [
                {
                  label: "↻ Refresh reflection + guide (plan on file)",
                  next: "resolution",
                  payload: "regen_materials_plan",
                  isAction: true,
                },
              ]
            : [];
        offerQuickReplies([
          ...regenChipR,
          { label: "→ Continue — extended time question", next: "askExtended", isAction: true },
        ]);
        setPlaceholder("Or describe the outcome — what happened next…");
        break;
      }
      case "askExtended":
        offerQuickReplies([
          { label: "Stayed in class", next: "teacherFollowUp", payload: "none" },
          { label: "Brief office redirect", next: "teacherFollowUp", payload: "administrative_redirect" },
          { label: "ISS / formal removal", next: "issDocs", payload: "formal_removal" },
        ]);
        setPlaceholder("Or describe the disposition…");
        break;
      case "issDocs":
        offerQuickReplies([
          { label: "→ Continue to teacher follow-up", next: "teacherFollowUp", isAction: true },
        ]);
        setPlaceholder("Or add to the ISS / re-entry plan above…");
        break;
      case "docOffer":
        offerQuickReplies([
          { label: "Yes — generate it", next: "genDoc" },
          { label: "I'm done for now", next: "finish" },
        ]);
        setPlaceholder("Or type anything else you want in the record…");
        break;
      case "genDoc":
        // Documents have been generated and are showing in the chat. The
        // only forward move from here is closing out the incident or
        // handing off; either way we want a visible chip for it.
        offerQuickReplies([
          { label: "✅ I'm done — wrap this up", next: "finish", isAction: true },
          { label: "💾 Save for later (handoff)", next: "handoff", isAction: true },
          { label: "💬 AI Advice", next: "genDoc", askMentor: true },
        ]);
        setPlaceholder("Or ask a question / add anything else for the record…");
        break;
      case "finish":
        // The incident is essentially complete. Anything else they type
        // is a side note — surface the remaining moves so they aren't
        // stranded staring at the completion card. The completion card
        // itself already exposes the documentation panel as a primary
        // button, so we don't duplicate that here.
        offerQuickReplies([
          { label: "💾 Save for later (handoff)", next: "handoff", isAction: true },
          { label: "💬 AI Advice", next: "finish", askMentor: true },
        ]);
        setPlaceholder("Or add a final note to the record…");
        break;
      case "handoff":
        // Rare — the handoff prompt is already visible. Just give a
        // sensible placeholder so the input doesn't sit blank.
        setPlaceholder("Add the handoff note, or 'jump back' to keep working…");
        break;

      // ── Early-step resume chips ────────────────────────────────────────
      // The user-reported dead-end pattern was hitting one of these
      // steps with an unscripted utterance and getting no chips back.
      // Each case below gives the leader a forward-progression chip plus
      // a "💬 AI Advice" lifeline so they're never stranded.
      case "welcome":
      case "regulation":
        offerQuickReplies([
          { label: "I'm ready — let's go", next: "situation", isAction: true },
          { label: "💬 AI Advice", next: curStep, askMentor: true },
        ]);
        setPlaceholder("Tell me what happened, or pick a chip above…");
        break;
      case "situation":
        offerQuickReplies([
          { label: "💬 AI Advice", next: curStep, askMentor: true },
          { label: "💾 Save for later (handoff)", next: "handoff", isAction: true },
        ]);
        setPlaceholder("Tell me who, what grade, and what happened…");
        break;
      case "askGrade":
        offerQuickReplies([
          { label: "K-2", next: "iep504", payload: "K-2" },
          { label: "3-5", next: "iep504", payload: "3-5" },
          { label: "6-8", next: "iep504", payload: "6-8" },
          { label: "9-12", next: "iep504", payload: "9-12" },
        ]);
        setPlaceholder("Or type the grade — K, 2nd, 7th, 10th…");
        break;
      case "iep504":
      case "iep504Refine":
        offerQuickReplies([
          { label: "Yes — IEP", next: "askContext", payload: "iep" },
          { label: "Yes — 504", next: "askContext", payload: "504" },
          { label: "Yes — both", next: "askContext", payload: "both" },
          { label: "Yes — BIP", next: "askContext", payload: "bip" },
          { label: "No", next: "askContext", payload: "no" },
          { label: "I need to check", next: "askContext", payload: "unsure" },
        ]);
        setPlaceholder("Or type a quick note about plans on file…");
        break;
      case "askContext":
        offerQuickReplies([
          { label: "Came out of nowhere", next: "askPrior", payload: "out_of_nowhere" },
          { label: "Building all day / week", next: "askPrior", payload: "buildup" },
          { label: "Specific trigger — I'll describe", next: "askPrior", payload: "specific" },
          { label: "Skip — continue", next: "askPrior", payload: "skip", isAction: true },
        ]);
        setPlaceholder("What was happening right before the behavior…");
        break;
      case "askPrior":
        offerQuickReplies([
          { label: "Done — continue →", next: "askTeacherAsk", payload: "continue", isAction: true },
          { label: "Skip — continue", next: "askTeacherAsk", payload: "skip", isAction: true },
          { label: "Nothing yet — first time", next: "askTeacherAsk", payload: "none" },
        ]);
        setPlaceholder("List anything already tried, or pick a chip above…");
        break;
      case "askTeacherAsk":
        offerQuickReplies([
          { label: "Reset & back to class", next: "askTeacherAsk", payload: "reset", multi: true },
          { label: "Formal documentation", next: "askTeacherAsk", payload: "formal_doc", multi: true },
          { label: "Admin conversation", next: "askTeacherAsk", payload: "admin_convo", multi: true },
          { label: "Counseling referral", next: "askTeacherAsk", payload: "counseling", multi: true },
          { label: "Parent contact requested", next: "askTeacherAsk", payload: "parent_loop", multi: true },
          { label: "Done — continue →", next: "understand", payload: "continue", isAction: true },
          { label: "Not sure — continue", next: "understand", payload: "unsure", isAction: true },
        ]);
        setPlaceholder("Pick all that apply, or type one ask per line…");
        break;
      case "understand":
        offerQuickReplies([
          { label: "Seeking connection / attention", next: "understand", payload: "seeking_connection", multi: true },
          { label: "Escape or avoid something hard", next: "understand", payload: "escape", multi: true },
          { label: "Big emotions they can't name", next: "understand", payload: "emotional", multi: true },
          { label: "Power / autonomy struggle", next: "understand", payload: "power", multi: true },
          { label: "💡 Want my read?", next: "understand", payload: "ai_read", isAction: true },
          { label: "Done — continue →", next: "genOutputs", payload: "continue", isAction: true },
          { label: "Not sure — generate the materials", next: "genOutputs", payload: "unsure", isAction: true },
        ]);
        setPlaceholder("Your read, or pick a chip above…");
        break;
      case "genOutputs":
        offerQuickReplies([
          { label: "→ I had the conversation, log it", next: "postConversation", isAction: true },
          { label: "Show me the conversation guide", next: "genOutputs", payload: "show_guide" },
          { label: "✨ What am I missing?", next: "genOutputs", payload: "check_missing" },
          { label: "💬 AI Advice", next: "genOutputs", askMentor: true },
        ]);
        setPlaceholder("Ask another question / share what happened…");
        break;
      case "teacherFollowUp":
        offerQuickReplies([
          { label: "Send a written summary", next: "teacherFollowUp", payload: "written_summary" },
          { label: "Quick chat / pop-in today", next: "docOffer", payload: "quick_chat" },
          { label: "Scheduled longer meeting", next: "docOffer", payload: "scheduled_meeting" },
          { label: "Already looped in", next: "docOffer", payload: "already_done" },
          { label: "Will handle later", next: "docOffer", payload: "later", isAction: true },
        ]);
        setPlaceholder("How will you loop the teacher in…");
        break;

      default:
        // Generic fallback so the leader is never left without guidance
        // on what to type next. With the early-step cases above this is
        // now only hit for steps that genuinely don't have a meaningful
        // resume option (none today — kept for future-proofing).
        setPlaceholder("Answer the prompt above, or share more context…");
        break;
    }
  };

  // ── Send handler ────────────────────────────────────────────────────────
  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    // First user action releases the boot-time top pin so subsequent
    // turns scroll to the bottom as a chat normally does.
    pinToTopRef.current = false;
    beginFlowEpoch();
    setInput("");
    sayUser(text);

    const curStep = stepRef.current;
    const cur = incidentRef.current;

    // Tier 1, Step A — AI intent classification. We run a single
    // classifier call against every turn and use the verdict to override
    // a small, high-confidence subset of the regex-based routing below
    // (today: orientation questions and review-mode navigation). For
    // every other intent we fall through unchanged. The verdict is
    // logged regardless so we can decide what to replace next.
    const verdict = await classifyTurn(text);
    const aiConfident = !!verdict && verdict.confidence >= CLASSIFIER_CONFIDENCE_FLOOR;
    const mentorDirective = parseMentorDirective(text);

    // Highest-priority routing: explicit "ask mentor" / evidence-check turns
    // must never fall through to scripted step advancement.
    if (mentorDirective.routeToMentor) {
      const mentorPrompt = mentorDirective.evidenceOnly
        ? `Evidence-check mode. Use only the uploaded plan extract available in CONTEXT notes.\n\nReturn exactly two sections:\n1) Exact quotes from uploaded plan\n2) Not documented in upload\n\nRules: no inference, no paraphrase as fact, no workflow advancement language. If no supporting quote exists, say "none found in uploaded extract" under section 1.\n\nAdmin request: ${mentorDirective.userPrompt || text}`
        : mentorDirective.userPrompt || text;
      const result = await askMentor(mentorPrompt);
      if (result && "html" in result) {
        append({ kind: "bodhi", id: genId(), html: result.html });
      } else {
        const detail = result && "error" in result ? result.error : "";
        await sayBodhi(
          `I couldn't reach the mentor just now${
            detail ? ` (${detail})` : ""
          }. Try again in a moment.`,
        );
      }
      offerStepResumeChips(curStep);
      if (mentorDirective.evidenceOnly) {
        setPlaceholder("Ask another evidence-check question, or continue the workflow…");
      }
      return;
    }

    // Plan-document evidence questions should always route to mentor even when
    // not prefixed with "ask mentor", and should not advance scripted steps.
    if (looksLikePlanEvidenceQuestion(text)) {
      const evidencePrompt =
        `Evidence-check mode. Use only uploaded plan extract text in CONTEXT notes.\n\n` +
        `Return exactly two sections:\n` +
        `1) Exact quotes from uploaded plan\n` +
        `2) Not documented in upload\n\n` +
        `No workflow advancement language. No inferred claims as facts.\n\n` +
        `Admin request: ${text}`;
      const result = await askMentor(evidencePrompt);
      if (result && "html" in result) {
        append({ kind: "bodhi", id: genId(), html: result.html });
      } else {
        await sayBodhi(
          "I couldn't reach the mentor just now. Try again in a moment.",
        );
      }
      offerStepResumeChips(curStep);
      setPlaceholder("Ask another evidence-check question, or continue the workflow…");
      return;
    }

    // Reflection-form regenerate: the admin is either telling us the
    // current form is off ("that reflection is super vague") or directly
    // asking us to redo it ("fix the reflection form"). Capture whatever
    // additional context they typed into conversation_notes so the next
    // generation sees it, then force-regenerate. The live `aiReflection`
    // state powers every rendered out-reflection card, so updating it
    // refreshes existing forms in place; we also append a new card so
    // the leader visually sees the updated version land.
    if (isReflectionRegenerateRequest(text) && curStep !== "welcome") {
      logUxSignal("reflection_form_regenerate_requested", { step: curStep });
      const trimmed = text.trim();
      const existingNotes = cur.conversation_notes?.trim() ?? "";
      const stamp = new Date().toLocaleString();
      const updatedNotes = existingNotes
        ? `${existingNotes}\n\nUpdated context (${stamp}): ${trimmed}`
        : `Updated context (${stamp}): ${trimmed}`;
      onUpdateIncident({ conversation_notes: updatedNotes });
      // Reset the cached "we already generated for this incident" guard so
      // generateMaterials actually re-runs against the freshly updated record.
      materialsGenRef.current = null;

      await sayBodhi(
        "Got it — let me regenerate the reflection form using what you just told me about this situation. One moment.",
      );
      setIsTyping(true);
      try {
        const id = cur.id;
        if (!id) {
          await sayBodhi(
            "I need to save the incident before I can refresh the form. Add a quick detail and I'll try again.",
          );
        } else {
          const { reflection } = await generateMaterials(id, { force: true });
          if (reflection) {
            append({ kind: "out-reflection", id: genId() });
            await sayBodhi(
              "I wove in the additional context and updated the reflection form. If anything is still off, tell me what to change and I'll redo it.",
            );
          } else {
            await sayBodhi(
              "I tried, but the AI generator didn't return a usable form just now. Want me to try once more, or shall I draft it manually with you?",
            );
          }
        }
      } catch (err) {
        console.error("[RestorationRoomChat] regenerate reflection on request:", err);
        await sayBodhi(
          "I hit an error regenerating the reflection form. Try sending the request again in a moment.",
        );
      } finally {
        setIsTyping(false);
      }
      offerStepResumeChips(curStep);
      setPlaceholder(
        "Add more context, ask for another revision, or pick a chip below…",
      );
      return;
    }

    // Universal direct-question handling: answer first, route second.
    // If the admin asks mixed questions ("what prompt?" + "reflection form?")
    // we answer both in one response and keep them on the same step.
    const asksCurrentPrompt = isCurrentPromptQuestion(text);
    const asksReflection = isReflectionFormQuestion(text);
    if ((asksCurrentPrompt || asksReflection) && curStep !== "welcome") {
      logUxSignal("direct_question_arbitrated", {
        asksCurrentPrompt,
        asksReflection,
        step: curStep,
      });
      const sections: string[] = [];
      if (asksCurrentPrompt) {
        sections.push(`**Current prompt:** ${currentPromptForStep(curStep, cur)}`);
      }
      if (asksReflection) {
        const reflectionDecision = assessReflectionNeed(cur);
        const lead = reflectionDecision.recommend
          ? "Yes — reflection is recommended now."
          : "Not yet — reflection should be deferred for now.";
        let line = `**Reflection form:** ${lead} Why: ${reflectionDecision.reason}.`;
        if (reflectionDecision.note) {
          line += ` _Heads up: ${reflectionDecision.note}_`;
        }
        sections.push(line);
      }
      sections.push("**Next actions:** Answer the current prompt, or use the quick chips below.");
      await sayBodhi(sections.join("\n\n"));
      offerStepResumeChips(curStep);
      setPlaceholder("Answer the current prompt, ask a direct question, or choose a chip...");
      return;
    }

    if (isInMomentUrgencyIntent(text) && curStep !== "welcome") {
      const result = await askMentor(
        `The admin needs immediate in-the-moment coaching. Use exactly 3 headers:\n` +
          `Right now (say this)\nDo this\nIf it doesn't land\n\n` +
          `Keep total length short and concrete. No workflow progression language.\n\n` +
          `Admin message: ${text}`,
      );
      if (result && "html" in result) {
        append({ kind: "bodhi", id: genId(), html: result.html });
      } else {
        await sayBodhi(
          "Right now: lead with regulation and one short validating line, then offer one concrete next move.",
        );
      }
      offerStepResumeChips(curStep);
      return;
    }

    if (isRepeatPushbackIntent(text) && curStep !== "welcome") {
      logUxSignal("repeat_pushback_detected", { step: curStep });
      const result = await askMentor(
        `The admin is signaling the workflow missed their context: "${text}". ` +
          `Acknowledge directly in one sentence, then give a concrete next move in 2 bullets max. ` +
          `Do not repeat the prior scripted question or advance the workflow.`,
      );
      if (result && "html" in result) {
        append({ kind: "bodhi", id: genId(), html: result.html });
      } else {
        await sayBodhi("You're right — let's reset from what you just said and choose the most useful next move.");
      }
      offerStepResumeChips(curStep);
      setPlaceholder("Add what I missed in one line, or continue from the chips...");
      return;
    }

    if (isPauseOrHoldIntent(text)) {
      await sayBodhi(
        "Paused. Tell me what you need to add, and I won't move this forward until you're ready.",
      );
      setPlaceholder("Add the missing detail, or type continue when you're ready...");
      return;
    }

    // Only pause for sensitive human-centered context during the
    // intake/coaching phases — once the leader has had the conversation
    // they're logging outcomes and follow-up plans, and pausing there
    // creates a loop. Example the user hit: at `resolution`, typing
    // "Susy is really sad about her mom — we have a plan to meet weekly"
    // is the answer to the outcome prompt, not a new context flag. The
    // pause was firing on the word "mom" and re-asking the same prompt.
    const HUMAN_CENTERED_PAUSE_STEPS: ReadonlySet<StepId> = new Set<StepId>([
      "welcome",
      "regulation",
      "situation",
      "askGrade",
      "iep504",
      "iep504Refine",
      "askContext",
      "askPrior",
      "askTeacherAsk",
      "understand",
      "genOutputs",
    ]);
    const humanCentered = HUMAN_CENTERED_PAUSE_STEPS.has(curStep)
      ? shouldPauseForHumanCenteredContext(text, verdict)
      : { pause: false as const, source: "ai" as const };
    if (humanCentered.pause) {
      logUxSignal("human_centered_pause", {
        source: humanCentered.source,
        humanConf: String(verdict?.extracted?.human_centered_confidence ?? 0),
      });
      const existingDesc = cur.incident_description?.trim() ?? "";
      const descLabel =
        humanCentered.source === "ai" ? "Human context (AI)" : "Critical context";
      const updatedDesc = existingDesc
        ? `${existingDesc}\n\n${descLabel}: ${text}`
        : `${descLabel}: ${text}`;
      const existingNotes = cur.conversation_notes?.trim() ?? "";
      const note =
        humanCentered.source === "ai"
          ? `Human context (AI): ${text}`
          : `Critical context update: ${text}`;
      onUpdateIncident({
        incident_description: updatedDesc,
        conversation_notes: existingNotes ? `${existingNotes}\n\n${note}` : note,
      });
      mentorContextSentRef.current = false;
      append({
        kind: "data-card",
        id: genId(),
        chips: [
          {
            key:
              humanCentered.source === "ai"
                ? "Human context (AI)"
                : "Urgent context (classifier offline)",
            value: text.length > 96 ? `${text.slice(0, 93)}...` : text,
          },
        ],
      });
      const studentHint = cur.student_identifier?.trim() || "this student (not named yet)";
      const mentorPrompt =
        `Sensitive home or family stressor during a **school behavior-incident** intake. The admin wrote:\n"${text}"\n\n` +
        `Student on the referral: **${studentHint}**.\n\n` +
        `They need coaching that **listens first** — not a repeat of scripted intake questions ("what was the trigger?", etc.).\n\n` +
        `Reply in markdown (use **bold** for section titles). Use exactly these sections:\n\n` +
        `**What I'm hearing**\n` +
        `2–4 short sentences. Acknowledge weight without clichés. Name how this might reasonably affect the student's bandwidth or behavior at school.\n\n` +
        `**Worth asking (gently)**\n` +
        `2–3 bullets: caring questions the admin might hold in mind, or softly ask the student/family **if** rapport allows — not an interrogation.\n\n` +
        `**Stance for school today**\n` +
        `1–2 bullets: regulation first, proportionality, when it may make sense to involve counseling or a broader team (stay district-neutral; don't invent facts).\n\n` +
        `**One practical move**\n` +
        `One bullet: the single next step you'd recommend.\n\n` +
        `Do not paste back the chatbot's last form question. Do not minimize the disclosure.`;
      const mentorResult = await askMentor(mentorPrompt);
      if (mentorResult && "html" in mentorResult) {
        append({ kind: "bodhi", id: genId(), html: mentorResult.html });
      }
      await sayBodhi(
        humanCentered.source === "ai"
          ? "This belongs in how we read this. I've logged it on the record. When you're ready, we can keep going with the intake, with this context in view."
          : "I've logged that as urgent context. When you're ready, we'll continue with this in view. (The AI classifier wasn't available, so if this is nuanced life context rather than an emergency, add a bit more detail or tap AI Advice.)",
      );
      const regenChip: QuickReply[] =
        !!aiGuideRef.current || !!aiReflectionRef.current
          ? [
              {
                label: "↻ Rebuild guide + reflection with this context",
                next: curStep,
                payload: "regen_materials_context",
                isAction: true,
              },
            ]
          : [];
      const crisisFollowChips: QuickReply[] = [...regenChip];
      if (curStep === "welcome" || curStep === "regulation") {
        crisisFollowChips.push({
          label: "Continue — tell me what happened",
          next: "situation",
          isAction: true,
        });
      } else if (
        curStep === "askContext" ||
        curStep === "askPrior" ||
        curStep === "askTeacherAsk" ||
        curStep === "understand" ||
        curStep === "genOutputs"
      ) {
        crisisFollowChips.push(
          { label: "← Re-open Understand", next: "understand", isAction: true },
          {
            label: "Continue from here",
            next: curStep,
            payload: "continue_with_context",
            isAction: true,
          },
        );
      } else {
        crisisFollowChips.push({
          label: "Continue from here",
          next: curStep,
          payload: "continue_with_context",
          isAction: true,
        });
      }
      offerQuickReplies(crisisFollowChips);
      setPlaceholder("Add more context, or choose the next move above...");
      return;
    }

    // Review mode — leader is reading a completed stage. Three intents
    // we have to disambiguate before we just blindly append:
    //   1. "jump back" / "done" / "back" → exit review and return to the
    //      natural resume step. We can't log these as content or the
    //      record fills up with noise.
    //   2. Substantive new facts on The Situation (e.g. "she also hit
    //      the teacher", "she said the f-word too") → these update the
    //      actual situation field and deserve a mentor re-read because
    //      they can change the disposition.
    //   3. Anything else → append as an annotation to conversation_notes
    //      under the stage's section, and tell the leader how to leave
    //      review when they're ready.
    if (reviewStageRef.current) {
      const stage = reviewStageRef.current;
      const stageLabel = stageLabelFor(stage);

      // 1. Navigation intent — exit review mode without logging.
      // Prefer the AI verdict when it's confident enough; fall back to
      // the original regex (kept verbatim) so behavior is unchanged
      // when the classifier is unavailable or unsure.
      const lo = text.toLowerCase().trim();
      const isNavigationByAI =
        aiConfident &&
        verdict!.intent === "navigation" &&
        (verdict!.extracted.navigation_target === "exit_review" ||
          verdict!.extracted.navigation_target === "back" ||
          verdict!.extracted.navigation_target === "forward");
      const isNavigationByRegex =
        /^(jump\s*back|go\s*back|take me back|back|exit|leave|done|finished|nevermind|never\s*mind|nope|no thanks?|continue|move on|forward|cancel)[.!?\s]*$/i.test(
          lo,
        );
      const isNavigation = isNavigationByAI || isNavigationByRegex;
      if (isNavigation) {
        reviewStageRef.current = null;
        const resume = resumeStepFor(cur);
        setStep(resume);
        await sayBodhi(
          `Back where we were. Pick up wherever you'd like — the "What we have so far" panel reflects the latest state.`,
        );
        // Surface step-appropriate chips so the leader has a clear next
        // move after exiting review, instead of just an empty input.
        offerStepResumeChips(resume);
        return;
      }

      // 2. Situation review — new facts update the source-of-truth
      // description AND get a mentor re-read so Bodhi engages with what
      // changed, instead of robotically saying "Added that".
      if (stage === "incident") {
        const existingDesc = cur.incident_description?.trim() ?? "";
        const updatedDesc = existingDesc
          ? `${existingDesc}\n\nAdditional context (added in review): ${text}`
          : text;
        onUpdateIncident({ incident_description: updatedDesc });

        const studentName = cur.student_identifier?.trim() || "this student";
        const gradeBit = cur.grade_level ? ` (Grade ${cur.grade_level})` : "";
        const mentorPrompt =
          `An admin is in REVIEW MODE on a behavior incident already in progress, and just added a new detail to The Situation. ` +
          `Existing record: ${studentName}${gradeBit}. Original description: "${existingDesc || "(none)"}". ` +
          `New detail just added: "${text}". ` +
          `In 2-3 sentences max, name what (if anything) this changes about the read or the disposition — be concrete. ` +
          `If it changes very little, say so plainly. Don't restart the intake. End with a one-line nudge about whether they should keep reviewing or jump back to the workflow.`;
        const mentorResult = await askMentor(mentorPrompt);
        if (mentorResult && "html" in mentorResult) {
          append({ kind: "bodhi", id: genId(), html: mentorResult.html });
        } else {
          await sayBodhi(
            `Logged on **The Situation**. Type **jump back** when you're ready to keep going.`,
          );
        }
        // Always give a visible exit + advice option so review mode
        // never becomes a dead end. `review_back` is the canonical
        // payload that handleQuickReply uses to exit review without
        // re-running the prior step's scripted prompt.
        offerQuickReplies([
          { label: "↶ Jump back to where I was", next: resumeStepFor(cur), payload: "review_back", isAction: true },
          { label: "💬 AI Advice", next: curStep, askMentor: true },
        ]);
        setPlaceholder(
          "Add more detail, or tap a chip when you're ready to keep going…",
        );
        return;
      }

      // 3. Default: tag the note under the stage's section so it's clear
      // in the record where it came from. Keep the response short — we
      // don't want a coaching response on every review note.
      const existing = cur.conversation_notes?.trim() ?? "";
      const note = `Review note (${stageLabel}): ${text}`;
      onUpdateIncident({
        conversation_notes: existing ? `${existing}\n\n${note}` : note,
      });
      await sayBodhi(
        `Logged on **${stageLabel}**. Tap **Jump back** when you're ready to keep going, or keep adding notes.`,
      );
      offerQuickReplies([
        { label: "↶ Jump back to where I was", next: resumeStepFor(cur), payload: "review_back", isAction: true },
        { label: "💬 AI Advice", next: curStep, askMentor: true },
      ]);
      setPlaceholder("Add another note, or tap a chip to leave review…");
      return;
    }

    // Process-orientation questions: "where am I", "what's next", "what do
    // we know". Always answerable from the captured state — answer with the
    // summary digest and DON'T advance the form. The leader stays exactly
    // where they were, just with their bearings restored.
    //
    // Prefer the AI classifier when it's confident (catches phrasings the
    // regex can't anticipate, e.g. "remind me where we are", "am I doing
    // this right?"). Fall back to the regex helper otherwise so behavior
    // is unchanged when the classifier is unavailable.
    const aiOrientation =
      aiConfident && verdict!.intent === "orientation"
        ? verdict!.extracted.orientation_kind ?? null
        : null;
    const orientation = aiOrientation ?? classifyOrientationQuestion(text);
    if (orientation) {
      // Keep orientation answers consistent with the summary headline's
      // "currently on X" logic (first incomplete stage), falling back to the
      // data-clamped active stage when everything is complete.
      const stages = [
        ...STAGE_META,
        ...(showsExtendedStage(cur) ? [EXTENDED_STAGE] : []),
      ];
      const completion = getStageCompletion(cur);
      const nextIncomplete = stages.find((s) => !completion[s.key])?.key ?? null;
      const digestStage =
        nextIncomplete ?? clampStageToData(stageForStep(curStep), cur);
      const digest = buildSummaryDigest(cur, digestStage);
      const nextLabel = stageLabelFor(digestStage);
      const tail =
        orientation === "next"
          ? `\n\nYou're on **${nextLabel}**. Answer the last prompt above to keep moving — or tap **Continue from where I left off** below.`
          : orientation === "where"
            ? `\n\nThe top of the chat shows the same picture in the **What we have so far** panel — you can expand it any time.`
            : "";
      await sayBodhi(`${digest}${tail}`);
      // Always surface a forward-progression chip + AI advice so the
      // leader is never stranded after asking "where am I" / "what's
      // next". `continue_from_here` matches the canonical payload the
      // dashboard uses to land back at resumeStepFor.
      offerQuickReplies([
        { label: "→ Continue from where I left off", next: resumeStepFor(cur), payload: "continue_from_here", isAction: true },
        { label: "💬 AI Advice", next: curStep, askMentor: true },
      ]);
      setPlaceholder("Or ask another question / share more context…");
      return;
    }

    // Tier 1 / Step D (phase 1): mid-flow context/correction routing.
    //
    // When the classifier says this turn is an add_context/correction update,
    // apply extracted facts directly to the incident state and append the text
    // to Situation when it looks like a substantive behavioral detail. This
    // prevents "wait, she also hit the teacher" from being treated as a random
    // answer to whatever prompt happened to be on screen.
    const canRouteMidFlowContext = ![
      "welcome",
      "regulation",
      "situation",
      "askGrade",
      "iep504",
      "iep504Refine",
    ].includes(curStep);
    if (
      canRouteMidFlowContext &&
      aiConfident &&
      (verdict!.intent === "add_context" || verdict!.intent === "correction")
    ) {
      const ai = applyAiExtractedFacts(verdict, cur);
      const looksLikeSituationDetail =
        /\b(also|actually|wait|another|plus|and|hit|threw|pushed|kicked|cursed|swore|threat|weapon|eloped|ran out|safety)\b/i.test(
          text,
        );
      const existingDesc = cur.incident_description?.trim() ?? "";
      const updatedDesc =
        looksLikeSituationDetail && existingDesc
          ? `${existingDesc}\n\nAdditional context: ${text}`
          : existingDesc;

      const hasStructuredUpdates = Object.keys(ai.updates).length > 0;
      const hasSituationAppend = updatedDesc !== existingDesc;
      if (hasStructuredUpdates || hasSituationAppend) {
        onUpdateIncident({
          ...ai.updates,
          ...(hasSituationAppend ? { incident_description: updatedDesc } : {}),
        });
        if (ai.chips.length) {
          append({ kind: "data-card", id: genId(), chips: ai.chips });
        }
        // Always re-emit the current step's prompt after capturing context.
        // Without this, steps that aren't in offerStepResumeChips' switch
        // (askContext, askPrior, askTeacherAsk, genOutputs, parentPrep, …)
        // would leave the leader staring at "Added that context to the
        // record." with no follow-up, and the conversation would stall.
        const nextPrompt = currentPromptForStep(curStep, incidentRef.current);
        const intro =
          verdict!.intent === "correction"
            ? "Updated the record with that correction."
            : "Added that context to the record.";
        await sayBodhi(`${intro}\n\n**Back to:** ${nextPrompt}`);
        offerStepResumeChips(curStep);
        return;
      }
    }

    // If the user is clearly asking for coaching help, sharing their emotional
    // state, or pushing back on the flow ("you don't know..."), route to Claude
    // instead of blindly logging the text as a form answer and advancing.
    //
    // Intake steps (welcome, regulation, situation, askGrade, iep504) are
    // included because those are the moments the admin is most likely to vent,
    // decompress, or say something the bot should actually *respond* to
    // before pressing on with form questions. We still let the scripted path
    // run after mentor replies — so if the admin shared any facts along with
    // the emotion, the flow still captures them.
    const mentorOnlySteps: StepId[] = [
      "understand",
      "postConversation",
      "resolution",
      "askExtended",
      "issDocs",
      "docOffer",
    ];
    const mentorFirstSteps: StepId[] = [
      "welcome",
      "regulation",
      "situation",
      "askGrade",
      "iep504",
      "iep504Refine",
    ];

    const shouldRouteToMentor =
      (mentorOnlySteps.includes(curStep) || mentorFirstSteps.includes(curStep)) &&
      isMentorHelpRequest(text);

    if (shouldRouteToMentor) {
      // Log the turn in conversation_notes for later-stage steps so it shows
      // up in the record. Early intake steps don't log (too noisy before we
      // even have a student ID).
      if (mentorOnlySteps.includes(curStep)) {
        const existing = cur.conversation_notes?.trim() ?? "";
        const noteLine = `Q (${curStep}): ${text}`;
        onUpdateIncident({
          conversation_notes: existing ? `${existing}\n\n${noteLine}` : noteLine,
        });
      }

      const result = await askMentor(text);
      if (result && "html" in result) {
        append({ kind: "bodhi", id: genId(), html: result.html });
      } else {
        const detail = result && "error" in result ? result.error : "";
        await sayBodhi(
          `I couldn't reach the mentor just now${
            detail ? ` (${detail})` : ""
          }. Try again in a moment, or answer the last question to keep the workflow moving.`,
        );
      }

      // After a mentor-only response, the leader is left in front of an
      // empty input with no obvious next move. Surface a small set of
      // "what now" chips appropriate to the current step so they don't
      // feel like they're typing into a void.
      if (mentorOnlySteps.includes(curStep)) {
        await new Promise((r) => window.setTimeout(r, 400));
        offerStepResumeChips(curStep);
        return;
      }

      // For the mentorOnly (mid-flow) steps we stop here and wait for the
      // admin's next input. For intake steps we want to gently hand back to
      // the form AFTER the mentor has spoken — but ONLY if the admin hasn't
      // also given us the answer to the current step's question. Pattern we
      // keep hitting: "Joe, 5th grade, they yelled at me in front of
      // everyone" triggers the emotional-help router (yelled at me) AND
      // contains the facts we're asking for. Re-prompting after the mentor
      // in that case feels like we didn't hear them.
      if (mentorFirstSteps.includes(curStep)) {
        await new Promise((r) => window.setTimeout(r, 900));
        if (curStep === "welcome" || curStep === "regulation") {
          // Tier 1 / Step B — auto-extraction takeover.
          //
          // Even when the welcome/regulation message reads as emotional
          // (mentor-first), the leader may have packed real case facts
          // alongside the venting ("ugh, Sarah is melting down — third
          // grader, threw a chair"). Capture the structured facts the
          // classifier extracted, treat the message itself as the
          // situation narrative, and skip ahead — instead of forcing the
          // leader to re-type everything in the next prompt.
          const ai = applyAiExtractedFacts(verdict, cur);
          const accommodations = detectAccommodationSignals(text);
          const dovedIn =
            !!ai.updates.student_identifier || !!ai.updates.safety_check;
          if (dovedIn) {
            const finalUpdates: Partial<Incident> = {
              ...ai.updates,
              incident_description: text,
              ...(accommodations.hasAny ? { iep_504_flag: true } : {}),
            };
            onUpdateIncident(finalUpdates);
            if (ai.chips.length) {
              append({ kind: "data-card", id: genId(), chips: ai.chips });
            }
            if (accommodations.hasAny) {
              append({
                kind: "data-card",
                id: genId(),
                chips: [{ key: "Plans", value: accommodations.chipValue }],
              });
              ensureFederalGuidanceShown(
                accommodations.plan ?? (accommodations.hasBip ? "bip" : "iep"),
              );
              const existing = cur.conversation_notes?.trim() ?? "";
              const note = `IEP/504/BIP context captured from intake: ${text}`;
              onUpdateIncident({
                conversation_notes: existing ? `${note}\n\n${existing}` : note,
              });
            }
            const haveGrade = !!(ai.updates.grade_level || cur.grade_level);
            await sayBodhi(
              haveGrade
                ? `Got it — captured what you shared. We'll come back to the rest in a moment.`
                : `Got it — captured what you shared. I just need ${ai.updates.student_identifier ?? "their"}'s grade level next.`,
            );
            setStep(
              haveGrade
                ? accommodations.hasAny
                  ? "iep504Refine"
                  : "iep504"
                : "askGrade",
            );
            return;
          }
          await sayBodhi(
            "When you're ready — tell me what happened. Who's the student, what grade, and what did they do?",
          );
          setPlaceholder(
            "e.g. Marcus, 7th grade, got into a fight in the hallway after lunch...",
          );
          setStep("situation");
        } else if (curStep === "situation") {
          // Did the admin also give us facts in the same message?
          const ex = extractFromSituation(text, cur);
          const gotFacts =
            !!ex.student_identifier ||
            !!ex.grade_level ||
            !!ex.location ||
            !!ex.safety_check ||
            ex.repeat_incident !== undefined;

          if (gotFacts) {
            const fields: Partial<Incident> = {};
            if (ex.student_identifier) fields.student_identifier = ex.student_identifier;
            if (ex.grade_level) fields.grade_level = ex.grade_level;
            if (ex.repeat_incident !== undefined) fields.repeat_incident = ex.repeat_incident;
            if (ex.safety_check) fields.safety_check = ex.safety_check;
            const desc = ex.incident_description ?? text;
            fields.incident_description = ex.location
              ? `${desc} (location: ${ex.location})`
              : desc;
            onUpdateIncident(fields);

            const chips: { key: string; value: string }[] = [];
            if (ex.student_identifier) chips.push({ key: "Student", value: ex.student_identifier });
            if (ex.grade_level) chips.push({ key: "Grade", value: ex.grade_level });
            if (ex.location) chips.push({ key: "Location", value: ex.location });
            if (ex.repeat_incident) chips.push({ key: "Repeat", value: "Yes" });
            if (ex.safety_check === "pause_needed") chips.push({ key: "Safety", value: "Pause needed" });
            if (chips.length) append({ kind: "data-card", id: genId(), chips });

            const gradeMentioned = !!ex.grade_level;
            window.setTimeout(
              () => goto(gradeMentioned || isGradeSet(cur.grade_level) ? "iep504" : "askGrade"),
              400,
            );
          } else {
            await sayBodhi(
              "When you're ready, I still need the basics so I can help: **who's the student, what grade, and what did they do?**",
            );
            setPlaceholder(
              "e.g. Ethan, 2nd grade, hit another student on the playground...",
            );
            // Stay on "situation" — the admin hasn't given us the facts yet.
          }
        } else if (curStep === "askGrade") {
          // Did the admin slip a grade in alongside the emotional content?
          // e.g. "5th, I'm so nervous". Reuse the same permissive parser we
          // use in the dedicated askGrade step.
          const lo2 = text.trim();
          const wordNum: Record<string, string> = {
            kindergarten: "K", kinder: "K", k: "K",
            first: "1", second: "2", third: "3", fourth: "4", fifth: "5",
            sixth: "6", seventh: "7", eighth: "8", ninth: "9",
            tenth: "10", eleventh: "11", twelfth: "12", senior: "12",
          };
          const wordHit = lo2.toLowerCase().match(
            /\b(kindergarten|kinder|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth|senior)\b/,
          );
          const gradeMatch =
            (wordHit ? ([0, wordNum[wordHit[1]]] as unknown as RegExpMatchArray) : null) ||
            lo2.match(/\b(?:grade\s*)(\d{1,2})\b/i) ||
            lo2.match(/\b(\d{1,2})\s*(?:st|nd|rd|th)\b/i) ||
            lo2.match(/\b(K|kinder(?:garten)?)\b/i) ||
            lo2.match(/^(\d{1,2})$/) ||
            null;
          let grade: GradeLevel | null = null;
          if (gradeMatch) {
            const raw = gradeMatch[1];
            grade = /^k/i.test(raw) ? "K-2" : toGradeLevel(raw);
          }
          if (grade) {
            onUpdateIncident({ grade_level: grade });
            append({
              kind: "data-card",
              id: genId(),
              chips: [{ key: "Grade", value: grade }],
            });
            window.setTimeout(() => goto("iep504"), 400);
          } else {
            await sayBodhi(
              `I still just need ${cur.student_identifier || "the student"}'s grade so I can tailor what comes next.`,
            );
            setPlaceholder("Grade (e.g. K, 2nd, 7th, 10th)...");
            // Stay on askGrade.
          }
        } else if (curStep === "iep504") {
          // Admin might have answered Y/N alongside the emotional content.
          const lo3 = text.toLowerCase();
          const mentionsIep = /\biep\b/.test(lo3);
          const mentions504 = /\b504\b/.test(lo3);
          const mentionsBip = /\bbip\b|\bbehavior intervention plan\b/.test(lo3);
          const yn = resolveYesNo(text, verdict);
          if (mentionsIep || mentions504 || mentionsBip || yn === "yes" || yn === "no") {
            // Re-run through the main iep504 handler by deferring to the
            // scripted path. Falls out of this if-block and never reaches
            // the canned "I hear you" re-prompt.
          } else {
            await sayBodhi(
              `Before I can help further I do need to know: does ${cur.student_identifier || "the student"} have an IEP, 504, or BIP?`,
            );
            window.setTimeout(() => goto("iep504"), 400);
            return;
          }
        }
      }
      // For everything except the "admin also gave us the iep504 answer"
      // case, we're done — return. The iep504-with-answer case falls through
      // so the main handler below processes it.
      if (curStep !== "iep504") return;
      // (fall through to iep504 handler)
    }

    if (curStep === "welcome") {
      // RESUMED INCIDENT BRANCH:
      // If the chat just rendered a "Welcome back to {student}'s incident"
      // header (i.e. the incident already has captured data), free text
      // here is the leader adding more context to the existing record —
      // not answering the first-time regulation greeting. Without this
      // guard the message would fall through to the cold-start flow and
      // re-ask for the student's name/grade we already have.
      if (incidentHasData(cur)) {
        const studentName = cur.student_identifier?.trim() || "this student";
        // Append the new detail to incident_description so it shows up in
        // the Situation card and is part of every downstream document /
        // re-synthesis. Tag it "Additional context (added later)" so the
        // record is honest about when the detail came in.
        const existingDesc = cur.incident_description?.trim() ?? "";
        const updatedDesc = existingDesc
          ? `${existingDesc}\n\nAdditional context (added later): ${text}`
          : text;
        onUpdateIncident({ incident_description: updatedDesc });

        // Optional ask-mentor read so the leader gets the same kind of
        // synthesis they'd have gotten the first time around — but framed
        // as "given what we already have + this new detail".
        const mentorPrompt =
          `An admin is updating an in-progress behavior incident with new context. Existing record: student ${studentName}` +
          (cur.grade_level ? ` (Grade ${cur.grade_level})` : "") +
          `. Original description: "${existingDesc || "(none)"}". They just added: "${text}". Briefly (2-3 sentences max) reflect what this changes about the read or what it doesn't — no re-asking for basics, no full restart. Then point them at one concrete next move from the work that's already in progress.`;
        const mentorResult = await askMentor(mentorPrompt);
        if (mentorResult && "html" in mentorResult) {
          append({ kind: "bodhi", id: genId(), html: mentorResult.html });
        } else {
          await sayBodhi(
            `Got it — logged that as additional context on ${studentName}'s incident.`,
          );
        }

        // Land them at the natural resume point so the next prompt is
        // "what to do next on this case", not "tell me about a student".
        const resume = resumeStepFor(cur);
        setStep(resume);
        setPlaceholder("Add more detail or ask me where you are…");
        return;
      }

      // The welcome prompt literally asks "how are you doing right now?" — so
      // whatever the admin types is an answer to THAT. We should respond to
      // it, not skip past it. Route through the mentor for a genuine, 2-3
      // sentence acknowledgment, then pivot to asking what happened.
      //
      // If the mentor-help detector didn't already catch it above, do it now.
      // Any free-text welcome answer longer than a one-word "ready" deserves
      // a warm response. Very short stock replies ("ok", "ready", "fine")
      // flow straight to the situation step.
      const lo = text.toLowerCase().trim();
      const isShortOK = /^(ok(ay)?|ready|sure|fine|yep|yes|yeah|good|all good|i'?m good|let'?s go|go|done)[.! ]*$/i.test(lo);
      if (isShortOK) {
        window.setTimeout(() => goto("situation"), 350);
        return;
      }

      // Tier 1 / Step B — auto-extraction takeover (cold start).
      //
      // The leader skipped the regulation greeting and dove straight into
      // describing the case ("Sarah, 3rd grade, was throwing chairs"). The
      // classifier already extracted the student name / grade / safety
      // signals from this turn — capture them now and treat the message as
      // the situation narrative, instead of asking again in the next step.
      // Guarded by a presence-of-name OR safety signal so a pure emotional
      // venture ("I'm overwhelmed today") still falls through to the warm
      // empathy reply below.
      const ai = applyAiExtractedFacts(verdict, cur);
      const accommodations = detectAccommodationSignals(text);
      const dovedIn =
        !!ai.updates.student_identifier || !!ai.updates.safety_check;
      if (dovedIn) {
        const finalUpdates: Partial<Incident> = {
          ...ai.updates,
          incident_description: text,
          ...(accommodations.hasAny ? { iep_504_flag: true } : {}),
        };
        onUpdateIncident(finalUpdates);
        if (ai.chips.length) {
          append({ kind: "data-card", id: genId(), chips: ai.chips });
        }
          if (accommodations.hasAny) {
            append({
              kind: "data-card",
              id: genId(),
              chips: [{ key: "Plans", value: accommodations.chipValue }],
            });
            // Surface the federal-guidance card. `accommodations.plan` is
            // null when only a BIP was mentioned — in that case we still
            // want the leader to see the BIP-specific guidance.
            ensureFederalGuidanceShown(
              accommodations.plan ?? (accommodations.hasBip ? "bip" : "iep"),
            );
            const existing = cur.conversation_notes?.trim() ?? "";
            const note = `IEP/504/BIP context captured from intake: ${text}`;
            onUpdateIncident({
              conversation_notes: existing ? `${note}\n\n${existing}` : note,
            });
          }
          // Brief mentor read of what we just heard so the leader feels
        // engaged with, not auto-categorized. Mentor unavailable → fall
        // back to a short canned acknowledgment.
        const studentName = ai.updates.student_identifier ?? "this student";
        const gradeBit = ai.updates.grade_level
          ? `, grade=${ai.updates.grade_level}`
          : "";
        const mentorPrompt =
          `An admin just opened a behavior incident with: "${text}". ` +
          `In 2-3 sentences max, briefly reflect what stands out and what you're noticing as the read — no full restart, no re-asking for basics already given (student=${studentName}${gradeBit}). End with one short line about what they should think about next.`;
        const mentorResult = await askMentor(mentorPrompt);
        if (mentorResult && "html" in mentorResult) {
          append({ kind: "bodhi", id: genId(), html: mentorResult.html });
        } else {
          await sayBodhi(`Got it — captured what you shared on **${studentName}**.`);
        }
        const haveGrade = !!(ai.updates.grade_level || cur.grade_level);
        window.setTimeout(
          () => goto(haveGrade ? (accommodations.hasAny ? "iep504Refine" : "iep504") : "askGrade"),
          400,
        );
        return;
      }

      const result = await askMentor(text);
      if (result && "html" in result) {
        append({ kind: "bodhi", id: genId(), html: result.html });
      } else {
        // Mentor unreachable — fall back to the old canned response so the
        // admin never gets stuck.
        await sayBodhi(
          "That makes sense. You're holding something real — and the fact that it affected you means you care deeply about this work. Take a breath.",
        );
      }
      await new Promise((r) => window.setTimeout(r, 900));
      await sayBodhi(
        "When you're ready — tell me what happened. Who's the student, what grade, and what did they do?",
      );
      setPlaceholder(
        "e.g. Marcus, 7th grade, got into a fight in the hallway after lunch...",
      );
      setStep("situation");
      return;
    }

    if (curStep === "situation") {
      const ex = extractFromSituation(text, cur);

      const hasAnyFacts =
        !!ex.student_identifier ||
        !!ex.grade_level ||
        !!ex.location ||
        !!ex.safety_check ||
        ex.repeat_incident !== undefined;

      // Relational / emotional openings that deserve an empathetic reply
      // before we continue ("my own kid", "i can't…", "shook me").
      const isRelational = /\b(my (own )?(kid|child|son|daughter)|i (can'?t|don'?t) (believe|handle|do this)|shook me|breaking down|gutted|heart.? ?broken|exhausted|at my limit)\b/i
        .test(text);

      // Always persist facts FIRST if we got any — so a later correction
      // ("the kid is Bob") doesn't get thrown away just because the same
      // message also contained emotional content.
      if (hasAnyFacts) {
        const fields: Partial<Incident> = {};
        const accommodations = detectAccommodationSignals(text);
        if (ex.student_identifier) fields.student_identifier = ex.student_identifier;
        if (ex.grade_level) fields.grade_level = ex.grade_level;
        if (ex.repeat_incident !== undefined) fields.repeat_incident = ex.repeat_incident;
        if (ex.safety_check) fields.safety_check = ex.safety_check;
        if (accommodations.hasAny) fields.iep_504_flag = true;
        const desc = ex.incident_description ?? text;
        fields.incident_description = ex.location
          ? `${desc} (location: ${ex.location})`
          : desc;
        onUpdateIncident(fields);

        const chips: { key: string; value: string }[] = [];
        if (ex.student_identifier) chips.push({ key: "Student", value: ex.student_identifier });
        if (ex.grade_level) chips.push({ key: "Grade", value: ex.grade_level });
        if (ex.location) chips.push({ key: "Location", value: ex.location });
        if (ex.repeat_incident) chips.push({ key: "Repeat", value: "Yes" });
        if (ex.safety_check === "pause_needed")
          chips.push({ key: "Safety", value: "Pause needed" });
        if (accommodations.hasAny) chips.push({ key: "Plans", value: accommodations.chipValue });
        if (chips.length) append({ kind: "data-card", id: genId(), chips });
        if (accommodations.hasAny) {
          ensureFederalGuidanceShown(
            accommodations.plan ?? (accommodations.hasBip ? "bip" : "iep"),
          );
        }
        if (accommodations.hasAny) {
          const existing = cur.conversation_notes?.trim() ?? "";
          const note = `IEP/504/BIP context captured from situation: ${text}`;
          onUpdateIncident({
            conversation_notes: existing ? `${note}\n\n${existing}` : note,
          });
        }
      }

      // If the message was relational OR we got zero facts, route through
      // the mentor for an empathetic response. Then either re-ask (no
      // facts) or advance (got the facts, just needed to acknowledge the
      // emotion first).
      if (!hasAnyFacts || isRelational) {
        const result = await askMentor(text);
        if (result && "html" in result) {
          append({ kind: "bodhi", id: genId(), html: result.html });
        } else {
          await sayBodhi(
            "I hear you — that sounds rough. I'm with you.",
          );
        }
        await new Promise((r) => window.setTimeout(r, 900));

        if (!hasAnyFacts) {
          await sayBodhi(
            "To help you, I still need the basics: **who's the student, what grade, and what exactly did they do?**",
          );
          setPlaceholder(
            "e.g. Ethan, 2nd grade, hit another student on the playground...",
          );
          return;
        }
        // Facts present + emotional content → advance, don't re-ask.
      }

      const gradeMentioned = !!ex.grade_level;
      const accommodations = detectAccommodationSignals(text);
      window.setTimeout(
        () =>
          goto(
            gradeMentioned || isGradeSet(cur.grade_level)
              ? accommodations.hasAny
                ? "iep504Refine"
                : "iep504"
              : "askGrade",
          ),
        400,
      );
      return;
    }

    if (curStep === "askGrade") {
      // Accept the natural ways a person types a grade: "5th", "5th grade",
      // "grade 5", "5 th", plain "5", "fifth", "K", "kindergarten". The old
      // regex list required either "grade" as a prefix/suffix OR a bare
      // integer, which meant "5th" (the most common typed answer) failed all
      // four patterns and dumped the user into the canned "I still just
      // need…" fallback.
      const lo = text.trim();
      const wordNum: Record<string, string> = {
        kindergarten: "K", kinder: "K", k: "K",
        first: "1", second: "2", third: "3", fourth: "4", fifth: "5",
        sixth: "6", seventh: "7", eighth: "8", ninth: "9",
        tenth: "10", eleventh: "11", twelfth: "12", senior: "12",
      };
      const wordHit = lo.toLowerCase().match(
        /\b(kindergarten|kinder|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth|senior)\b/,
      );
      const gradeMatch =
        (wordHit ? ([0, wordNum[wordHit[1]]] as unknown as RegExpMatchArray) : null) ||
        lo.match(/\b(?:grade\s*)(\d{1,2})\b/i) ||
        lo.match(/\b(\d{1,2})\s*(?:st|nd|rd|th)\b/i) ||
        lo.match(/\b(K|kinder(?:garten)?)\b/i) ||
        lo.match(/^(\d{1,2})$/) ||
        lo.match(/\b(\d{1,2})\b/) ||
        null;
      let grade: GradeLevel | null = null;
      if (gradeMatch) {
        const raw = gradeMatch[1];
        grade = /^k/i.test(raw) ? "K-2" : toGradeLevel(raw);
      }
      // Tier 1 / Step B fallback — if the regex couldn't parse a grade
      // ("elementary school", "she's a little one", "high schooler") but
      // the classifier did, trust the AI's mapped bucket.
      if (
        !grade &&
        verdict &&
        verdict.confidence >= CLASSIFIER_CONFIDENCE_FLOOR &&
        verdict.extracted.grade_level
      ) {
        grade = verdict.extracted.grade_level;
      }
      if (grade) {
        onUpdateIncident({ grade_level: grade });
        append({
          kind: "data-card",
          id: genId(),
          chips: [{ key: "Grade", value: grade }],
        });
        window.setTimeout(() => goto(cur.iep_504_flag ? "iep504Refine" : "iep504"), 400);
        return;
      }

      // No grade parsed. Don't silently advance — the admin said something
      // that isn't a grade. Acknowledge what they said (Claude-backed when we
      // can reach it) and re-ask for the grade without judgment.
      const result = await askMentor(text);
      if (result && "html" in result) {
        append({ kind: "bodhi", id: genId(), html: result.html });
      } else {
        await sayBodhi("I hear you.");
      }
      await new Promise((r) => window.setTimeout(r, 700));
      await sayBodhi(
        `I still just need ${cur.student_identifier || "the student"}'s grade level so I can match what comes next to their developmental stage. Ballpark is fine — K, 2nd, 7th, 10th…`,
      );
      setPlaceholder("Grade (e.g. K, 2nd, 7th, 10th)...");
      return;
    }

    if (curStep === "iep504") {
      const lo = text.toLowerCase();
      const mentionsIep = /\biep\b/.test(lo);
      const mentions504 = /\b504\b/.test(lo);
      const mentionsBip = /\bbip\b|\bbehavior intervention plan\b/.test(lo);
      // Tier 1 / Step C — resolve yes/no via the classifier when it's
      // confident, falling back to the regex parseYesNo otherwise. The
      // regex misses informal phrasings ("yeah, kinda", "I think so",
      // "not really, no") and locks on the wrong word when the answer
      // begins with a "no" or "yes" that was actually a hedge.
      const yn = resolveYesNo(text, verdict);
      const isYes = mentionsIep || mentions504 || mentionsBip || yn === "yes";
      const isNo = !isYes && yn === "no";

      if (isYes) {
        const plan: "iep" | "504" | "both" | "bip" =
          mentionsIep && mentions504
            ? "both"
            : mentions504
              ? "504"
              : mentionsIep
                ? "iep"
                : "bip";
        onUpdateIncident({ iep_504_flag: true });
        if (plan === "bip") {
          append({
            kind: "data-card",
            id: genId(),
            chips: [{ key: "Plans", value: "BIP" }],
          });
        }
        ensureFederalGuidanceShown(plan);
        const existing = cur.conversation_notes?.trim() ?? "";
        const note = `IEP/504 context noted: ${text}`;
        onUpdateIncident({
          conversation_notes: existing ? `${note}\n\n${existing}` : note,
        });
      } else if (isNo) {
        onUpdateIncident({ iep_504_flag: false });
      } else {
        const existing = cur.conversation_notes?.trim() ?? "";
        const note = `IEP/504 status: needs verification (user note: ${text})`;
        onUpdateIncident({
          conversation_notes: existing ? `${note}\n\n${existing}` : note,
        });
      }
      window.setTimeout(() => goto("askContext"), 400);
      return;
    }

    if (curStep === "iep504Refine") {
      const sig = detectAccommodationSignals(text);
      const existing = cur.conversation_notes?.trim() ?? "";
      if (sig.hasAny) {
        onUpdateIncident({ iep_504_flag: true });
        append({
          kind: "data-card",
          id: genId(),
          chips: [{ key: "Plans", value: sig.chipValue }],
        });
        ensureFederalGuidanceShown(
          sig.plan ?? (sig.hasBip ? "bip" : "iep"),
        );
        const note = `Additional plan context: ${text}`;
        onUpdateIncident({
          conversation_notes: existing ? `${note}\n\n${existing}` : note,
        });
      } else if (resolveYesNo(text, verdict) === "unsure") {
        const note = `IEP/504/BIP status: needs verification (user note: ${text})`;
        onUpdateIncident({
          conversation_notes: existing ? `${note}\n\n${existing}` : note,
        });
      } else {
        const note = `Plan supports noted: ${text}`;
        onUpdateIncident({
          iep_504_flag: true,
          conversation_notes: existing ? `${note}\n\n${existing}` : note,
        });
      }
      window.setTimeout(() => goto("askContext"), 400);
      return;
    }

    if (curStep === "askContext") {
      if (isContinueIntent(text)) {
        window.setTimeout(() => goto("askPrior"), 250);
        return;
      }
      if (isWorkflowControlUtterance(text)) {
        await sayBodhi(
          "I'm with you. For this step, I just need what happened right before the behavior (or choose Skip).",
        );
        offerQuickReplies([
          { label: "Came out of nowhere", next: "askPrior", payload: "out_of_nowhere" },
          { label: "Building all day / week", next: "askPrior", payload: "buildup" },
          { label: "Specific trigger — I'll describe", next: "askPrior", payload: "specific" },
          { label: "Skip this — continue", next: "askPrior", payload: "skip", isAction: true },
        ]);
        return;
      }
      if (isVagueStructuredReply("askContext", text)) {
        const existing = cur.conversation_notes?.trim() ?? "";
        const note = `Q (askContext): ${text}`;
        onUpdateIncident({
          conversation_notes: existing ? `${note}\n\n${existing}` : note,
        });
        const result = await askMentor(
          `I need help getting clearer trigger/context detail before I move on. Here's what I typed: "${text}". ` +
            "Give me 2-3 likely antecedent frames to check and one exact sentence I can use with the teacher/student to pin down what happened right before the behavior.",
        );
        if (result && "html" in result) {
          append({ kind: "bodhi", id: genId(), html: result.html });
        } else {
          await sayBodhi(
            "Let's tighten the context first. Name what happened right before the behavior (trigger, transition, demand, peer/adult interaction, or setting).",
          );
        }
        offerQuickReplies([
          { label: "Specific trigger — I'll describe", next: "askPrior", payload: "specific" },
          { label: "Building all day / week", next: "askPrior", payload: "buildup" },
          { label: "Came out of nowhere", next: "askPrior", payload: "out_of_nowhere" },
          { label: "Skip this — continue", next: "askPrior" },
        ]);
        setPlaceholder(
          "What happened right before? (transition, demand, peer conflict, setting, timing...)",
        );
        return;
      }
      const existing = cur.conversation_notes?.trim() ?? "";
      const note = `Trigger/context: ${text}`;
      onUpdateIncident({
        conversation_notes: existing ? `${note}\n\n${existing}` : note,
      });
      window.setTimeout(() => goto("askPrior"), 300);
      return;
    }

    if (curStep === "askPrior") {
      if (isContinueIntent(text)) {
        window.setTimeout(() => goto("askTeacherAsk"), 250);
        return;
      }
      if (isWorkflowControlUtterance(text)) {
        await sayBodhi(
          "I can keep moving, but first tell me what was already tried (or choose Done — continue).",
        );
        offerQuickReplies([
          { label: "I'll list top 3 attempts", next: "askPrior" },
          { label: "Use selected checkboxes only", next: "askPrior" },
          { label: "Nothing yet — first time", next: "askTeacherAsk", payload: "none" },
          { label: "Done — continue →", next: "askTeacherAsk", payload: "continue" },
        ]);
        return;
      }
      if (isVagueStructuredReply("askPrior", text)) {
        const existing = cur.conversation_notes?.trim() ?? "";
        const note = `Q (askPrior): ${text}`;
        onUpdateIncident({
          conversation_notes: existing ? `${existing}\n\n${note}` : note,
        });
        const result = await askMentor(
          `I need help clarifying what was already tried before referral. Here's what I typed: "${text}". ` +
            "Coach me to capture the top 3 attempts and likely outcomes in a way I can document quickly.",
        );
        if (result && "html" in result) {
          append({ kind: "bodhi", id: genId(), html: result.html });
        } else {
          await sayBodhi(
            "Helpful next move: list the top 2-3 things already tried and what happened after each one.",
          );
        }
        offerQuickReplies([
          { label: "I'll list top 3 attempts", next: "askPrior" },
          { label: "Use selected checkboxes only", next: "askPrior" },
          { label: "Nothing yet — first time", next: "askTeacherAsk", payload: "none" },
          { label: "Done — continue →", next: "askTeacherAsk", payload: "continue" },
        ]);
        setPlaceholder(
          "e.g. Redirections, break offered, parent call — student escalated after each",
        );
        return;
      }
      if (/^\s*nothing yet\b|^\s*first time\b/i.test(text)) {
        onUpdateIncident({ prior_interventions: "Nothing yet — first time referral" });
        window.setTimeout(() => goto("askTeacherAsk"), 300);
        return;
      }
      const existingFree = cur.prior_interventions?.trim() ?? "";
      const nextFree = existingFree ? `${existingFree}\n${text}` : text;
      onUpdateIncident({ prior_interventions: nextFree });
      window.setTimeout(() => goto("askTeacherAsk"), 300);
      return;
    }

    if (curStep === "askTeacherAsk") {
      if (isContinueIntent(text)) {
        window.setTimeout(() => goto("understand"), 250);
        return;
      }
      const teacherAskMultiChips: QuickReply[] = [
        { label: "Reset & back to class", next: "askTeacherAsk", payload: "reset", multi: true },
        { label: "Formal documentation", next: "askTeacherAsk", payload: "formal_doc", multi: true },
        { label: "Admin conversation", next: "askTeacherAsk", payload: "admin_convo", multi: true },
        { label: "Parent contact requested", next: "askTeacherAsk", payload: "parent_loop", multi: true },
        { label: "Counseling referral", next: "askTeacherAsk", payload: "counseling", multi: true },
        { label: "Done — continue →", next: "understand", payload: "continue", isAction: true },
        { label: "Not sure — continue", next: "understand", payload: "unsure", isAction: true },
      ];
      if (isWorkflowControlUtterance(text)) {
        await sayBodhi(
          "Before we move on, what is the teacher specifically asking for? Pick all that apply, type more, or hit **Done — continue**.",
        );
        offerQuickReplies(teacherAskMultiChips);
        return;
      }
      if (isVagueStructuredReply("askTeacherAsk", text)) {
        const existing = cur.conversation_notes?.trim() ?? "";
        const note = `Q (askTeacherAsk): ${text}`;
        onUpdateIncident({
          conversation_notes: existing ? `${existing}\n\n${note}` : note,
        });
        const result = await askMentor(
          `I need to clarify what the teacher is asking for from this referral. Here's my current answer: "${text}". ` +
            "Translate this into concrete outcome options and one sentence I can use to confirm the teacher's real ask.",
        );
        if (result && "html" in result) {
          append({ kind: "bodhi", id: genId(), html: result.html });
        } else {
          await sayBodhi(
            "Let's make it concrete: is the teacher asking for reset/re-entry support, formal documentation, parent contact, counseling, or admin follow-up? You can pick more than one.",
          );
        }
        offerQuickReplies(teacherAskMultiChips);
        setPlaceholder("Add another ask (type one per line) or pick chips above…");
        return;
      }
      // Append the typed ask to the running free-text field. Multiple
      // typed lines are kept on separate lines so the AI can see each
      // one as a distinct request when shaping downstream materials.
      const existingFree = cur.teacher_requested_outcome?.trim() ?? "";
      const nextFree = existingFree ? `${existingFree}\n${text}` : text;
      onUpdateIncident({ teacher_requested_outcome: nextFree });
      await sayBodhi(
        "Got it. **Add more if there's more**, or hit **Done — continue** when you're finished.",
      );
      offerQuickReplies(teacherAskMultiChips);
      setPlaceholder("Type another ask, or pick a chip above…");
      return;
    }

    if (curStep === "understand") {
      if (isContinueIntent(text)) {
        window.setTimeout(() => goto("genOutputs"), 250);
        return;
      }
      const understandMultiChips: QuickReply[] = [
        { label: "Seeking connection / attention", next: "understand", payload: "seeking_connection", multi: true },
        { label: "Escape or avoid something hard", next: "understand", payload: "escape", multi: true },
        { label: "Needs sensory regulation", next: "understand", payload: "sensory", multi: true },
        { label: "Big emotions they can't name", next: "understand", payload: "emotional", multi: true },
        { label: "Power / autonomy struggle", next: "understand", payload: "power", multi: true },
        { label: "Peer conflict / social pressure", next: "understand", payload: "peer", multi: true },
        { label: "💡 Want my read?", next: "understand", payload: "ai_read", isAction: true },
        { label: "Done — continue →", next: "genOutputs", payload: "continue", isAction: true },
        { label: "Not sure — generate the materials", next: "genOutputs", payload: "unsure", isAction: true },
      ];
      // Append each typed read as its own `Motivation:` line so multiple
      // hypotheses can stack alongside any chips the leader has toggled.
      const existing = cur.conversation_notes?.trim() ?? "";
      const prefix = `Motivation: ${text}`;
      const nextNotes = existing ? `${prefix}\n\n${existing}` : prefix;
      onUpdateIncident({ conversation_notes: nextNotes });
      await sayBodhi(
        "That's a real and honest read.\n\n**Add more if there are other layers**, or hit **Done — continue** to generate materials.",
      );
      offerQuickReplies(understandMultiChips);
      setPlaceholder(`Type another read on ${cur.student_identifier || "the student"}, or pick a chip above…`);
      return;
    }

    // genOutputs (phase A) — the admin has the reflection form + due-process
    // card in front of them and is either asking a question, reflecting on
    // the student, or noting something new. There's no form field to fill
    // here, so anything they type should get a real mentor response, and the
    // phase-A chips need to stay in view so there's always a clear next move.
    if (curStep === "genOutputs") {
      const hasReflectionCard = messagesRef.current.some((m) => m.kind === "out-reflection");
      const hasDueProcessCard = messagesRef.current.some((m) => m.kind === "out-due-process");
      const hasGuideCard = messagesRef.current.some((m) => m.kind === "out-coaching");
      const hasPhaseA = hasReflectionCard && hasDueProcessCard;

      if (isFormsLocationQuestion(text)) {
        if (hasPhaseA) {
          await sayBodhi(
            "Your reflection form and due-process check are already in this chat thread above. Scroll up a bit and open the **Reflection form** card. If you want the conversation guide next, tap **Show me the conversation guide**.",
          );
        } else {
          await sayBodhi(
            "I haven't posted the student forms in chat yet. Tap **⚡ Just generate the forms now** (or ask me to generate now) and I'll post them here.",
          );
        }
        offerQuickReplies([
          { label: "⚡ Just generate the forms now", next: "genOutputs", payload: "force_generate_forms", isAction: true },
          { label: "Show me the conversation guide", next: "genOutputs", payload: "show_guide" },
          { label: "✨ What am I missing?", next: "genOutputs", payload: "check_missing" },
          { label: "💬 AI Advice", next: "genOutputs", askMentor: true },
        ]);
        setPlaceholder("Ask another question, or choose the next action above…");
        return;
      }

      if (isGenerateFormsRequest(text)) {
        if (hasPhaseA) {
          await sayBodhi(
            hasGuideCard
              ? "The forms and conversation guide are already on this thread above."
              : "The student forms are already on this thread above. Tap **Show me the conversation guide** when you're ready.",
          );
          offerQuickReplies([
            { label: "Show me the conversation guide", next: "genOutputs", payload: "show_guide" },
            { label: "→ I had the conversation, log it", next: "postConversation", isAction: true },
          ]);
          return;
        }
        await goto("genOutputs");
        return;
      }

      if (isContinueIntent(text) || isWorkflowControlUtterance(text)) {
        await sayBodhi(
          "You're at the forms step. Choose one: generate/review forms, open the conversation guide, or log what happened after your conversation.",
        );
        offerQuickReplies([
          { label: "⚡ Just generate the forms now", next: "genOutputs", payload: "force_generate_forms", isAction: true },
          { label: "Show me the conversation guide", next: "genOutputs", payload: "show_guide" },
          { label: "→ I had the conversation, log it", next: "postConversation", isAction: true },
          { label: "💬 AI Advice", next: "genOutputs", askMentor: true },
        ]);
        setPlaceholder("Tell me what you want to do next at this step…");
        return;
      }

      const existing = cur.conversation_notes?.trim() ?? "";
      const noteLine = `Q (genOutputs): ${text}`;
      onUpdateIncident({
        conversation_notes: existing ? `${existing}\n\n${noteLine}` : noteLine,
      });
      const result = await askMentor(text);
      if (result && "html" in result) {
        append({ kind: "bodhi", id: genId(), html: result.html });
      } else {
        const detail = result && "error" in result ? result.error : "";
        await sayBodhi(
          `I couldn't reach the mentor just now${
            detail ? ` (${detail})` : ""
          }. Try again in a moment.`,
        );
      }
      // Re-offer phase-A chips so there's always a visible path forward.
      // We also include a forward-progression chip so the leader never has
      // to guess what to type when they're done coaching and ready to log
      // what actually happened.
      await new Promise((r) => window.setTimeout(r, 300));
      offerQuickReplies([
        { label: "→ I had the conversation, log it", next: "postConversation", isAction: true },
        { label: "Show me the conversation guide", next: "genOutputs", payload: "show_guide" },
        { label: "✨ What am I missing?", next: "genOutputs", payload: "check_missing" },
        {
          label: "💬 AI Advice",
          next: "genOutputs",
          askMentor: true,
          payload:
            "Before I go into the conversation, help me think through what's most important to say and how to frame it for this student.",
        },
      ]);
      setPlaceholder("Or ask another question / share what happened…");
      return;
    }

    if (curStep === "postConversation") {
      const existing = cur.conversation_notes?.trim() ?? "";
      const note = `Conversation notes: ${text}`;
      onUpdateIncident({
        conversation_notes: existing ? `${existing}\n\n${note}` : note,
        conversation_completed: true,
      });
      window.setTimeout(() => goto("resolution"), 300);
      return;
    }

    if (curStep === "resolution") {
      const existing = cur.conversation_notes?.trim() ?? "";
      const nextNotes = existing ? `${existing}\n\nOutcome: ${text}` : `Outcome: ${text}`;
      onUpdateIncident({
        conversation_notes: nextNotes,
        behavior_follow_up: text,
        conversation_completed: true,
        status: "followup",
      });
      // The debrief used to fire here, before the leader had answered the
      // "did this require extended time out of class?" question. That made
      // the debrief land while we still didn't know the disposition. We now
      // hold the debrief until askExtended is answered (see below).
      window.setTimeout(() => goto("askExtended"), 400);
      return;
    }

    if (curStep === "askExtended") {
      const ex = extractExtendedSupport(text);
      let nextStep: StepId;
      if (ex) {
        onUpdateIncident({ removal_type: ex.removal_type });
        // Removal paths run through issDocs first; in-class paths skip
        // straight to teacher follow-up. Either way, the debrief is held
        // until teacherFollowUp completes so it reads as a true wrap-up.
        nextStep = ex.needs_iss ? "issDocs" : "teacherFollowUp";
      } else {
        const existing = cur.conversation_notes?.trim() ?? "";
        onUpdateIncident({
          conversation_notes: existing ? `${existing}\n\nRemoval context: ${text}` : `Removal context: ${text}`,
        });
        nextStep = "teacherFollowUp";
      }
      window.setTimeout(() => goto(nextStep), 300);
      return;
    }

    if (curStep === "teacherFollowUp") {
      // Free-form teacher follow-up note. Logged with the same prefix the
      // chip handler uses so the IncidentSummaryCard surfaces it
      // regardless of how it was captured.
      const existing = cur.conversation_notes?.trim() ?? "";
      const noteLine = `Teacher follow-up: ${text}`;
      onUpdateIncident({
        conversation_notes: existing ? `${existing}\n\n${noteLine}` : noteLine,
      });
      await sayBodhi(`Got it — logged the teacher follow-up plan.`);
      // Wrap-up moment — run the debrief before docOffer so it reflects
      // the full picture (situation → conversation → disposition →
      // teacher follow-up) instead of a partial cut.
      await runDebriefBlocking(cur.behavior_follow_up || text);
      window.setTimeout(() => goto("docOffer"), 300);
      return;
    }

    if (curStep === "handoff") {
      // Free-form handoff note. Persist who/when so the next person knows
      // where things stand, mark the incident paused, and bounce to the
      // dashboard so a colleague can pick it up cleanly.
      const stamp = new Date().toLocaleString();
      const existing = cur.conversation_notes?.trim() ?? "";
      const noteLine = `Handoff note (${currentUserName} · ${stamp}): ${text}`;
      onUpdateIncident({
        conversation_notes: existing ? `${existing}\n\n${noteLine}` : noteLine,
        status: "paused",
      });
      await sayBodhi(
        `Saved. Anyone with access can pick this up — the **What we have so far** panel and your handoff note will be the first thing they see.`,
      );
      toast.success("Saved for handoff", {
        description: "Returning you to the dashboard.",
      });
      window.setTimeout(() => onBackToDashboard(), 600);
      return;
    }

    if (curStep === "issDocs") {
      const existing = cur.conversation_notes?.trim() ?? "";
      onUpdateIncident({
        conversation_notes: existing ? `${existing}\n\nISS/Re-entry note: ${text}` : `ISS/Re-entry note: ${text}`,
      });
      await sayBodhi(`Added that to the record.`);
      // Re-anchor the next move so the leader isn't left with just an
      // empty input and a card. issDocs has its own resume chips
      // (continue to teacher follow-up) that we surface here.
      offerStepResumeChips("issDocs");
      return;
    }

    if (curStep === "docOffer") {
      // Tier 1 / Step C — same AI-first yes/no resolution as iep504,
      // so "go for it" / "sure" / "nah, not yet" / "I think so" all
      // route correctly instead of getting filed as a free-form note.
      const yn = resolveYesNo(text, verdict);
      if (yn === "yes") {
        window.setTimeout(() => goto("genDoc"), 300);
      } else if (yn === "no") {
        window.setTimeout(() => goto("finish"), 300);
      } else {
        const existing = cur.conversation_notes?.trim() ?? "";
        onUpdateIncident({
          conversation_notes: existing ? `${existing}\n\n${text}` : text,
        });
        window.setTimeout(() => goto("genDoc"), 300);
      }
      return;
    }

    if (curStep === "finish") {
      const existing = cur.conversation_notes?.trim() ?? "";
      onUpdateIncident({
        conversation_notes: existing ? `${existing}\n\n${text}` : text,
      });
      await sayBodhi(`Added that to the record.`);
      // The incident is otherwise complete; surface the wrap-up paths so
      // the leader is never stranded after a final note. They can save
      // for handoff, ask for advice, or open the documentation panel
      // straight from the completion card above.
      offerStepResumeChips("finish");
      return;
    }

    // ── Safety net ──────────────────────────────────────────────────────
    // If we got here, no per-step branch claimed this input. Without a
    // catch-all the leader would type something and get nothing back —
    // exactly the dead-end pattern leaders flagged. Log the orphan turn
    // to the record so it isn't lost, acknowledge it, and re-anchor with
    // the current step's resume chips so the next move is always one
    // tap away.
    {
      console.warn(
        `[RestorationRoomChat] no branch handled curStep="${curStep}" — falling back to safety-net response.`,
      );
      const existing = cur.conversation_notes?.trim() ?? "";
      const orphanLine = `Note (${curStep}): ${text}`;
      onUpdateIncident({
        conversation_notes: existing ? `${existing}\n\n${orphanLine}` : orphanLine,
      });
      await sayBodhi(
        "I added that to the record. Pick the next move below — I'll keep us moving forward until the incident is wrapped.",
      );
      offerStepResumeChips(curStep);
      setPlaceholder("Or type the next thing you need…");
      return;
    }
  };

  // ── Due process toggle ──────────────────────────────────────────────────
  const handleDueProcess = () => {
    if (isReadOnly) return;
    const next = !incident.due_process_acknowledged;
    onUpdateIncident({
      due_process_acknowledged: next,
      due_process_timestamp: next ? new Date().toLocaleString() : "",
    });
  };

  const currentStage = useMemo(() => stageForStep(step), [step]);

  // Visual "active stage" for the tracker / progress bar / summary card.
  //
  // `currentStage` is the script's position (derived from `step`). That can
  // legitimately get ahead of the data — e.g. on a resumed incident where
  // late-stage flags exist but the basic Situation fields don't satisfy
  // `getStageCompletion`. In that case the chevrons would highlight, say,
  // "Resolution" while the summary headline says "currently on The
  // Situation", and the progress bar would freeze at a high step number
  // even as the leader fills in earlier info.
  //
  // We clamp the visual active stage to the FIRST incomplete stage by
  // completion, but only when the script has run ahead of the data. If
  // the script is at or behind the first incomplete stage (the normal
  // forward-march case, and review mode), we keep the script's position
  // so the visuals follow what the leader is actually working on.
  const effectiveActiveStage = useMemo<ChatTabStage>(
    () => clampStageToData(currentStage, incident),
    [currentStage, incident],
  );

  // "Continue from where I left off" — used by the summary card and by
  // the process-orientation Q&A. Jumps to the natural resume step
  // computed from the captured state.
  const handleContinueFromHere = useCallback(() => {
    if (isReadOnly) return;
    const inc = incidentRef.current;
    const target = resumeStepFor(inc);
    const targetStage = stageForStep(target);
    void (async () => {
      // If the leader is currently parked in review mode, drop them back
      // at the natural resume point WITHOUT re-running its scripted
      // prompt — they're "continuing", not re-answering.
      if (reviewStageRef.current) {
        reviewStageRef.current = null;
        setStep(target);
        await sayBodhi(
          `Picking up at **${stageLabelFor(targetStage)}**. Same notes, no re-answers.`,
        );
        // We deliberately don't replay the step's scripted prompt here
        // ("no re-answers"), but without chips the leader is staring at
        // an empty input with no obvious move. Surface the same resume
        // chips the rest of the flow uses.
        offerStepResumeChips(target);
        return;
      }
      if (targetStage !== stageForStep(stepRef.current)) {
        await sayBodhi(`Picking up at **${stageLabelFor(targetStage)}** — same notes, no need to re-answer anything.`);
      }
      goto(target);
    })();
  }, [goto, sayBodhi, isReadOnly]);

  // Handler shared by the in-chat StageTracker and the summary card.
  // Same gating rules as the imperative jumpToStage exposed to the parent
  // — completed stages can be revisited, future stages are locked until
  // prerequisites are met. COMPLETED stages enter "review mode" instead
  // of re-running the scripted prompts: the leader sees what was captured,
  // can add a note, and can jump back to where they were — no
  // re-interrogation. Forward jumps to as-yet-uncaptured stages run the
  // normal scripted entry.
  const handleJumpToStage = useCallback(
    (stage: ChatTabStage) => {
      if (isReadOnly) return;
      const inc = incidentRef.current;
      const stages = [
        ...STAGE_META,
        ...(showsExtendedStage(inc) ? [EXTENDED_STAGE] : []),
      ];
      const completion = getStageCompletion(inc);
      const previousActive = stageForStep(stepRef.current);
      if (stage === previousActive) return;
      if (!isStageReachable(stage, previousActive, completion, stages)) {
        toast.info("Finish the current step first.", {
          description: "You can come back here once the prior steps are done.",
        });
        return;
      }
      const label: Record<ChatTabStage, string> = {
        incident: "The Situation",
        reflection: "Understand",
        conversation: "The Conversation",
        resolution: "Resolution",
        extended: "Extended Support",
      };

      // Review mode — only when the target stage is already complete.
      // Show what's on the record, no re-prompting.
      if (completion[stage]) {
        const target = anchorStepForStage(stage, inc);
        const previousAnchor = anchorStepForStage(previousActive, inc);
        void (async () => {
          reviewStageRef.current = stage;
          setStep(target);
          await sayBodhi(
            `Here's what's on the record for **${label[stage]}**. You can read it, add a note, or jump back when you're ready — I won't re-ask anything you already answered.`,
          );
          const chips = buildStageChips(inc, stage);
          if (chips.length) {
            append({ kind: "data-card", id: genId(), chips });
          }
          // Offer clear navigation: add nothing and pop back, OR
          // explicitly re-run the scripted step if they actually want to
          // revise the answers it captured.
          offerQuickReplies([
            {
              label: `← Back to ${label[previousActive]}`,
              next: previousAnchor,
              payload: "review_back",
              isAction: true,
            },
            {
              label: "Re-answer this step",
              next: target,
              isAction: true,
            },
          ]);
          // Stage-specific hint: Situation review accepts new facts that
          // reshape the read; other stages take notes / annotations.
          setPlaceholder(
            stage === "incident"
              ? `Add new detail (e.g. "she also hit the teacher") — or type "jump back" to return.`
              : `Add a note — or type "jump back" to return.`,
          );
        })();
        return;
      }

      // Forward to an uncaptured stage — run the scripted entry.
      void (async () => {
        await sayBodhi(`Jumping to **${label[stage]}** — you can revise anything here.`);
        const target = anchorStepForStage(stage, inc);
        goto(target);
      })();
    },
    [goto, sayBodhi, append, offerQuickReplies, setPlaceholder, isReadOnly],
  );

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <>
    <div className="flex flex-col h-[calc(100vh-180px)] min-h-[500px] bg-gray-50 rounded-2xl border-2 border-bodhi-gold/40 shadow-xl overflow-hidden">
      {/* In-chat stage tracker — same source of truth as the page header,
          so locked/completed/active states stay in sync. Sequential
          progression is enforced inside `handleJumpToStage`. */}
      <StageTracker
        variant="compact"
        incident={incident}
        activeStage={effectiveActiveStage}
        onJump={handleJumpToStage}
        isReadOnly={isReadOnly}
      />
      <div className="px-4 pt-2 pb-3 bg-white border-b border-gray-200">
        <StageProgressBar incident={incident} activeStage={effectiveActiveStage} />
      </div>

      {/* "What we have so far" — collapsible summary the leader can refer
          back to at any moment so they're never lost about the case state.
          Defaults open when resuming an incident with prior data; otherwise
          collapsed so it doesn't dominate a fresh intake. */}
      {/* Collapsed by default. The single-line headline already says
          "currently on X · 1 of 4 steps complete", so we don't need to
          push the chat below the fold to convey that. The leader can
          expand whenever they want a deeper look. */}
      <IncidentSummaryCard
        incident={incident}
        activeStage={effectiveActiveStage}
        onJump={handleJumpToStage}
        onContinue={handleContinueFromHere}
        defaultOpen={false}
        isReadOnly={isReadOnly}
      />

      {/* Chat log */}
      <div
        ref={chatRef}
        className="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-3 scroll-smooth"
      >
        {messages.map((m) => {
          if (m.kind === "bodhi") return <BodhiBubble key={m.id} html={m.html} />;
          if (m.kind === "user") return <UserBubble key={m.id} text={m.text} />;
          if (m.kind === "data-card") return <DataCard key={m.id} chips={m.chips} />;
          if (m.kind === "quick-replies") {
            return (
              <QuickReplies
                key={m.id}
                options={m.options}
                onPick={(o) => handleQuickReply(o, m.id)}
                disabled={quickRepliesDisabled}
                selected={m.selected}
              />
            );
          }
          if (m.kind === "iep504-guidance") {
            const planLabel =
              m.plan === "both"
                ? "IEP + 504 Plan"
                : m.plan === "504"
                  ? "504 Plan"
                  : m.plan === "bip"
                    ? "Behavior Intervention Plan (BIP)"
                    : "IEP";
            const isBipOnly = m.plan === "bip";
            return (
              <OutCardShell
                key={m.id}
                tone="amber"
                eyebrow={`⚠ Federal guidance · ${planLabel}`}
                title="What this means for how you proceed"
              >
                <div className="space-y-3 text-sm text-gray-800 leading-relaxed">
                  <p>
                    {isBipOnly
                      ? "Because this student has a BIP, a few things change:"
                      : `Because this student has a ${planLabel}, a few things change:`}
                  </p>
                  <ul className="list-disc pl-5 space-y-1.5 text-sm text-gray-800">
                    {isBipOnly ? (
                      <>
                        <li>
                          <strong>Follow the BIP first.</strong> The supports,
                          de-escalation moves, and replacement behaviors in the
                          BIP should drive how you respond — not after-the-fact
                          consequences.
                        </li>
                        <li>
                          <strong>Check for an underlying IEP/504.</strong>{" "}
                          Many BIPs are part of an IEP or 504. If this student
                          has one, manifestation determination and other
                          federal protections also apply — confirm with your
                          special education coordinator.
                        </li>
                        <li>
                          <strong>Loop in the case manager.</strong> Share
                          this incident with whoever owns the BIP so the plan
                          can be revisited if the strategies aren't working.
                        </li>
                        <li>
                          <strong>Document fidelity.</strong> Write down what
                          you did, which BIP supports were used, and why your
                          response matched (or intentionally differed from) the
                          plan.
                        </li>
                      </>
                    ) : (
                      <>
                        <li>
                          <strong>Manifestation determination.</strong> If a
                          removal totals more than 10 cumulative school days,
                          IDEA requires the team to determine whether the
                          behavior is a manifestation of the student's
                          disability.
                        </li>
                        <li>
                          <strong>Special ed coordinator in the loop.</strong>
                          {" "}
                          Any formal removal — even short — should be shared
                          with your special education coordinator and the case
                          manager.
                        </li>
                        <li>
                          <strong>BIP check.</strong> If there's a Behavior
                          Intervention Plan (BIP), review it now. The supports
                          and strategies there may already tell you what
                          approach to take.
                        </li>
                        <li>
                          <strong>Services continue.</strong> Services from
                          the IEP (counseling, OT, specialized instruction,
                          etc.) must continue during any removal of 10+
                          cumulative days.
                        </li>
                        <li>
                          <strong>Write it down.</strong> Document everything —
                          what happened, supports given in the moment, and why
                          your response matches (or intentionally differs from)
                          the BIP.
                        </li>
                      </>
                    )}
                  </ul>
                  <p className="text-xs text-amber-900 bg-amber-50 border-l-4 border-amber-500 rounded-r px-3 py-2">
                    This is general guidance, not legal advice. Loop in your
                    special education coordinator and follow your district's
                    procedures.
                  </p>
                </div>
              </OutCardShell>
            );
          }
          if (m.kind === "removal-decision-card") {
            return <RemovalDecisionCard key={m.id} decision={m.decision} />;
          }
          if (m.kind === "debrief-card") {
            return <DebriefCard key={m.id} debrief={m.debrief} />;
          }
          if (m.kind === "missing-checks-card") {
            return (
              <MissingChecksCard
                key={m.id}
                ready={m.ready}
                gaps={m.gaps}
                onDismiss={() =>
                  setMessages((msgs) => msgs.filter((msg) => msg.id !== m.id))
                }
                onAnswer={(field, answer) => {
                  const cur = incidentRef.current;
                  const existing = cur.conversation_notes?.trim() ?? "";
                  const line = `[${field}] ${answer}`;
                  onUpdateIncident({
                    conversation_notes: existing ? `${existing}\n\n${line}` : line,
                  });
                  toast.success(`Added: ${field}`);
                }}
                onComplete={({ answered, skipped }) => {
                  // Nudge the admin forward so they don't get stuck staring at
                  // a filled-in card with no next move. We re-offer the
                  // phase-A genOutputs chips (or a light "you're good to go"
                  // if they're past that step).
                  void (async () => {
                    const summary =
                      answered > 0
                        ? `Got it — added${
                            skipped > 0
                              ? ` ${answered} ${
                                  answered === 1 ? "answer" : "answers"
                                } (skipped ${skipped})`
                              : ` ${answered} ${
                                  answered === 1 ? "answer" : "answers"
                                }`
                          } to the record.`
                        : "Okay — skipped those for now. You can always come back to them.";
                    await sayBodhi(`${summary} **What's next?**`);
                    if (stepRef.current === "genOutputs") {
                      offerQuickReplies([
                        {
                          label: "💬 AI Advice on the conversation",
                          next: "genOutputs",
                          askMentor: true,
                          payload:
                            "Based on what I just added, what should I lead with and what should I avoid in this conversation?",
                        },
                        {
                          label: "➡ Show me the conversation guide",
                          next: "genOutputs",
                          payload: "show_guide",
                          isAction: true,
                        },
                      ]);
                    } else {
                      // Past genOutputs — keep them in flow by giving generic
                      // next-move affordances rather than silent.
                      offerQuickReplies([
                        {
                          label: "💬 AI Advice",
                          next: stepRef.current,
                          askMentor: true,
                          payload:
                            "Given what I just added to the record, what should I pay attention to next?",
                        },
                        {
                          label: "Keep going",
                          next: stepRef.current,
                          isAction: true,
                        },
                      ]);
                    }
                  })();
                }}
              />
            );
          }
          if (m.kind === "motivation-card") {
            return (
              <MotivationCard
                key={m.id}
                hypotheses={m.hypotheses}
                disabled={
                  quickRepliesDisabled || stepRef.current !== "understand"
                }
                onPick={(h) => {
                  const existing =
                    incidentRef.current.conversation_notes?.trim() ?? "";
                  const line = `Motivation: ${h.label} (AI read) — ${h.why}`;
                  onUpdateIncident({
                    conversation_notes: existing ? `${line}\n\n${existing}` : line,
                  });
                  sayUser(`Going with: ${h.label}`);
                  window.setTimeout(() => goto("genOutputs"), 250);
                }}
              />
            );
          }
          if (m.kind === "out-reflection") {
            // Prefer the Claude-generated reflection prompts (already tuned
            // to this incident) over the static, grade-level template. If
            // the AI generation failed, we fall back to the templated
            // prompts run through `personalizeReflectionPrompts` so the
            // form still references the specific incident.
            const personal =
              aiReflection ??
              personalizeReflectionPrompts(reflectionPrompts, incident);
            const dateStr = new Date().toISOString().split("T")[0];
            const reflectionGrade = effectiveGrade(incident.grade_level);
            const filename = `student-reflection-${studentSlug(incident.student_identifier)}-${reflectionGrade.replace("-", "")}-${dateStr}.pdf`;
            const reflectionHtml = buildReflectionFormHTML(
              reflectionGrade,
              incident.student_identifier,
              personal,
            );
            return (
              <CardErrorBoundary key={m.id} label="Student Reflection Form">
                <PreviewTile
                  icon={<GraduationCap className="w-5 h-5" aria-hidden />}
                  eyebrow={`Student Reflection · Grade ${formatGradeForDoc(incident.grade_level)}${
                    incident.grade_level === "K-2" ? " · Read aloud" : ""
                  }`}
                  title={`Reflection form${
                    incident.student_identifier ? ` · ${incident.student_identifier}` : ""
                  }`}
                  summary="Personalized prompts for the student. Print, download, open in Google Docs, or save as Word."
                  onPrimary={() => setOpenPanel({ kind: "reflection" })}
                  secondaryActions={
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadHtmlAsPDF(reflectionHtml, filename);
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        <Download className="w-3.5 h-3.5 mr-1" /> PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          printHtml(reflectionHtml, "Student Reflection Form");
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        <Printer className="w-3.5 h-3.5 mr-1" /> Print
                      </Button>
                      <GoogleDocExportButton
                        html={reflectionHtml}
                        plain={buildReflectionFormPlainText(
                          reflectionGrade,
                          incident.student_identifier,
                          personal,
                        )}
                        docTitle={`Student Reflection Form - ${incident.student_identifier || "Student"} - ${new Date().toLocaleDateString()}`}
                        label="Google Doc"
                        dialogTitle="Copy reflection form into a Google Doc"
                        variant="ghost"
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadHtmlAsWord(reflectionHtml, filename, "Student Reflection Form");
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        <FileText className="w-3.5 h-3.5 mr-1" /> Word
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(
                            buildReflectionFormPlainText(
                              reflectionGrade,
                              incident.student_identifier,
                              personal,
                            ),
                            reflectionHtml,
                          );
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                      </Button>
                    </>
                  }
                />
              </CardErrorBoundary>
            );
          }
          if (m.kind === "out-due-process") {
            return (
              <CardErrorBoundary key={m.id} label="Due Process Checkbox">
              <OutCardShell
                eyebrow="✦ Due process"
                title="Student opportunity to respond"
              >
                <label
                  className={cn(
                    "flex items-start gap-3 p-3 rounded-lg border transition-colors",
                    incident.due_process_acknowledged
                      ? "bg-bodhi-teal/5 border-bodhi-teal/40"
                      : "bg-gray-50 border-gray-200 hover:border-bodhi-teal/40",
                    isReadOnly ? "cursor-not-allowed opacity-70" : "cursor-pointer",
                  )}
                  onClick={(e) => {
                    if (isReadOnly) return;
                    e.preventDefault();
                    handleDueProcess();
                  }}
                >
                  <span
                    className={cn(
                      "w-4 h-4 rounded border-2 flex-shrink-0 mt-0.5 flex items-center justify-center text-[10px]",
                      incident.due_process_acknowledged
                        ? "bg-bodhi-teal border-bodhi-teal text-white"
                        : "border-gray-300 bg-white",
                    )}
                  >
                    {incident.due_process_acknowledged ? "✓" : ""}
                  </span>
                  <div className="flex-1">
                    <span className="text-sm text-gray-800 leading-relaxed">
                      Student was informed of the referral and given the opportunity to respond
                      before any formal action was taken.
                    </span>
                    {incident.due_process_acknowledged && incident.due_process_timestamp && (
                      <p className="text-xs text-bodhi-teal/80 mt-1.5">
                        Recorded: {incident.due_process_timestamp}
                      </p>
                    )}
                  </div>
                </label>
              </OutCardShell>
              </CardErrorBoundary>
            );
          }
          if (m.kind === "out-coaching") {
            const dateStr = new Date().toISOString().split("T")[0];
            const coachingFilename = `conversation-guide-${studentSlug(incident.student_identifier)}-${dateStr}.pdf`;
            const coachingPrintHtml = buildCoachingPrintHtml(
              m.guideHtml,
              m.grade,
              incident.student_identifier,
            );
            return (
              <CardErrorBoundary key={m.id} label="Conversation Guide">
                <PreviewTile
                  icon={<FileText className="w-5 h-5" aria-hidden />}
                  eyebrow={`Facilitator Guide · Grade ${m.grade}`}
                  title={`Your conversation prep${
                    incident.student_identifier ? ` · ${incident.student_identifier}` : ""
                  }`}
                  summary={
                    m.isAi
                      ? "AI-tailored coaching script grounded in this incident and current notes."
                      : "Template fallback coaching script (tap regenerate if you want a more tailored AI version)."
                  }
                  onPrimary={() =>
                    setOpenPanel({
                      kind: "coaching",
                      guideHtml: m.guideHtml,
                      grade: m.grade,
                      isAi: m.isAi,
                    })
                  }
                  secondaryActions={
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadHtmlAsPDF(coachingPrintHtml, coachingFilename);
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        <Download className="w-3.5 h-3.5 mr-1" /> PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          printHtml(coachingPrintHtml, "Facilitator Conversation Guide");
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        <Printer className="w-3.5 h-3.5 mr-1" /> Print
                      </Button>
                      <GoogleDocExportButton
                        html={coachingPrintHtml}
                        plain={(m.guideHtml || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}
                        docTitle={`Facilitator Conversation Guide - ${incident.student_identifier || "Student"} - ${new Date().toLocaleDateString()}`}
                        label="Google Doc"
                        dialogTitle="Copy conversation guide into a Google Doc"
                        variant="ghost"
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadHtmlAsWord(coachingPrintHtml, coachingFilename, "Facilitator Conversation Guide");
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        <FileText className="w-3.5 h-3.5 mr-1" /> Word
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(
                            (m.guideHtml || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim(),
                            coachingPrintHtml,
                          );
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                      </Button>
                    </>
                  }
                />
              </CardErrorBoundary>
            );
          }
          if (m.kind === "out-iss-docs") {
            const issSummaryBits: string[] = [];
            if (incident.iss_work_plan?.trim()) issSummaryBits.push("Work plan drafted");
            if (incident.iss_reflection_completed) issSummaryBits.push("Teachers notified");
            if (incident.reentry_conversation_needed)
              issSummaryBits.push(
                incident.reentry_facilitator
                  ? `Re-entry: ${incident.reentry_facilitator}`
                  : "Re-entry conversation planned",
              );
            if (incident.return_to_class_plan?.trim())
              issSummaryBits.push("Return plan drafted");
            if (incident.reentry_checkin_schedule?.trim())
              issSummaryBits.push("Check-in schedule set");
            const issSummary = issSummaryBits.length
              ? issSummaryBits.join(" · ")
              : "Draft the ISS work plan, re-entry, and check-in schedule.";
            return (
              <CardErrorBoundary key={m.id} label="ISS / Removal Plan">
                <PreviewTile
                  icon={<BookOpen className="w-5 h-5" aria-hidden />}
                  eyebrow="Extended Support · ISS & Re-entry"
                  title={`ISS / removal plan${
                    incident.student_identifier ? ` · ${incident.student_identifier}` : ""
                  }`}
                  summary={issSummary}
                  onPrimary={() => setOpenPanel({ kind: "iss-docs" })}
                  secondaryActions={
                    !isReadOnly ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Even after a formal removal, the referring
                          // teacher usually still needs an update — route
                          // through teacher follow-up before docOffer.
                          goto("teacherFollowUp");
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        Continue to teacher follow-up →
                      </Button>
                    ) : null
                  }
                />
              </CardErrorBoundary>
            );
          }
          if (m.kind === "out-documentation") {
            const attachCount = attachments.length;
            const summaryBits: string[] = [];
            if (incident.removal_type) {
              summaryBits.push(incident.removal_type.replace(/_/g, " "));
            }
            if (attachCount > 0) {
              summaryBits.push(`${attachCount} file${attachCount === 1 ? "" : "s"}`);
            }
            summaryBits.push(
              incident.due_process_acknowledged
                ? "Due process ✓"
                : "Due process pending",
            );
            summaryBits.push("Polish · narrative");
            summaryBits.push("Reopen from header or Documents");
            return (
              <CardErrorBoundary key={m.id} label="Incident Documentation">
                <PreviewTile
                  icon={<FileCheck2 className="w-5 h-5" aria-hidden />}
                  eyebrow={`Incident Documentation · ${new Date().toLocaleDateString()}`}
                  title={`${incident.student_identifier || "Student"}${
                    incident.grade_level ? ` · Grade ${incident.grade_level}` : ""
                  }`}
                  summary={summaryBits.join(" · ")}
                  onPrimary={() => setOpenPanel({ kind: "documentation" })}
                  secondaryActions={
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadIncidentPacketPDF(incident);
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        <Download className="w-3.5 h-3.5 mr-1" /> Full packet
                      </Button>
                      {!isReadOnly && incident.status !== "closed" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCloseIncident();
                          }}
                          className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                        >
                          Mark complete
                        </Button>
                      )}
                    </>
                  }
                />
              </CardErrorBoundary>
            );
          }
          if (m.kind === "out-parent") {
            const summaryParts: string[] = ["Email draft", "Phone script"];
            if (incident.student_identifier) {
              summaryParts.push(`call with ${incident.student_identifier} + parent`);
            }
            return (
              <CardErrorBoundary key={m.id} label="Parent Communication">
                <PreviewTile
                  icon={<Phone className="w-5 h-5" aria-hidden />}
                  eyebrow="Parent Communication"
                  title="Draft your outreach"
                  summary={summaryParts.join(" · ")}
                  onPrimary={() => setOpenPanel({ kind: "parent" })}
                  secondaryActions={
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenPanel({ kind: "parent", initialTab: "email" });
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        Email
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenPanel({ kind: "parent", initialTab: "phone_script" });
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        Phone script
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenPanel({ kind: "parent", initialTab: "student_call" });
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        Student + parent
                      </Button>
                    </>
                  }
                />
              </CardErrorBoundary>
            );
          }
          if (m.kind === "out-teacher-summary") {
            const teacherLabel =
              incident.teacher_name?.trim() || "the teacher";
            const summaryBits: string[] = [
              teacherSummary?.isAi ? "AI draft" : "Template draft",
              "Edit · copy · download · print",
            ];
            return (
              <CardErrorBoundary key={m.id} label="Teacher Written Summary">
                <PreviewTile
                  icon={<Mail className="w-5 h-5" aria-hidden />}
                  eyebrow="Written summary for the teacher"
                  title={`Note for ${teacherLabel}`}
                  summary={summaryBits.join(" · ")}
                  onPrimary={() => setOpenPanel({ kind: "teacher-summary" })}
                  secondaryActions={
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!teacherSummary?.text}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!teacherSummary?.text) return;
                          const dateStr = new Date()
                            .toISOString()
                            .split("T")[0];
                          const filename = `teacher-summary-${studentSlug(
                            incident.student_identifier,
                          )}-${dateStr}.pdf`;
                          void downloadHtmlAsPDF(
                            buildTeacherSummaryPrintHtml(
                              teacherSummary.text,
                              incident.student_identifier,
                              incident.teacher_name,
                            ),
                            filename,
                          );
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        <Download className="w-3.5 h-3.5 mr-1" /> PDF
                      </Button>
                      <GoogleDocExportButton
                        html={buildTeacherSummaryPrintHtml(
                          teacherSummary?.text ?? "",
                          incident.student_identifier,
                          incident.teacher_name,
                        )}
                        plain={teacherSummary?.text ?? ""}
                        docTitle={`Teacher Written Summary - ${incident.student_identifier || "Student"} - ${new Date().toLocaleDateString()}`}
                        label="Google Doc"
                        dialogTitle="Copy teacher summary into a Google Doc"
                        variant="ghost"
                        disabled={!teacherSummary?.text}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!teacherSummary?.text}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!teacherSummary?.text) return;
                          const dateStr = new Date().toISOString().split("T")[0];
                          downloadHtmlAsWord(
                            buildTeacherSummaryPrintHtml(
                              teacherSummary.text,
                              incident.student_identifier,
                              incident.teacher_name,
                            ),
                            `teacher-summary-${studentSlug(incident.student_identifier)}-${dateStr}.doc`,
                            "Teacher Written Summary",
                          );
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        <FileText className="w-3.5 h-3.5 mr-1" /> Word
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!teacherSummary?.text}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!teacherSummary?.text) return;
                          void copyToClipboard(teacherSummary.text);
                        }}
                        className="h-7 text-xs text-bodhi-teal hover:bg-bodhi-teal/10"
                      >
                        <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                      </Button>
                    </>
                  }
                />
              </CardErrorBoundary>
            );
          }
          if (m.kind === "out-complete") {
            return (
              <CompleteBanner
                key={m.id}
                studentLabel={m.studentLabel}
                onNewIncident={onNewIncident}
              />
            );
          }
          if (m.kind === "attachment") {
            return (
              <AttachmentCard
                key={m.id}
                fileName={m.fileName}
                fileSize={m.fileSize}
                mimeType={m.mimeType}
                status={m.status}
                errorMessage={m.errorMessage}
                fileId={m.fileId}
                onView={handleViewAttachment}
                onRemove={handleRemoveAttachment}
                isReadOnly={isReadOnly}
              />
            );
          }
          return null;
        })}
        {isTyping && <TypingIndicator />}
      </div>

      {/* Input — always enabled when not read-only, regardless of step */}
      {!isReadOnly && (
        <div className="border-t border-gray-200 bg-white px-4 py-2.5">
          <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            multiple
            accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
            onChange={handleFileInputChange}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-9 h-9 rounded-full border border-gray-200 bg-white hover:bg-bodhi-teal/10 hover:border-bodhi-teal/40 text-gray-500 hover:text-bodhi-teal flex items-center justify-center flex-shrink-0 transition-colors"
            aria-label="Attach a file"
            title="Attach a file (PDF, image, doc, etc.)"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          {/* Save for handoff — pauses the incident so a colleague (or you,
              later) can pick it up from the dashboard without resetting
              anything. Hidden when the incident hasn't been written yet
              (no row to update) or when the incident is already closed. */}
          {!!incident.id && incident.status !== "closed" && (
            <button
              type="button"
              onClick={() => goto("handoff")}
              className="hidden sm:inline-flex items-center gap-1 h-9 px-3 rounded-full border border-gray-200 bg-white hover:bg-bodhi-teal/10 hover:border-bodhi-teal/40 text-gray-600 hover:text-bodhi-teal text-xs font-medium flex-shrink-0 transition-colors"
              aria-label="Save for handoff"
              title="Save for handoff — pauses the incident so a colleague can pick it up later."
            >
              <Save className="w-3.5 h-3.5" />
              Save for handoff
            </button>
          )}
          <textarea
            ref={inputRef}
            rows={1}
            placeholder={inputPlaceholder}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              const el = e.currentTarget;
              el.style.height = "auto";
              el.style.height = Math.min(el.scrollHeight, 120) + "px";
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            className="flex-1 resize-none bg-gray-50 border border-gray-200 focus:border-bodhi-teal focus:bg-white rounded-xl px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 placeholder:italic outline-none transition-colors"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim()}
            className="w-9 h-9 rounded-full bg-bodhi-teal hover:bg-bodhi-teal/90 text-white flex items-center justify-center flex-shrink-0 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            aria-label="Send"
          >
            <Send className="w-4 h-4" />
          </button>
          </div>
        </div>
      )}

      {isReadOnly && (
        <div className="border-t border-gray-200 bg-white px-4 py-3 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={onBackToDashboard}
            className="border-gray-300 text-gray-700"
          >
            Back to all incidents
          </Button>
        </div>
      )}
    </div>

    {/* Right-side panel for long-form outputs (reflection, coaching guide,
        documentation, parent comms). Keeps the chat log scannable. */}
    <Sheet
      open={openPanel !== null}
      onOpenChange={(open) => {
        if (!open) setOpenPanel(null);
      }}
    >
      <SheetContent
        side="right"
        className="w-[min(780px,94vw)] sm:max-w-none overflow-y-auto p-0"
      >
        {openPanel?.kind === "reflection" && (() => {
          const personal =
            aiReflection ??
            personalizeReflectionPrompts(reflectionPrompts, incident);
          // Merge the admin's in-session edits on top of the generated defaults.
          const effective: ReflectionPrompts = {
            intro: reflectionOverrides?.intro ?? personal.intro,
            closing: reflectionOverrides?.closing ?? personal.closing,
            prompts: reflectionOverrides?.prompts ?? personal.prompts,
          };
          const dateStr = new Date().toISOString().split("T")[0];
          const reflectionGrade = effectiveGrade(incident.grade_level);
          const filename = `student-reflection-${studentSlug(incident.student_identifier)}-${reflectionGrade.replace("-", "")}-${dateStr}.pdf`;
          const reflectionHtml = buildReflectionFormHTML(
            reflectionGrade,
            incident.student_identifier,
            effective,
          );
          const updatePrompt = (i: number, patch: Partial<{ question: string; hint: string }>) => {
            const basePrompts = reflectionOverrides?.prompts ?? personal.prompts;
            const next = basePrompts.map((p, idx) => (idx === i ? { ...p, ...patch } : p));
            setReflectionOverrides({
              intro: reflectionOverrides?.intro ?? personal.intro,
              closing: reflectionOverrides?.closing ?? personal.closing,
              prompts: next,
            });
          };
          const removePrompt = (i: number) => {
            const basePrompts = reflectionOverrides?.prompts ?? personal.prompts;
            // Always leave at least one prompt — a reflection form with
            // zero questions would just print blanks.
            if (basePrompts.length <= 1) {
              toast.info("Keep at least one question on the form.");
              return;
            }
            const next = basePrompts.filter((_, idx) => idx !== i);
            setReflectionOverrides({
              intro: reflectionOverrides?.intro ?? personal.intro,
              closing: reflectionOverrides?.closing ?? personal.closing,
              prompts: next,
            });
          };
          const addPrompt = () => {
            const basePrompts = reflectionOverrides?.prompts ?? personal.prompts;
            setReflectionOverrides({
              intro: reflectionOverrides?.intro ?? personal.intro,
              closing: reflectionOverrides?.closing ?? personal.closing,
              prompts: [
                ...basePrompts,
                { question: "New question", hint: "" },
              ],
            });
          };
          return (
            <div className="flex flex-col h-full">
              <SheetHeader className="px-6 pt-6 pb-3 border-b border-gray-200 bg-bodhi-teal text-white space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-white/80 font-semibold">
                  Student Reflection · Grade {formatGradeForDoc(incident.grade_level)}
                  {incident.grade_level === "K-2" && " · Read aloud"}
                </div>
                <SheetTitle className="text-white font-literata text-xl">
                  Reflection form
                  {incident.student_identifier && ` · ${incident.student_identifier}`}
                </SheetTitle>
                <SheetDescription className="text-white/80">
                  Personalized prompts tailored to this incident.
                </SheetDescription>
              </SheetHeader>
              <div className="px-6 py-5 border-b border-gray-200 bg-white flex flex-wrap gap-2">
                <Button
                  onClick={() => downloadHtmlAsPDF(reflectionHtml, filename)}
                  className="bg-bodhi-teal text-white hover:bg-bodhi-teal/90 shadow-sm"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => printHtml(reflectionHtml, "Student Reflection Form")}
                  className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                >
                  <Printer className="w-4 h-4 mr-2" /> Print
                </Button>
                <GoogleDocExportButton
                  html={reflectionHtml}
                  plain={buildReflectionFormPlainText(
                    reflectionGrade,
                    incident.student_identifier,
                    effective,
                  )}
                  docTitle={`Student Reflection Form - ${incident.student_identifier || "Student"} - ${new Date().toLocaleDateString()}`}
                  label="Copy & open Google Doc"
                  dialogTitle="Copy reflection form into a Google Doc"
                  size="default"
                  className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                />
                <Button
                  variant="outline"
                  onClick={() => downloadHtmlAsWord(reflectionHtml, filename, "Student Reflection Form")}
                  className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                >
                  <FileText className="w-4 h-4 mr-2" /> Word
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    copyToClipboard(
                      buildReflectionFormPlainText(
                        reflectionGrade,
                        incident.student_identifier,
                        effective,
                      ),
                      reflectionHtml,
                    )
                  }
                  className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                >
                  <Copy className="w-4 h-4 mr-2" /> Copy
                </Button>
                {!isReadOnly && (
                  <Button
                    variant="outline"
                    onClick={() => setReflectionEditing((v) => !v)}
                    className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                  >
                    {reflectionEditing ? (
                      <>
                        <Save className="w-4 h-4 mr-2" /> Done editing
                      </>
                    ) : (
                      <>
                        <Pencil className="w-4 h-4 mr-2" /> Edit
                      </>
                    )}
                  </Button>
                )}
                {reflectionEditing && reflectionOverrides && (
                  <Button
                    variant="ghost"
                    onClick={() => setReflectionOverrides(null)}
                    className="text-gray-500 hover:text-gray-800"
                  >
                    Reset to generated
                  </Button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 bg-white text-gray-900">
                {reflectionEditing && !isReadOnly ? (
                  <>
                    <p className="text-xs text-gray-500">
                      Edits live in this session — download or print when you're
                      done. Use "Reset to generated" to return to the AI-written
                      defaults.
                    </p>
                    <div>
                      <Label className="text-xs">Intro (shown to the student)</Label>
                      <Textarea
                        rows={3}
                        value={effective.intro}
                        onChange={(e) =>
                          setReflectionOverrides({
                            intro: e.target.value,
                            closing:
                              reflectionOverrides?.closing ?? personal.closing,
                            prompts:
                              reflectionOverrides?.prompts ?? personal.prompts,
                          })
                        }
                      />
                    </div>
                    <ol className="space-y-4">
                      {effective.prompts.map((p, i) => (
                        <li
                          key={i}
                          className="pb-3 border-b border-gray-100 last:border-0 last:pb-0"
                        >
                          <div className="flex items-start gap-2">
                            <span className="flex-shrink-0 w-6 h-6 bg-bodhi-teal text-white rounded-full flex items-center justify-center text-xs font-semibold">
                              {i + 1}
                            </span>
                            <div className="flex-1 space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <Label className="text-xs">Question</Label>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removePrompt(i)}
                                  className="h-7 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                                  disabled={effective.prompts.length <= 1}
                                  aria-label={`Delete question ${i + 1}`}
                                  title={
                                    effective.prompts.length <= 1
                                      ? "Keep at least one question"
                                      : "Remove this question"
                                  }
                                >
                                  <Trash2 className="w-3.5 h-3.5 mr-1" />
                                  Delete
                                </Button>
                              </div>
                              <Textarea
                                rows={2}
                                value={p.question}
                                onChange={(e) =>
                                  updatePrompt(i, { question: e.target.value })
                                }
                              />
                              <div>
                                <Label className="text-xs">Hint / note</Label>
                                <Input
                                  value={p.hint}
                                  onChange={(e) =>
                                    updatePrompt(i, { hint: e.target.value })
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                    <Button
                      variant="outline"
                      onClick={addPrompt}
                      className="w-full border-dashed border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                    >
                      + Add another question
                    </Button>
                    <div>
                      <Label className="text-xs">Closing note</Label>
                      <Textarea
                        rows={2}
                        value={effective.closing}
                        onChange={(e) =>
                          setReflectionOverrides({
                            intro:
                              reflectionOverrides?.intro ?? personal.intro,
                            closing: e.target.value,
                            prompts:
                              reflectionOverrides?.prompts ?? personal.prompts,
                          })
                        }
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {effective.intro && (
                      <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-md px-3 py-2.5">
                        <p className="text-sm text-amber-900 leading-relaxed whitespace-pre-wrap">
                          {effective.intro}
                        </p>
                      </div>
                    )}
                    <ol className="space-y-4">
                      {effective.prompts.map((p, i) => (
                        <li
                          key={i}
                          className="pb-3 border-b border-gray-100 last:border-0 last:pb-0"
                        >
                          <div className="flex items-start gap-2">
                            <span className="flex-shrink-0 w-6 h-6 bg-bodhi-teal text-white rounded-full flex items-center justify-center text-xs font-semibold">
                              {i + 1}
                            </span>
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-gray-900 leading-relaxed">
                                {p.question}
                              </p>
                              <p className="text-xs text-gray-500 italic mt-0.5">
                                {p.hint}
                              </p>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                    {effective.closing && (
                      <div className="bg-green-50 border border-green-200 rounded-md px-3 py-2.5">
                        <p className="text-sm text-green-900 leading-relaxed italic">
                          {effective.closing}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })()}

        {openPanel?.kind === "coaching" && (() => {
          const dateStr = new Date().toISOString().split("T")[0];
          const coachingFilename = `conversation-guide-${studentSlug(incident.student_identifier)}-${dateStr}.pdf`;
          const coachingPrintHtml = buildCoachingPrintHtml(
            openPanel.guideHtml,
            openPanel.grade,
            incident.student_identifier,
          );
          return (
            <div className="flex flex-col h-full">
              <SheetHeader className="px-6 pt-6 pb-3 border-b border-gray-200 bg-bodhi-teal text-white space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-white/80 font-semibold">
                  Facilitator Guide · Grade {openPanel.grade}
                </div>
                <SheetTitle className="text-white font-literata text-xl">
                  Your conversation prep
                  {incident.student_identifier && ` · ${incident.student_identifier}`}
                </SheetTitle>
                <SheetDescription className="text-white/80">
                  {openPanel.isAi
                    ? "AI-tailored coaching script grounded in the incident and current notes."
                    : "Template fallback coaching script. You can regenerate later for a more tailored AI version."}
                </SheetDescription>
              </SheetHeader>
              <div className="px-6 py-5 border-b border-gray-200 bg-white flex flex-wrap gap-2">
                <Button
                  onClick={() => downloadHtmlAsPDF(coachingPrintHtml, coachingFilename)}
                  className="bg-bodhi-teal text-white hover:bg-bodhi-teal/90 shadow-sm"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => printHtml(coachingPrintHtml, "Facilitator Conversation Guide")}
                  className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                >
                  <Printer className="w-4 h-4 mr-2" /> Print
                </Button>
                <GoogleDocExportButton
                  html={coachingPrintHtml}
                  plain={(openPanel.guideHtml || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}
                  docTitle={`Facilitator Conversation Guide - ${incident.student_identifier || "Student"} - ${new Date().toLocaleDateString()}`}
                  label="Copy & open Google Doc"
                  dialogTitle="Copy conversation guide into a Google Doc"
                  size="default"
                  className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                />
                <Button
                  variant="outline"
                  onClick={() => downloadHtmlAsWord(coachingPrintHtml, coachingFilename, "Facilitator Conversation Guide")}
                  className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                >
                  <FileText className="w-4 h-4 mr-2" /> Word
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    copyToClipboard(
                      (openPanel.guideHtml || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim(),
                      coachingPrintHtml,
                    )
                  }
                  className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                >
                  <Copy className="w-4 h-4 mr-2" /> Copy
                </Button>
              </div>
              <div
                className="flex-1 overflow-y-auto px-6 py-5 bg-white max-w-none text-gray-900 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-bodhi-teal [&_h3]:font-literata [&_h3]:mb-3 [&_h3]:mt-0 [&_h4]:text-sm [&_h4]:font-semibold [&_h4]:text-bodhi-teal [&_h4]:uppercase [&_h4]:tracking-wide [&_h4]:mt-5 [&_h4]:mb-2 [&_p]:text-sm [&_p]:text-gray-800 [&_p]:leading-relaxed [&_p]:my-2 [&_ul]:text-sm [&_ul]:text-gray-800 [&_ul]:my-2 [&_ul]:pl-5 [&_ul]:list-disc [&_li]:my-1 [&_li]:leading-relaxed [&_small]:text-gray-500 [&_small]:text-xs [&_strong]:text-gray-900 [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: openPanel.guideHtml }}
              />
            </div>
          );
        })()}

        {openPanel?.kind === "teacher-summary" && (() => {
          const dateStr = new Date().toISOString().split("T")[0];
          const teacherLabel =
            incident.teacher_name?.trim() || "the teacher";
          const summaryText = teacherSummary?.text ?? "";
          const printHtml_ = buildTeacherSummaryPrintHtml(
            summaryText,
            incident.student_identifier,
            incident.teacher_name,
          );
          const filename = `teacher-summary-${studentSlug(
            incident.student_identifier,
          )}-${dateStr}.pdf`;
          return (
            <div className="flex flex-col h-full">
              <SheetHeader className="px-6 pt-6 pb-3 border-b border-gray-200 bg-bodhi-teal text-white space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-white/80 font-semibold">
                  Written summary · For {teacherLabel}
                </div>
                <SheetTitle className="text-white font-literata text-xl">
                  Note for the teacher
                  {incident.student_identifier && ` · re: ${incident.student_identifier}`}
                </SheetTitle>
                <SheetDescription className="text-white/80">
                  {teacherSummary?.isAi
                    ? "AI-drafted from the incident record. Edit before you send."
                    : teacherSummary
                      ? "Template fallback draft. Edit before you send — or regenerate for a tailored AI version."
                      : "Drafting…"}
                </SheetDescription>
              </SheetHeader>
              <div className="px-6 py-5 border-b border-gray-200 bg-white flex flex-wrap gap-2">
                <Button
                  disabled={!summaryText}
                  onClick={() => downloadHtmlAsPDF(printHtml_, filename)}
                  className="bg-bodhi-teal text-white hover:bg-bodhi-teal/90 shadow-sm disabled:opacity-50"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <Button
                  variant="outline"
                  disabled={!summaryText}
                  onClick={() => printHtml(printHtml_, "Teacher Written Summary")}
                  className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                >
                  <Printer className="w-4 h-4 mr-2" /> Print
                </Button>
                <GoogleDocExportButton
                  html={printHtml_}
                  plain={summaryText}
                  docTitle={`Teacher Written Summary - ${incident.student_identifier || "Student"} - ${new Date().toLocaleDateString()}`}
                  label="Copy & open Google Doc"
                  dialogTitle="Copy teacher summary into a Google Doc"
                  size="default"
                  disabled={!summaryText}
                  className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                />
                <Button
                  variant="outline"
                  disabled={!summaryText}
                  onClick={() => downloadHtmlAsWord(printHtml_, filename, "Teacher Written Summary")}
                  className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                >
                  <FileText className="w-4 h-4 mr-2" /> Word
                </Button>
                <Button
                  variant="outline"
                  disabled={!summaryText}
                  onClick={() => copyToClipboard(summaryText, printHtml_)}
                  className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                >
                  <Copy className="w-4 h-4 mr-2" /> Copy
                </Button>
                {!isReadOnly && (
                  <Button
                    variant="outline"
                    onClick={() => setTeacherSummaryEditing((v) => !v)}
                    className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                  >
                    {teacherSummaryEditing ? (
                      <>
                        <Save className="w-4 h-4 mr-2" /> Done editing
                      </>
                    ) : (
                      <>
                        <Pencil className="w-4 h-4 mr-2" /> Edit
                      </>
                    )}
                  </Button>
                )}
                {!isReadOnly && (
                  <Button
                    variant="outline"
                    disabled={teacherSummaryGenerating}
                    onClick={() => void generateTeacherSummary({ openPanelAfter: false })}
                    className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                  >
                    {teacherSummaryGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Drafting…
                      </>
                    ) : (
                      <>↻ Regenerate</>
                    )}
                  </Button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-5 bg-white text-gray-900">
                {teacherSummaryGenerating && !summaryText ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" /> Drafting the summary…
                  </div>
                ) : teacherSummaryEditing && !isReadOnly ? (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-500">
                      Edits live in this session. Use Copy / Download / Print
                      to save the version you actually send.
                    </p>
                    <Textarea
                      rows={18}
                      value={summaryText}
                      onChange={(e) =>
                        setTeacherSummary((prev) =>
                          prev
                            ? { ...prev, text: e.target.value }
                            : {
                                text: e.target.value,
                                isAi: false,
                                generatedAt: new Date().toISOString(),
                              },
                        )
                      }
                      className="font-serif leading-relaxed"
                    />
                  </div>
                ) : summaryText ? (
                  <div className="font-serif text-[15px] leading-relaxed text-gray-900 whitespace-pre-wrap">
                    {summaryText}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    No draft yet — click Regenerate above to create one.
                  </p>
                )}
              </div>
            </div>
          );
        })()}

        {openPanel?.kind === "iss-docs" && (
          <div className="flex flex-col h-full">
            <SheetHeader className="px-6 pt-6 pb-3 border-b border-gray-200 bg-bodhi-teal text-white space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-white/80 font-semibold">
                Extended Support · ISS &amp; Re-entry
              </div>
              <SheetTitle className="text-white font-literata text-xl">
                ISS / removal plan
                {incident.student_identifier && ` · ${incident.student_identifier}`}
              </SheetTitle>
              <SheetDescription className="text-white/80">
                Document the academic work plan, re-entry conversation, and
                check-in schedule. This info is included in the final record.
              </SheetDescription>
            </SheetHeader>
            <div className="px-6 py-5 border-b border-gray-200 bg-white flex flex-wrap gap-2">
              {!isReadOnly && (
                <Button
                  onClick={() => {
                    setOpenPanel(null);
                    goto("docOffer");
                  }}
                  className="bg-bodhi-teal text-white hover:bg-bodhi-teal/90 shadow-sm"
                >
                  Continue to documentation →
                </Button>
              )}
              {!isReadOnly && (
                <Button
                  variant="outline"
                  disabled={reentryDrafting}
                  onClick={() => void handleDraftReentryPlan()}
                  className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                >
                  {reentryDrafting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Drafting…
                    </>
                  ) : (
                    <>✨ Draft plan with AI</>
                  )}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setOpenPanel({ kind: "documentation" })}
                className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
              >
                <FileCheck2 className="w-4 h-4 mr-2" /> View full documentation
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 bg-white text-gray-900">
              <ISSDocsStage
                incident={incident}
                onUpdate={onUpdateIncident}
                onBack={() => setOpenPanel(null)}
                onClose={onCloseIncident}
                onGoToDashboard={onBackToDashboard}
                isReadOnly={isReadOnly}
              />
            </div>
          </div>
        )}

        {openPanel?.kind === "documentation" && (
          <div className="flex flex-col h-full">
            <SheetHeader className="px-6 pt-6 pb-3 border-b border-gray-200 bg-bodhi-teal text-white space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-white/80 font-semibold">
                Incident Documentation · {new Date().toLocaleDateString()}
              </div>
              <SheetTitle className="text-white font-literata text-xl">
                {incident.student_identifier || "Student"}
                {incident.grade_level ? ` · Grade ${incident.grade_level}` : ""}
              </SheetTitle>
              <SheetDescription className="text-white/80">
                Complete record of the incident and response.
              </SheetDescription>
            </SheetHeader>
            <div className="px-6 py-5 border-b border-gray-200 bg-white flex flex-wrap gap-2">
              <Button
                onClick={() => downloadIncidentPacketPDF(incident)}
                className="bg-bodhi-teal text-white hover:bg-bodhi-teal/90 shadow-sm"
              >
                <Download className="w-4 h-4 mr-2" /> Download full packet
              </Button>
              {!isReadOnly && incident.status !== "closed" && (
                <Button
                  variant="outline"
                  onClick={() => setDocEditing((v) => !v)}
                  className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                >
                  {docEditing ? (
                    <>
                      <Save className="w-4 h-4 mr-2" /> Done editing
                    </>
                  ) : (
                    <>
                      <Pencil className="w-4 h-4 mr-2" /> Edit
                    </>
                  )}
                </Button>
              )}
              {!isReadOnly && incident.status !== "closed" && (
                <Button
                  variant="outline"
                  onClick={() => void runMissingChecks("pre-close")}
                  disabled={checkingMissing}
                  className="border-bodhi-gold/40 text-bodhi-gold hover:bg-bodhi-gold/10"
                >
                  {checkingMissing ? (
                    <>
                      <span className="inline-block h-3 w-3 rounded-full border-2 border-bodhi-gold border-t-transparent animate-spin mr-2" />
                      Checking…
                    </>
                  ) : (
                    <>✨ What am I missing?</>
                  )}
                </Button>
              )}
              {!isReadOnly && incident.status !== "closed" && (
                <Button
                  variant="outline"
                  onClick={onCloseIncident}
                  className="border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/5"
                >
                  Mark incident complete
                </Button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 bg-white text-gray-900">
              {/* Phase 4b: Missing-checks result from the doc panel button */}
              {!isReadOnly && missingResult && (
                missingResult.gaps?.length > 0 ? (
                  <MissingChecksCard
                    ready={missingResult.ready}
                    gaps={missingResult.gaps}
                    onDismiss={() => setMissingResult(null)}
                    onAnswer={(field, answer) => {
                      const cur = incidentRef.current;
                      const existing = cur.conversation_notes?.trim() ?? "";
                      const line = `[${field}] ${answer}`;
                      onUpdateIncident({
                        conversation_notes: existing ? `${existing}\n\n${line}` : line,
                      });
                      toast.success(`Added: ${field}`);
                    }}
                  />
                ) : (
                  <div className="rounded-lg border border-emerald-300/60 bg-emerald-50/60 p-4 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">
                        ✨ What am I missing?
                      </div>
                      <p className="text-sm text-gray-700 mt-0.5">
                        Nothing flagged — you're good to go.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setMissingResult(null)}
                      className="text-[11px] text-emerald-800/70 hover:text-emerald-900 underline decoration-dotted underline-offset-2"
                    >
                      Dismiss
                    </button>
                  </div>
                )
              )}
              {/* Phase 4a (Tier 2): AI polish — visible in read view; when
                  "Edit" is on, the same polish control lives by the textarea. */}
              {!isReadOnly && !docEditing && (() => {
                const polishApplied =
                  !!acceptedPolish &&
                  acceptedPolish.trim() ===
                    (incident.incident_description ?? "").trim();
                return (
                <div className="rounded-lg border border-bodhi-teal/25 bg-gradient-to-br from-bodhi-teal/5 to-transparent p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-bodhi-teal font-semibold flex items-center gap-2">
                        ✨ Professional description
                        {polishApplied && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-medium border border-emerald-200 normal-case tracking-normal">
                            <Check className="w-3 h-3" /> Applied to record
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {polishApplied
                          ? "The polished version is saved on this incident. Use Edit to fine-tune, or Regenerate for a fresh draft."
                          : "Rewrites \"what happened\" as neutral, behavior-first language for the permanent file. Your original stays in the thread until you accept."}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handlePolishDescription()}
                      disabled={polishingDesc || !incident.incident_description?.trim()}
                      className="flex-shrink-0 text-xs border-bodhi-teal/40 text-bodhi-teal hover:bg-bodhi-teal/10"
                    >
                      {polishingDesc ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                          Polishing…
                        </>
                      ) : descPolishDraft ? (
                        "Regenerate"
                      ) : polishApplied ? (
                        "Regenerate"
                      ) : (
                        "Polish with AI"
                      )}
                    </Button>
                  </div>
                  {descPolishDraft && (
                    <div className="mt-3 rounded-md border border-bodhi-teal/20 bg-white p-4">
                      <div className="text-[11px] font-semibold text-bodhi-teal uppercase tracking-wide mb-2">
                        Draft — review before accepting
                      </div>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {descPolishDraft}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-bodhi-teal text-white hover:bg-bodhi-teal/90"
                          onClick={() => {
                            const polished = descPolishDraft;
                            setAcceptedPolish(polished);
                            onUpdateIncident({ incident_description: polished });
                            setDescPolishDraft(null);
                            toast.success("Description updated.");
                          }}
                        >
                          Use this version
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-gray-500"
                          onClick={() => setDescPolishDraft(null)}
                        >
                          Discard
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                );
              })()}
              {/* Phase 4c: AI-drafted narrative summary */}
              {!isReadOnly && (() => {
                const narrativeApplied =
                  !!acceptedNarrative?.trim() &&
                  (incident.conversation_notes ?? "").includes(
                    acceptedNarrative.trim(),
                  );
                return (
                <div className="rounded-lg border border-bodhi-gold/30 bg-gradient-to-br from-bodhi-gold/5 to-transparent p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-bodhi-gold font-semibold flex items-center gap-2">
                        ✨ Narrative summary
                        {narrativeApplied && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-medium border border-emerald-200 normal-case tracking-normal">
                            <Check className="w-3 h-3" /> Added to case notes
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {narrativeApplied ? (
                          <>
                            Saved in the <strong className="font-medium text-gray-700">Notes</strong> field on this incident. Edit it under <strong className="font-medium text-gray-700">Notes</strong> (or in <strong className="font-medium text-gray-700">Edit</strong>), or click Regenerate for a fresh draft.
                          </>
                        ) : (
                          <>
                            A dignified, neutral summary for the file — from everything you captured, including any text from uploaded plan files.{" "}
                            <strong className="font-medium text-gray-700">Add to case notes</strong> saves it in the <strong className="font-medium text-gray-700">Notes</strong> field on this incident (same place as the documentation panel) so it persists if you close the draft.
                          </>
                        )}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handleDraftNarrative()}
                      disabled={draftingNarrative || !incident.incident_description?.trim()}
                      className="flex-shrink-0 text-xs border-bodhi-gold/40 text-bodhi-gold hover:bg-bodhi-gold/10"
                    >
                      {draftingNarrative ? (
                        <>
                          <span className="inline-block h-3 w-3 rounded-full border-2 border-bodhi-gold border-t-transparent animate-spin mr-1.5" />
                          Drafting…
                        </>
                      ) : narrativeDraft ? (
                        "Regenerate"
                      ) : narrativeApplied ? (
                        "Regenerate"
                      ) : (
                        "Draft with AI"
                      )}
                    </Button>
                  </div>
                  {narrativeDraft && (
                    <div className="mt-3 prose prose-sm max-w-none bg-white border border-bodhi-gold/20 rounded-md p-4">
                      <div
                        className="text-sm text-gray-800 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: mentorMd.render(narrativeDraft) }}
                      />
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100 not-prose">
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-bodhi-gold/90 text-white hover:bg-bodhi-gold"
                          onClick={handleAddNarrativeToCaseNotes}
                        >
                          Add to case notes
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => {
                            navigator.clipboard.writeText(narrativeDraft);
                            toast.success("Narrative copied to clipboard.");
                          }}
                        >
                          Copy
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-gray-500"
                          onClick={() => setNarrativeDraft(null)}
                        >
                          Close draft
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                );
              })()}
              {docEditing && !isReadOnly ? (
                <div className="space-y-4 bg-bodhi-teal/5 rounded-lg p-4 border border-bodhi-teal/15">
                  <p className="text-xs text-gray-500">
                    Changes save automatically. Click "Done editing" when you're
                    finished to return to the read-only view.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="doc-student" className="text-xs">
                        Student
                      </Label>
                      <Input
                        id="doc-student"
                        value={incident.student_identifier}
                        onChange={(e) =>
                          onUpdateIncident({ student_identifier: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="doc-grade" className="text-xs">
                        Grade
                      </Label>
                      <Select
                        value={incident.grade_level || undefined}
                        onValueChange={(v) =>
                          onUpdateIncident({ grade_level: v as GradeLevel })
                        }
                      >
                        <SelectTrigger id="doc-grade">
                          <SelectValue placeholder="Select grade…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="K-2">K-2</SelectItem>
                          <SelectItem value="3-5">3-5</SelectItem>
                          <SelectItem value="6-8">6-8</SelectItem>
                          <SelectItem value="9-12">9-12</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-6">
                    <label className="flex items-center gap-2 text-sm text-gray-800">
                      <Checkbox
                        checked={incident.repeat_incident}
                        onCheckedChange={(v) =>
                          onUpdateIncident({ repeat_incident: !!v })
                        }
                      />
                      Repeat incident
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-800">
                      <Checkbox
                        checked={incident.iep_504_flag}
                        onCheckedChange={(v) =>
                          onUpdateIncident({ iep_504_flag: !!v })
                        }
                      />
                      IEP / 504
                    </label>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="doc-desc" className="text-xs">
                        What happened
                      </Label>
                      <button
                        type="button"
                        onClick={() => void handlePolishDescription()}
                        disabled={polishingDesc || !incident.incident_description?.trim()}
                        className="text-[11px] text-bodhi-gold hover:text-bodhi-gold/80 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                      >
                        {polishingDesc ? (
                          <>
                            <span className="inline-block h-3 w-3 rounded-full border-2 border-bodhi-gold border-t-transparent animate-spin" />
                            Polishing…
                          </>
                        ) : (
                          <>✨ Polish with AI</>
                        )}
                      </button>
                    </div>
                    <Textarea
                      id="doc-desc"
                      rows={3}
                      value={incident.incident_description}
                      onChange={(e) =>
                        onUpdateIncident({ incident_description: e.target.value })
                      }
                    />
                    {descPolishDraft && (
                      <div className="mt-2 rounded-md border border-bodhi-gold/30 bg-bodhi-gold/5 p-3">
                        <div className="text-[11px] font-semibold text-bodhi-gold uppercase tracking-wide mb-1">
                          ✨ Polished draft (review before accepting)
                        </div>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                          {descPolishDraft}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Button
                            size="sm"
                            className="h-7 text-xs bg-bodhi-teal text-white hover:bg-bodhi-teal/90"
                            onClick={() => {
                              const polished = descPolishDraft;
                              setAcceptedPolish(polished);
                              onUpdateIncident({ incident_description: polished });
                              setDescPolishDraft(null);
                              toast.success("Description updated.");
                            }}
                          >
                            Use this version
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-gray-500"
                            onClick={() => setDescPolishDraft(null)}
                          >
                            Discard
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="doc-removal" className="text-xs">
                      Removal type
                    </Label>
                    <Select
                      value={incident.removal_type || "none"}
                      onValueChange={(v) =>
                        onUpdateIncident({
                          removal_type:
                            v === "none"
                              ? ""
                              : (v as Incident["removal_type"]),
                        })
                      }
                    >
                      <SelectTrigger id="doc-removal">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— none / stayed in class —</SelectItem>
                        <SelectItem value="administrative_redirect">
                          Administrative redirect
                        </SelectItem>
                        <SelectItem value="formal_removal">
                          Formal removal (ISS / suspension)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="doc-notes" className="text-xs">
                      Notes
                    </Label>
                    <Textarea
                      id="doc-notes"
                      rows={6}
                      value={incident.conversation_notes}
                      onChange={(e) =>
                        onUpdateIncident({ conversation_notes: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="doc-followup" className="text-xs">
                      Follow-up / outcome
                    </Label>
                    <Textarea
                      id="doc-followup"
                      rows={4}
                      value={incident.behavior_follow_up}
                      onChange={(e) =>
                        onUpdateIncident({ behavior_follow_up: e.target.value })
                      }
                    />
                  </div>
                  {(incident.removal_type === "formal_removal" ||
                    incident.iss_work_plan ||
                    incident.return_to_class_plan ||
                    incident.reentry_conversation_needed) && (
                    <div className="pt-4 mt-2 border-t border-bodhi-teal/20 space-y-4">
                      <div className="text-[10px] uppercase tracking-wider text-bodhi-teal font-semibold">
                        ISS &amp; re-entry plan
                      </div>
                      <label className="flex items-center gap-2 text-sm text-gray-800">
                        <Checkbox
                          checked={incident.iss_reflection_completed}
                          onCheckedChange={(v) =>
                            onUpdateIncident({
                              iss_reflection_completed: !!v,
                            })
                          }
                        />
                        Academic work plan communicated to teachers
                      </label>
                      <div>
                        <Label htmlFor="doc-iss-work" className="text-xs">
                          ISS work plan
                        </Label>
                        <Textarea
                          id="doc-iss-work"
                          rows={5}
                          value={incident.iss_work_plan}
                          onChange={(e) =>
                            onUpdateIncident({ iss_work_plan: e.target.value })
                          }
                        />
                      </div>
                      <label className="flex items-center gap-2 text-sm text-gray-800">
                        <Checkbox
                          checked={incident.reentry_conversation_needed}
                          onCheckedChange={(v) =>
                            onUpdateIncident({
                              reentry_conversation_needed: !!v,
                            })
                          }
                        />
                        Re-entry conversation needed before return
                      </label>
                      {incident.reentry_conversation_needed && (
                        <div>
                          <Label htmlFor="doc-iss-fac" className="text-xs">
                            Re-entry facilitator
                          </Label>
                          <Input
                            id="doc-iss-fac"
                            value={incident.reentry_facilitator}
                            onChange={(e) =>
                              onUpdateIncident({
                                reentry_facilitator: e.target.value,
                              })
                            }
                          />
                        </div>
                      )}
                      <div>
                        <Label htmlFor="doc-iss-return" className="text-xs">
                          Return-to-class plan / conditions
                        </Label>
                        <Textarea
                          id="doc-iss-return"
                          rows={3}
                          value={incident.return_to_class_plan}
                          onChange={(e) =>
                            onUpdateIncident({
                              return_to_class_plan: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label htmlFor="doc-iss-checkin" className="text-xs">
                          Check-in schedule after return
                        </Label>
                        <Input
                          id="doc-iss-checkin"
                          value={incident.reentry_checkin_schedule}
                          onChange={(e) =>
                            onUpdateIncident({
                              reentry_checkin_schedule: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-1.5 text-xs bg-bodhi-teal/5 rounded-lg p-4 border border-bodhi-teal/15">
                  <Row k="Date" v={new Date().toLocaleString()} />
                  <Row k="Student" v={incident.student_identifier || "—"} />
                  <Row k="Grade" v={incident.grade_level || "—"} />
                  <Row
                    k="Repeat incident"
                    v={incident.repeat_incident ? "Yes" : "No"}
                  />
                  <Row
                    k="IEP / 504"
                    v={incident.iep_504_flag ? "Yes — see federal guidance" : "No"}
                  />
                  <Row k="What happened" v={incident.incident_description || "—"} />
                  <Row
                    k="Removal type"
                    v={incident.removal_type ? incident.removal_type.replace(/_/g, " ") : "—"}
                  />
                  <Row k="Notes" v={incident.conversation_notes || "—"} />
                  <Row k="Follow-up" v={incident.behavior_follow_up || "—"} />
                  <Row
                    k="Due process"
                    v={
                      incident.due_process_acknowledged
                        ? `✓ ${incident.due_process_timestamp}`
                        : "⚠ Not yet confirmed"
                    }
                  />
                  {(incident.removal_type === "formal_removal" ||
                    incident.iss_work_plan ||
                    incident.return_to_class_plan ||
                    incident.reentry_conversation_needed ||
                    incident.reentry_checkin_schedule) && (
                    <>
                      <div className="mt-3 pt-3 border-t border-bodhi-teal/20 text-[10px] uppercase tracking-wider text-bodhi-teal font-semibold">
                        ISS &amp; re-entry plan
                      </div>
                      <Row
                        k="Work plan communicated"
                        v={incident.iss_reflection_completed ? "Yes" : "No"}
                      />
                      <Row
                        k="ISS work plan"
                        v={incident.iss_work_plan || "—"}
                      />
                      <Row
                        k="Re-entry conversation"
                        v={
                          incident.reentry_conversation_needed
                            ? incident.reentry_facilitator
                              ? `Yes — ${incident.reentry_facilitator}`
                              : "Yes"
                            : "No"
                        }
                      />
                      <Row
                        k="Return-to-class plan"
                        v={incident.return_to_class_plan || "—"}
                      />
                      <Row
                        k="Check-in schedule"
                        v={incident.reentry_checkin_schedule || "—"}
                      />
                    </>
                  )}
                </div>
              )}

              {attachments.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="text-[10px] uppercase tracking-wider text-bodhi-teal font-semibold mb-2">
                    Attached files ({attachments.length})
                  </div>
                  <div className="flex flex-col gap-2">
                    {attachments.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2"
                      >
                        <div className="w-7 h-7 rounded-md bg-bodhi-teal/10 text-bodhi-teal flex items-center justify-center flex-shrink-0">
                          <FileTypeIcon mimeType={a.mime_type} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {a.file_name}
                          </div>
                          <div className="text-[11px] text-gray-500">
                            {formatFileSize(a.file_size)}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleViewAttachment(a.id)}
                          className="text-bodhi-teal hover:bg-bodhi-teal/10 rounded-md px-2 py-1 text-xs font-medium flex items-center gap-1 transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" aria-hidden />
                          View
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {openPanel?.kind === "parent" && (
          <div className="flex flex-col h-full">
            <SheetHeader className="px-6 pt-6 pb-3 border-b border-gray-200 bg-bodhi-teal text-white space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-white/80 font-semibold">
                Parent Communication
              </div>
              <SheetTitle className="text-white font-literata text-xl">
                Draft your outreach
              </SheetTitle>
              <SheetDescription className="text-white/80">
                Email, phone script, or call with {incident.student_identifier || "student"} + parent.
              </SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-6 py-5 bg-white text-gray-900">
              <ParentCommunication
                studentIdentifier={incident.student_identifier}
                gradeLevel={incident.grade_level}
                teacherName={incident.teacher_name}
                incidentDescription={incident.incident_description}
                studentReflection={incident.student_reflection_responses}
                chosenPath={incident.chosen_path}
                conversationNotes={incident.conversation_notes}
                parentContactType={incident.parent_contact_type}
                parentCallNotes={incident.parent_call_notes}
                followUpDate={incident.follow_up_date}
                initialTab={openPanel.initialTab}
                onUpdateParentContactType={(type) =>
                  onUpdateIncident({ parent_contact_type: type })
                }
                onUpdateParentCallNotes={(notes) =>
                  onUpdateIncident({ parent_call_notes: notes })
                }
              />
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
    </>
  );
});

const Row = ({ k, v }: { k: string; v: string }) => (
  <div className="flex gap-2 items-start">
    <span className="text-gray-500 min-w-[110px] flex-shrink-0">{k}:</span>
    <span className="text-gray-900 break-words">{v}</span>
  </div>
);
